<script setup>
import CryptoJS from "crypto-js";
import DescriptionInline from "./DescriptionInline.vue";
import DropDownInfo from "./DropDownInfo.vue";
import DropDownInfoNumbered from "./DropDownInfoNumbered.vue";
import HeaderReport from "./HeaderReport.vue";
import FieldNameInline from "./FieldNameInline.vue";
import iccApi from "../api/iccApi";
import Swal from "sweetalert2";
import ButtonSubmit from "./ButtonSubmit.vue";
import Multiselect from "@vueform/multiselect";
import { flashMessage } from "../functions.js";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";
import AdminDashboardNominationModal from "./AdminDashboardNominationModal.vue";
import AdminDashboardNomineeDetailsModal from "./AdminDashboardNomineeDetailsModal.vue";
import AdminDashboardBulkUploadModal from "./AdminDashboardBulkUploadModal.vue";
import AdminDashboardAddUserModal from "./AdminDashboardAddUserModal.vue";
import VueCookies from 'vue-cookies';
import * as XLSX from 'xlsx';
import { getTimezoneAbbreviation } from "../functions.js";
import { replaceTokens } from "../functions.js";
 
// Excel-style column resize. Usage: v-resizable-columns="'some-table-id'"
const vResizableColumns = {
  mounted(el, binding) {
    const storeKey = 'icc-colw:' + binding.value;
    let widths = {};
    try { widths = JSON.parse(localStorage.getItem(storeKey) || '{}') || {}; } catch (e) { widths = {}; }
    const MIN = 60;
    let raf = 0;
    let lastDrag = 0;
 
    const getTable = () => (el.tagName === 'TABLE' ? el : el.querySelector('table'));
    const labelOf = (th) => {
      const t = th.querySelector('.title-container span');
      return ((t ? t.textContent : th.textContent) || '').trim();
    };
    const setColWidth = (table, index, w) => {
      table.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tr').forEach((row) => {
        const cell = row.querySelectorAll(':scope > th, :scope > td')[index];
        if (!cell || cell.colSpan > 1) return;
        cell.style.width = w + 'px';
        cell.style.minWidth = w + 'px';
        cell.style.maxWidth = w + 'px';
        if (cell.tagName === 'TD') {
          cell.style.overflow = 'hidden';
          cell.style.textOverflow = 'ellipsis';
          cell.style.whiteSpace = 'nowrap';
        }
      });
    };
    const startDrag = (e, table, th) => {
      e.preventDefault();
      e.stopPropagation();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      const label = labelOf(th);
      const idx = Array.prototype.indexOf.call(th.parentNode.children, th);
      const startX = e.clientX;
      const startW = th.getBoundingClientRect().width;
      handle.classList.add('active');
      const move = (ev) => {
        const w = Math.max(MIN, Math.round(startW + (ev.clientX - startX)));
        widths[label] = w;
        setColWidth(table, idx, w);
      };
      const up = () => {
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
        handle.removeEventListener('pointercancel', up);
        handle.classList.remove('active');
        lastDrag = Date.now();
        try { localStorage.setItem(storeKey, JSON.stringify(widths)); } catch (err) { console.warn('Could not save column widths', err); }
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
      handle.addEventListener('pointercancel', up);
    };
    const apply = () => {
      const table = getTable();
      if (!table) return;
      table.querySelectorAll(':scope > thead > tr > th, :scope > tr:first-child > th, :scope > tbody > tr:first-child > th')
        .forEach((th) => {
          const label = labelOf(th);
          if (!label) return; // skips the select-all checkbox column
          if (!th.querySelector(':scope > .icc-col-resizer')) {
            const h = document.createElement('span');
            h.className = 'icc-col-resizer';
            h.addEventListener('pointerdown', (e) => startDrag(e, table, th));
            th.appendChild(h);
          }
          if (widths[label]) {
            setColWidth(table, Array.prototype.indexOf.call(th.parentNode.children, th), widths[label]);
          }
        });
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(apply); };
 
    // never let the end of a drag count as a click (sorting / filter)
    el.addEventListener('click', (e) => {
      if (Date.now() - lastDrag < 300) { e.stopPropagation(); e.preventDefault(); }
    }, true);
 
    const mo = new MutationObserver(schedule); // childList only, so style changes do not loop
    mo.observe(el, { childList: true, subtree: true });
    el._iccResize = { mo };
    schedule();
  },
  unmounted(el) {
    if (el._iccResize) el._iccResize.mo.disconnect();
  },
};
 

</script>

<template>
  <div class="icc-root" :style="{ '--icc-fs-body': Math.max(16, Number(brandData.font_text_size) || 16) + 'px' }">
    <form @submit.prevent="handleSubmit" class="icc-form">
      <div class="icc-shell" :style="{ '--icc-primary': brandData.main_color1 || '#0e5071', '--icc-on-primary': onPrimaryColor }">

        <!-- BEGIN: Sidebar (Hierarchy filter) -->
        <aside class="icc-sidebar" :class="{ collapsed: sidebarCollapsed }">
          <section class="icc-sidebar-head">
            <span class="icc-sidebar-title">Hierarchy filter</span>
            <button type="button" class="icc-pill" @click="sidebarCollapsed = !sidebarCollapsed">
              <span v-if="!sidebarCollapsed">HIDE</span>
              <span v-else class="icc-vertical">SHOW ITERATION PICKER</span>
            </button>
          </section>

          <div class="icc-sidebar-scroll" v-show="!sidebarCollapsed">
            <div class="icc-filter-title">
              <h3>Select your filters</h3>
              <span class="icc-info">i
                <span class="icc-tip">Drill down through your organization to filter data for a specific program, iteration, or group. You will only see areas you have permission to access.</span>
              </span>
            </div>

            <clip-loader v-if="isLoading" :color="setColor" :size="setSize"></clip-loader>

            <template v-else>
              <!-- Statistics box -->
              <div class="icc-stats">
                <clip-loader v-if="setLoading && survey.iteration.iteration_id != null" :loading="setLoading"
                  :color="setColor" :size="setSize"></clip-loader>
                <div v-if="!setLoading && survey.iteration.iteration_id != null && statisticsData.length > 0">
                  <div v-for="(stat, index) in statisticsData" :key="index">
                    <p><strong>STATISTICS<br>Participants:</strong> {{ stat.participants_count }}</p>
                    <p><strong>Participants registered:</strong> {{ stat.participants_registered }}
                      <span class="icc-pct">({{ ((stat.participants_registered / stat.participants_count) * 100).toFixed(1) }}%)</span></p>
                    <p><strong>Participants submitted:</strong> {{ stat.participants_submitted }}
                      <span class="icc-pct">({{ ((stat.participants_submitted / stat.participants_count) * 100).toFixed(1) }}%)</span></p>
                    <p><strong>Participants eligible for report:</strong> {{ stat.participants_eligible }}
                      <span class="icc-pct">({{ ((stat.participants_eligible / stat.participants_count) * 100).toFixed(1) }}%)</span></p>
                    <p><strong>Total nominations:</strong> {{ stat.nominees_count }}
                      <span class="icc-pct">({{ (stat.nominees_count / stat.participants_count).toFixed(1) }} per participant)</span></p>
                    <p><strong>Total nominations submitted:</strong> {{ stat.nominees_submitted }}
                      <span class="icc-pct">({{ (stat.nominees_submitted / stat.participants_count).toFixed(1) }} per participant)</span></p>
                  </div>
                </div>
                <p v-else-if="!setLoading"><strong>STATISTICS</strong><br>Select an iteration and press Select to see statistics.</p>
              </div>

              <!-- Filter selects -->
              <div class="icc-fields">
            <div class="icc-field">
              <label class="icc-label">Organization<span class="icc-chev">›</span></label>
              <select class="icc-select" :class="{ 'icc-placeholder': !selectedFilters.organization }"
                :disabled="false"
                @change="onCascadeSelect('organization', organizations, $event.target.value)">
                <option value="" :selected="!selectedFilters.organization">— Select —</option>
                <option v-for="o in (organizations)" :key="o.id" :value="o.id"
                  :selected="selectedFilters.organization?.id === o.id">{{ o.name }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label">Sub-Organization<span class="icc-chev">›</span></label>
              <select class="icc-select" :class="{ 'icc-placeholder': !selectedFilters.subOrganization }"
                :disabled="!selectedFilters.organization"
                @change="onCascadeSelect('subOrganization', selectedFilters.organization?.subOrgs || [], $event.target.value)">
                <option value="" :selected="!selectedFilters.subOrganization">— Select —</option>
                <option v-for="o in (selectedFilters.organization?.subOrgs || [])" :key="o.id" :value="o.id"
                  :selected="selectedFilters.subOrganization?.id === o.id">{{ o.name }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label">Program<span class="icc-chev">›</span></label>
              <select class="icc-select" :class="{ 'icc-placeholder': !selectedFilters.program }"
                :disabled="!selectedFilters.subOrganization"
                @change="onCascadeSelect('program', selectedFilters.subOrganization?.programs || [], $event.target.value)">
                <option value="" :selected="!selectedFilters.program">— Select —</option>
                <option v-for="o in (selectedFilters.subOrganization?.programs || [])" :key="o.id" :value="o.id"
                  :selected="selectedFilters.program?.id === o.id">{{ o.name }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label">Iteration<span class="icc-chev">›</span></label>
              <select class="icc-select" :class="{ 'icc-placeholder': !selectedFilters.iteration }"
                :disabled="!selectedFilters.program"
                @change="onCascadeSelect('iteration', selectedFilters.program?.iterations || [], $event.target.value)">
                <option value="" :selected="!selectedFilters.iteration">— Select —</option>
                <option v-for="o in (selectedFilters.program?.iterations || [])" :key="o.id" :value="o.id"
                  :selected="selectedFilters.iteration?.id === o.id">{{ o.name }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label" for="survey-template-select">Survey<span class="icc-chev">›</span></label>
              <select id="survey-template-select" class="icc-select" v-model="template_id"
                @change="showSurveyType(); clearData();">
                <option v-for="item in filteredSurveyTemplate" :key="item.survey_template_id"
                  :value="item.survey_template_id">{{ item.survey_template_file_name }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label">Stream<span class="icc-chev">›</span></label>
              <select class="icc-select" :class="{ 'icc-placeholder': !selectedFilters.stream }"
                :disabled="!selectedFilters.iteration"
                @change="onCascadeSelect('stream', selectedFilters.iteration?.streams || [], $event.target.value)">
                <option value="" :selected="!selectedFilters.stream">— Select —</option>
                <option v-for="o in (selectedFilters.iteration?.streams || [])" :key="o.id" :value="o.id"
                  :selected="selectedFilters.stream?.id === o.id">{{ o.name }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label">Group<span class="icc-chev">›</span></label>
              <select class="icc-select" :class="{ 'icc-placeholder': !selectedFilters.group }"
                :disabled="!selectedFilters.stream"
                @change="onCascadeSelect('group', selectedFilters.stream?.groups || [], $event.target.value)">
                <option value="" :selected="!selectedFilters.group">— Select —</option>
                <option v-for="o in (selectedFilters.stream?.groups || [])" :key="o.id" :value="o.id"
                  :selected="selectedFilters.group?.id === o.id">{{ o.name }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label" for="icc-show-role">Show Role<span class="icc-chev">›</span></label>
              <select id="icc-show-role" class="icc-select" v-model="filterType" @change="onFilterTypeChange">
                <option v-for="o in is_nomination_options" :key="String(o.Value)" :value="o.Value">{{ o.label }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label" for="icc-batch-mode">Survey Batch Actions<span class="icc-chev">›</span></label>
              <select id="icc-batch-mode" class="icc-select" v-model="batchAction" @change="onBatchActionChange">
                <option v-for="o in default_batch_action" :key="String(o.Value)" :value="o.Value">{{ o.label }}</option>
              </select>
            </div>
            <div class="icc-field">
              <label class="icc-label" for="respondentSearch">Respondent Search<span class="icc-chev">›</span></label>
              <input id="respondentSearch" type="text" class="icc-select" v-model="respondentSearch" minlength="3"
                placeholder="Type at least 3 chars..." @keydown.enter.stop.prevent="searchResult()" />
            </div>
              </div>
            </template>
          </div>

          <!-- Sidebar footer (pinned orange Select button) -->
          <div class="icc-sidebar-foot" v-show="!sidebarCollapsed">
            <button type="button" class="icc-select-btn" :disabled="disableSearchButton" @click="searchResult()">Select</button>
            <button type="button" class="icc-link-btn" @click="clearAllFilters">Clear all filters</button>
            <div class="icc-foot-row" v-if="selectedFilters.iteration">
              <button type="button" class="icc-link-btn" @click="releaseReportsNow">Release Reports Now</button>
              <button type="button" class="icc-link-btn" :disabled="isGeneratingToken" @click="generateReleaseUrl">Copy Release URL</button>
            </div>
            <div class="icc-release" v-if="generatedReleaseUrl">
              <div class="icc-release-head">Release URL (valid for 7 days) · Expires: {{ generatedReleaseUrlExpiry }}</div>
              <div class="icc-release-box">
                <span class="icc-release-text">{{ generatedReleaseUrl }}</span>
                <button type="button" class="icc-link-btn" @click="copyReleaseUrl">{{ urlCopied ? 'Copied!' : 'Copy' }}</button>
              </div>
            </div>
          </div>
        </aside>
        <!-- END: Sidebar -->

        <!-- BEGIN: Main content -->
        <main class="icc-main">
          <div class="icc-main-inner">

            <!-- Top action bar -->
            <header class="icc-actionbar">
              <h2></h2>
              <div class="icc-btn-row">
                <button type="button" class="icc-btn" @click="show_bulk_upload_modal = true">Upload user via CSV</button>
                <button type="button" class="icc-btn" @click="show_add_user_modal = true">Add individual user</button>
                <button type="button" class="icc-btn" :disabled="!(secondFetchUserData.length > 0 && showIndividualRecords == false)"
                  @click="showColumnPicker = !showColumnPicker">Adjust table views</button>
                <div class="icc-dropdown">
                  <button type="button" class="icc-btn icc-btn-drop" :disabled="selected.length == 0">
                    <span>Batch Action Edit</span>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 9l-7 7-7-7" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                  <div class="icc-menu" v-if="selected.length > 0">
                    <button type="button" v-for="q in quickBatchItems" :key="q.id" @click="openQuickBatch(q.id)">{{ q.label }}</button>
                    <button type="button" class="icc-menu-more" @click="openQuickBatch(null)">All batch actions…</button>
                  </div>
                </div>
                <button type="button" class="icc-btn"
                  :disabled="!(secondFetchUserData.length > 0 && showIndividualRecords == false)"
                  @click="showColumnPicker = !showColumnPicker">Hide/show selected columns</button>
              </div>
            </header>

            <!-- Breadcrumb -->
            <div class="icc-breadcrumb">
              <span class="icc-path">Path:</span>
              <span class="icc-crumbs">
                <span v-if="!hasSelections" class="icc-crumb-empty">No filters selected</span>
                <span v-if="selectedFilters.organization">Org: <strong>{{ selectedFilters.organization.name }}</strong></span>
                <span v-if="selectedFilters.subOrganization"> → Sub-Org: <strong>{{ selectedFilters.subOrganization.name }}</strong></span>
                <span v-if="selectedFilters.program"> → Program: <strong>{{ selectedFilters.program.name }}</strong></span>
                <span v-if="selectedFilters.iteration"> → Iteration: <strong>{{ selectedFilters.iteration.name }}</strong></span>
                <span v-if="selectedFilters.stream"> → Stream: <strong>{{ selectedFilters.stream.name }}</strong></span>
                <span v-if="selectedFilters.group"> → Group: <strong>{{ selectedFilters.group.name }}</strong></span>
              </span>
            </div>

            <!-- Column picker (opened by "Hide/show selected columns") -->
            <div class="icc-panel" v-if="showColumnPicker && secondFetchUserData.length > 0 && showIndividualRecords == false">
          <v-select v-model="selectedHeaders" :items="survey_headers_data" label="Select Columns to Focus Your View"
            multiple outlined return-object v-if="secondFetchUserData.length > 0 && showIndividualRecords == false">
            <template v-slot:selection="{ item, index }">
              <v-chip variant="text" v-if="index < 6">
                <span>{{ item.title }}</span>
              </v-chip>
              <span v-if="index === 6 && item.title !== 'Survey Invitee'" class="grey--text caption">
                (+{{ selectedHeaders.length - 6 }} others)
              </span>
            </template>
          </v-select>
            </div>

            <!-- Batch actions panel (all existing batch actions, unchanged logic) -->
            <div class="icc-panel icc-batch-panel" v-if="toggle && (firstFetchUserData.length > 0 || secondFetchUserData.length > 0)">
              <div class="icc-panel-head">
                <strong>Batch actions</strong>
                <button type="button" class="icc-link-btn" @click="toggle = false; actions = null">Close</button>
              </div>
              <p class="icc-hint" v-if="selected.length == 0">Select at least one row in the table to use batch actions.</p>
        <div class="actions-container" v-if="selected.length > 0">

          <div v-if="
              toggle == true &&
              firstFetchUserData.length > 0 &&
              selected.length > 0
            " class="batchAction-btn">
            <label style="color: #ccc" for="selectBatchAction">
              <DescriptionInline class="batch-action-desc" label="Select batch action " />
            </label>
            <select id="selectBatchAction" name="batchAction" v-model="actions" @change="clearInput">
              <option selected :value="null">Select action</option>

              <option :value="19">Add survey</option>
              <option :value="21">Update program/iteration</option>
              <option v-for="action in filteredAssignGroup" v-bind:key="action.batch_action_id"
                v-bind:value="action.batch_action_id">
                {{ action.batch_action_type }}
              </option>
            </select>
          </div>

          <div v-if="
              toggle == true &&
              secondFetchUserData.length > 0 &&
              selected.length > 0
            " class="batchAction-btn">
            <label style="color: #ccc" for="selectBatchAction">
              <DescriptionInline class="batch-action-desc" label="Select batch action " />
            </label>
            <select class="batch-action-input2" id="selectBatchAction" name="batchAction" v-model="actions"
              @change="clearInput">
              <option selected :value="null">Select action</option>
              <option v-for="action in filteredBatchActions" v-bind:key="action.batch_action_id"
                v-bind:value="action.batch_action_id">
                {{ action.batch_action_type }}
              </option>
            </select>
          </div>

          <div v-if="toggle == true && actions == 24 && selected.length > 0" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" id="tags-multiselect" v-model="survey.action_tag"
                placeholder="Select tag(s)" mode="tags" :searchable="true" :options="filteredRemoveTags" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 22 && selected.length > 0" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" id="stream-multiselect" v-model="survey.action_stream_id"
                placeholder="Select/Add Stream" :searchable="true" :options="filteredStreamActions"
                :createOption="true" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 22 && selected.length > 0" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" id="group-multiselect" v-model="survey.group_id"
                placeholder="Select/Add Group" :searchable="true" :options="filteredGroupMultiSelectActions"
                :createOption="true" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 21 && selected.length > 0" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" id="program-multiselect" v-model="survey.action_program_id"
                placeholder="Select/Add Program" :searchable="true" :options="filteredProgramActions"
                :createOption="true" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 21 && selected.length > 0" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" id="iteration-multiselect" v-model="survey.action_iteration_id"
                placeholder="Select/Add Iteration" :searchable="true" :options="filteredIterationMultiSelectActions"
                :createOption="true" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 20 && selected.length > 0" style="width: 25%">
            <label style="color: #ccc" for="selectTag">
              <DescriptionInline class="batch-action-desc" label="Select Tag " />
            </label>
            <!-- TS feature- change from single selection to multi select in Add Tag -->

            <!-- <select class="input1" id="selectTag" name="tags" v-model="tags">
              <option selected :value="null">Select tag</option>
              <option
                v-for="tag in filteredTags"
                v-bind:key="tag.tag_id"
                v-bind:value="tag.tag_id"
              >
                {{ tag.tag_type }}
              </option>
            </select> -->
            <div class="input1">
              <Multiselect class="multiselect-input" id="tags-multiselect" v-model="survey.action_tag"
                placeholder="Select tag(s)" mode="tags" :searchable="true" :options="filteredRemoveTags" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 19 && selected.length > 0" style="width: 25%">
            <label style="color: #ccc" for="selectSurveyTemplate">
              <DescriptionInline class="batch-action-desc" label="Select Survey Template " />
            </label>
            <select class="input1" id="selectSurveyTemplate" name="surveyTemplate" v-model="survey.survey_template_id">
              <option selected :value="null">Select survey template</option>
              <option v-for="template in filteredUserSurveyTemplate" v-bind:key="template.survey_template_id"
                v-bind:value="template.survey_template_id">
                {{ template.survey_template_name }} - {{ template.survey_file }}
              </option>
            </select>
          </div>

          <div v-if="toggle == true && survey.survey_template_id != null" style="margin-right: 20px; width: 25%">
            <label v-if="
                filteredIteration.length == 0 || survey.iteration.iteration_id == null
              " style="color: #ccc" class="batch-action-desc">
              No iteration selected
            </label>
            <div v-for="iteration in filteredIteration" :key="iteration">
              <label v-if="
                  iteration.time_zone == null ||
                  iteration.time_zone == '' ||
                  iteration.time_zone == 'undefined'
                " style="color: #ccc" class="batch-action-desc">
                Iteration timezone is not set
              </label>
              <label v-else-if="survey.iteration.iteration_id == iteration.iteration_id" style="color: #ccc"
                class="batch-action-desc">
                Dates and time are based in timezone: {{ iteration.time_zone }}
              </label>
            </div>
            <br />
            <label style="color: #ccc" for="launch">
              <DescriptionInline class="batch-action-desc" label="Launch" />
            </label><br />
            <input class="input1" type="datetime-local" id="launch" :value="survey.launch"
              @input="survey.launch = $event.target.value" required />
            <br />
            <label style="color: #ccc" for="reminder">
              <DescriptionInline class="batch-action-desc" label="Survey Reminder" />
            </label><br />
            <input class="input1" type="datetime-local" id="reminder" :value="survey.reminder"
              @input="survey.reminder = $event.target.value" />
            <br />
            <label style="color: #ccc" for="deadline_initial">
              <DescriptionInline class="batch-action-desc" label="Initial deadline" />
            </label><br />
            <input class="input1" type="datetime-local" id="deadline_initial" required :value="survey.deadline_initial"
              @input="survey.deadline_initial = $event.target.value" />
            <br />
            <label style="color: #ccc" for="deadline_final">
              <DescriptionInline class="batch-action-desc" label="Final deadline" />
            </label><br />
            <input class="input1" type="datetime-local" id="deadline_final" required :value="survey.deadline_final"
              @input="survey.deadline_final = $event.target.value" />
            <br />
            <label style="color: #ccc" for="coach_report_start">
              <DescriptionInline class="batch-action-desc" label="Coach report start" />
            </label><br />
            <input class="input1" type="datetime-local" id="coach_report_start" :value="survey.coach_report_start"
              @input="survey.coach_report_start = $event.target.value" />
            <br />
            <label style="color: #ccc" for="coach_report_end">
              <DescriptionInline class="batch-action-desc" label="Coach report end" />
            </label><br />
            <input class="input1" type="datetime-local" id="coach_report_end" :value="survey.coach_report_end"
              @input="survey.coach_report_end = $event.target.value" />
            <br />
            <label style="color: #ccc" for="participant_report_start">
              <DescriptionInline class="batch-action-desc" label="Participant report start" />
            </label><br />
            <input class="input1" type="datetime-local" id="participant_report_start"
              :value="survey.participant_report_start" @input="survey.participant_report_start = $event.target.value" />
          </div>

          <div v-if="toggle == true && actions == 1" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="suborg_inp" placeholder="Change sub-organization"
                :options="filteredSubOrgActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 2" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="program_inp" placeholder="Change program name"
                :options="filteredProgramActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 3" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="iteration_inp" placeholder="Change iteration name"
                :options="filteredIterationActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 4" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="stream_inp" placeholder="Change stream name"
                :options="filteredStreamActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 5" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="group_inp" placeholder="Change group name"
                :options="filteredGroupActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 6" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="coach_email_inp" placeholder="Change Primary Coach"
                :searchable="true" :options="filteredCoachEmailActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 61" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="coach_email_inp" placeholder="Add Primary Coach"
                :searchable="true" :options="filteredCoachEmailActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 64" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="secondary_coach_email_inp"
                placeholder="Change Secondary Coach" :searchable="true" :options="filteredCoachEmailActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 63" style="width: 30% !important" class="batch-action-input">
            <div class="input1">
              <Multiselect class="multiselect-input" v-model="secondary_coach_email_inp"
                placeholder="Add Secondary Coach" :searchable="true" :options="filteredCoachEmailActions" />
            </div>
          </div>

          <div v-if="toggle == true && actions == 7" style="width: 25%">
            <label style="color: #ccc" for="final-deadline-date">
              <DescriptionInline class="batch-action-desc" label="New final deadline date* " />
            </label>
            <input type="datetime-local" class="input1" id="final-deadline-date" v-model="dates.deadline_final" />
          </div>

          <div v-if="toggle == true && actions == 8" style="width: 25%">
            <label style="color: #ccc" for="launch-date">
              <DescriptionInline class="batch-action-desc" label="New launch date* " />
            </label>
            <input type="datetime-local" class="input1" id="launch-date" v-model="dates.launch" />
          </div>

          <div v-if="toggle == true && actions == 9" style="width: 25%">
            <label style="color: #ccc" for="survey-reminder-date">
              <DescriptionInline class="batch-action-desc" label="New survey reminder date* " />
            </label>
            <input type="datetime-local" class="input1" id="survey-reminder-date" v-model="dates.reminder" />
          </div>

          <div v-if="toggle == true && actions == 10" style="width: 25%">
            <label style="color: #ccc" for="coach-report-start-date">
              <DescriptionInline class="batch-action-desc" label="New coach report start date* " />
            </label>
            <input type="datetime-local" class="input1" id="coach-report-start-date"
              v-model="dates.coach_report_start" />
          </div>

          <div v-if="toggle == true && actions == 11" style="width: 25%">
            <label style="color: #ccc" for="coach-report-end-date">
              <DescriptionInline class="batch-action-desc" label="New coach report end date* " />
            </label>
            <input type="datetime-local" class="input1" id="coach-report-end-date" v-model="dates.coach_report_end" />
          </div>

          <div v-if="toggle == true && actions == 12" style="width: 25%">
            <label style="color: #ccc" for="participant-report-start-date">
              <DescriptionInline class="batch-action-desc" label="New participant report start date* " />
            </label>
            <input type="datetime-local" class="input1" id="participant-report-start-date"
              v-model="dates.participant_report_start" />
          </div>

          <div v-if="toggle == true && actions == 13" style="width: 25%">
            <label style="color: #ccc" for="participant-report-start-date">
              <DescriptionInline class="batch-action-desc" label="New initial deadline date* " />
            </label>
            <input type="datetime-local" class="input1" id="initial-deadline-date" v-model="dates.initial_deadline" />
          </div>

          <ButtonSubmit label="Go" id="batchAction" :disabled="disableGoButton == false" @click="swalValidation"
            v-if="toggle == true && firstFetchUserData.length > 0" />

          <ButtonSubmit label="Go" id="batchAction" :disabled="disableGoButton == false" @click="secondHandleSave" v-if="
              toggle == true &&
              secondFetchUserData.length > 0 &&
              actions != 14 &&
              actions != 15 &&
              actions != 23 &&
              actions != 25 &&
              actions != 58 &&
              actions != 59 &&
              actions != 60 &&
              actions != 66 &&
              actions != 67 &&
              actions != 68 &&
              actions != 69 &&
              actions != 70 &&
              actions != 71 &&
              actions != 72 &&
              actions != 75 &&
              actions != 76 &&
              actions != 78 &&
              actions != 79 &&
              actions != 80 &&
              actions != 81 &&
              actions != 82 &&
              actions != 62 &&
              actions != 65 &&
              actions != 16
            " />

          <!--Unsubmit survey == 23-->
          <!--Remove Primary coach == 62-->
          <!--remove secondary coach == 65-->
          <!--undrop == 15-->
          <!--Make PDF available == 60-->
          <!--Generate PDF == 59-->
          <!--drop == 14-->
          <!--Run iteration == 25-->
          <!--Calculate selected participants scores == 58-->

          <ButtonSubmit label="Go" id="batchAction" @click="secondHandleSave" :disabled="selected.length == 0" v-if="
              toggle == true && secondFetchUserData.length > 0 && 
              ( actions == 62 ||
                actions == 65 ||
                actions == 81 ||
                actions == 82 ||
                actions == 23 ||
                actions == 15 ||
                actions == 16 ||
                actions == 76 ||
                actions == 60 ||
                actions == 66 ||
                actions == 67 ||
                actions == 68 ||
                actions == 69 ||
                actions == 70 ||
                actions == 71 ||
                actions == 72 ||
                actions == 75 ||
                actions == 78 ||
                actions == 79 ||
                actions == 80 ||
                actions == 59 ||
                actions == 14 ||
                actions == 25 ||
                actions == 58 )
            " />
        </div>
            </div>

            <!-- Main table section -->
            <section class="icc-table-section">
              <div class="icc-table-title">Admin control center</div>
              <div class="icc-select-row">
                <label class="icc-selectall">
                  <input type="checkbox" v-model="allSelected"
                    :disabled="firstFetchUserData.length == 0 && secondFetchUserData.length == 0"
                    @change="showIndividualRecords ? userSelectAll() : surveySelectAll()" />
                  <span>Select All / Click on the Table</span>
                </label>
                <span>Select: {{ selected.length }}</span>
              </div>

              <div class="icc-table-body">
                <clip-loader :loading="setLoading" :color="setColor" :size="setSize"></clip-loader>
        <v-card v-if="firstFetchUserData.length > 0 && showIndividualRecords == true" class="user-data-table-container">
          <v-data-table v-resizable-columns="'icc-user'" :headers="userHeaders" :items="filteredUserData" items-per-page="50" return-object show-select
            v-model="selected" :loading="isLoading" density="compact" loading-text="Loading... Please wait"
            class="user-custom-data-table">


            <!-- Filtering Code -->

            <template v-for="header in userHeaders" v-slot:[`header.${header.key}`]="{ column }" :key="header.key">
              <thead class="v-data-table-header">
                <tr>
                  <th
                    v-bind:class="[column.sortable ? 'sortable' : '', pagination.sortBy == column.value ? 'active': '', pagination.descending ? 'desc':'asc']"
                    @click="column.sortable ? changeSort(column.value) : ''">
                    <div class="title-container">
                      <span>{{ column.title }}</span>
                      <v-icon v-if="column.sortable" class="v-data-table-header__icon" small>
                        {{ pagination.descending ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                      </v-icon>
                    </div>
                  </th>
                  <th style="min-width:15px;">
                    <v-autocomplete multiple variant="plain" dense :items="userColumnValueList(column.value)"
                      v-model="filters[column.value]" @click.stop="{/* Stop click event propagation */}"
                      class="column-header-filter">
                      <!-- Selection Template (Optional, Adjust Display if Needed) -->
                      <template v-slot:selection="{ item }">
                        <span>{{ item }}</span>
                      </template>
                    </v-autocomplete>


                  </th>
                </tr>
              </thead>
            </template>

            <template v-slot:no-data>
              <v-btn color="primary" @click="initialize"> Reload Table </v-btn>
            </template>
          </v-data-table>
        </v-card>
                <v-card class="survey-data-table-container"
                  v-if="secondFetchUserData.length > 0 && showIndividualRecords == false">
          <v-data-table v-resizable-columns="'icc-survey'" :headers="displayedHeaders" :items="filteredSurveyAssignmentData"
            @update:options="handleOptionsUpdate" items-per-page="50" return-object show-select v-model="selected"
            :loading="isLoading" density="compact" loading-text="Loading... Please wait"
            class="survey-custom-data-table" fixed-header :items-per-page-options="[50, 12, 100, 150, 200, -1]"
            theme="blue">

            <template v-for="header in surveyHeaders" v-slot:[`header.${header.key}`]="{ column }" :key="header.key">
              <!-- text-align right the header if isNumeric = true -->
              <div v-if="column.isNumeric" style="display:flex; justify-content: flex-end;">
                <thead class="v-data-table-header">
                  <tr>
                    <th
                      v-bind:class="[column.sortable ? 'sortable' : '', pagination.sortBy == column.value ? 'active': '', pagination.descending ? 'desc':'asc']"
                      @click="column.sortable ? changeSort(column.value) : ''">
                      <div class="title-container">
                        <span>{{ column.title }}</span>
                        <v-icon v-if="column.sortable" class="v-data-table-header__icon" small
                          :color="pagination.sortBy === column.value ? brandData.accent_color1 : ''">
                          {{ pagination.sortBy === column.value && pagination.descending ? 'mdi-arrow-down' :
                          'mdi-arrow-up' }}
                        </v-icon>

                      </div>
                    </th>
                    <th style="min-width:15px;">
                      <v-autocomplete multiple variant="plain" dense
                        :items="surveyColumnValueList(column.value, column.key)" v-model="filters[column.value]"
                        @click.stop="{}" class="column-header-filter">
                        <!-- Custom Selection Template -->
                        <template v-slot:selection="{ index }">
                          <!-- Display all items up to a customizable limit -->
                          <span v-if="index < 1">
                            <v-icon class="mr-2" small>
                              {{ 'mdi-filter' }}
                            </v-icon>
                          </span>
                        </template>
                      </v-autocomplete>

                    </th>
                  </tr>
                </thead>
              </div>
              <div v-else>
                <thead class="v-data-table-header">
                  <tr>
                    <th
                      v-bind:class="[column.sortable ? 'sortable' : '', pagination.sortBy == column.value ? 'active': '', pagination.descending ? 'desc':'asc']"
                      @click="column.sortable ? changeSort(column.value) : ''">
                      <div class="title-container">
                        <span>{{ column.title }}</span>
                        <v-icon v-if="column.sortable" class="v-data-table-header__icon" small
                          :color="pagination.sortBy === column.value ? brandData.accent_color1 : ''">
                          {{ pagination.sortBy === column.value && pagination.descending ? 'mdi-arrow-down' :
                          'mdi-arrow-up' }}
                        </v-icon>

                      </div>
                    </th>
                    <th style="min-width:15px;">

                      <v-autocomplete multiple variant="plain" dense
                        :items="surveyColumnValueList(column.value, column.key)" v-model="filters[column.value]"
                        @click.stop="{}" class="column-header-filter">
                        <!-- Custom Selection Template -->
                        <template v-slot:selection="{ index }">
                          <!-- Display items dynamically -->
                          <span v-if="index < 1">
                            <v-icon class="mr-2" small>
                              {{ 'mdi-filter' }}
                            </v-icon>
                          </span>
                        </template>
                      </v-autocomplete>

                    </th>
                  </tr>
                </thead>
              </div>

            </template>

            <!-- text-align right the data if isNumeric = true -->
            <template v-for="header in surveyHeaders" :key="header.key" v-slot:[`item.${header.key}`]="{ item }">
              <div v-if="header.isNumeric && header.key !== 'survey_invitee' && header.key !== 'participant_name'"
                style="display:flex; justify-content: flex-end;">
                <span>
                  {{ item[header.key] }}
                </span>
              </div>
              <div v-else-if="header.key === 'survey_invitee'">
                <tr>
                  <td>
                    <router-link class="participant-details-link" :to="{
                                  name: 'EditParticipantBox',
                                  query: { iccParticipantData: encrypt(JSON.stringify(item)),
                                            dataCheck: 'survey_invitee', 
                                            userData: JSON.stringify(userData),
                                            brandData: JSON.stringify(brandData),
                                        } // <-- changed 'props' to 'params'
                              }" target="_blank">
                      {{ item.survey_invitee }}
                    </router-link>
                  </td>
                </tr>
              </div>
              <div v-else-if="header.key === 'participant_name'">
                <tr>
                  <td>
                    <router-link class="participant-details-link" :to="{
                                  name: 'EditParticipantBox',
                                  query: { iccParticipantData: encrypt(JSON.stringify(item)),
                                            dataCheck: 'participant', 
                                            userData: JSON.stringify(userData),
                                            brandData: JSON.stringify(brandData),
                                        } // <-- changed 'props' to 'params'
                              }" target="_blank">
                      {{ item.participant_name }}
                    </router-link>
                  </td>
                </tr>
              </div>
              <div v-else-if="header.key === 'ind_email'">
                <tr>
                  <td>
                    <router-link class="participant-details-link" :to="{
                                  name: 'manage_user',
                                  query: { 

                                    iccParticipantData: encrypt(JSON.stringify(item.ind_email))

                                  } // <-- changed 'props' to 'params'
                              }" target="_blank">
                      {{ item.ind_email }}
                    </router-link>
                  </td>
                </tr>
              </div>
              <div v-else-if="header.key === 'participant_email'">
                <tr>
                  <td>
                    <router-link class="participant-details-link" :to="{
                                  name: 'manage_user',
                                  query: { 

                                    iccParticipantData: encrypt(JSON.stringify(item.participant_email))

                                  } // <-- changed 'props' to 'params'
                              }" target="_blank">
                      {{ item.participant_email }}
                    </router-link>
                  </td>
                </tr>
              </div>
              <div v-else>
                <span>
                  {{ item[header.key] }}
                </span>
              </div>
            </template>

            <template v-slot:no-data>
              <v-btn color="primary" @click="initialize"> Reload Table </v-btn>
            </template>
          </v-data-table>
                </v-card>
                <div class="icc-empty" v-if="!setLoading && !isLoading && firstFetchUserData.length == 0 && secondFetchUserData.length == 0">
                  Use the filters on the left and press <strong>Select</strong> to load results.
                </div>
              </div>

              <div class="icc-table-foot">
                <div>{{ resultCount }} result{{ resultCount === 1 ? '' : 's' }}</div>
                <button type="button" class="icc-btn icc-btn-sm"
                  v-if="secondFetchUserData.length > 0 && showIndividualRecords == false" @click="downloadExcel">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 4v11m0 0l-4-4m4 4l4-4M5 20h14" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Download Results (Excel)
                </button>
              </div>
            </section>

            <!-- History (iteration log) -->
            <div class="icc-history">
        <div class="boxReports" style="
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex-wrap: wrap;
          ">
          <div :style="{ backgroundColor: brandData.main_color1 || '#0e5071' }" class="label-div">
            <v-btn variant="plain" v-ripple="false" @click.prevent="toggleIterationLog = !toggleIterationLog"
              style="font-size: 18px; background-color: transparent; border: transparent;">
              <strong>{{ toggleIterationLog ? '—' : '+' }}</strong>
            </v-btn>
            <strong style="font-weight: bold; font-size: 18px;">History</strong>
          </div>

          <div class="box-div" v-show="toggleIterationLog">
            <br />
            <div id="iteration-log-table-container">
              <clip-loader v-if="survey.iteration.iteration_id !== null" :loading="setLoading" :color="setColor"
                :size="setSize"></clip-loader>
              <table id="iteration-log-table" v-resizable-columns="'icc-history'" v-if="setLoading === false && survey.iteration.iteration_id !== null">
                <tr>
                  <th>Action</th>
                  <th>When</th>
                  <th>Who</th>
                  <th>Survey template</th>
                  <th>Status</th>
                </tr>
                <tr v-for="row in iterationLogTableRows" :key="row">
                  <td>{{ row.batch_action_name }}</td>
                  <td>{{ formatDate(row.action_when) }}</td>
                  <td>{{ row.action_by }}</td>
                  <td>{{ row.survey_template_name }}</td>
                  <td>
                    {{ row.STATUS }}
                    <a v-if="row.STATUS === 'Success'"><i class="fa-solid fa-star"></i></a>
                    <a v-else><i class="fas fa-info-circle icon-background1"></i></a>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
            </div>
        <!-- <br />
        <div
          class="boxReports"
          style="
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex-wrap: wrap;
          "
        >
          <div class="label-div">
            <v-btn
              variant="plain"
              v-ripple="false"
              @click.prevent="toggle2 = !toggle2"
              style="font-size: 18px; background-color: transparent; border: transparent;"
            >
              <strong>{{ toggle2 ? '—' : '+' }}</strong>
            </v-btn>
            <strong style="font-weight: bold; font-size: 18px;">Status definitions</strong>
          </div>
          <div class="box-div" v-show="toggle2" style="padding: 0 10px 0 10px">
            <br />
            <div>
              <FieldNameInline label="Not yet launched: "></FieldNameInline>
              <span
                >The assessment is in the system but has not been made available
                to the participant yet.</span
              >
              <br />
              <FieldNameInline label="Ready to start: "></FieldNameInline>
              <span
                >The assessment is available for the participant to begin.</span
              >
              <br />
              <FieldNameInline label="Started: "></FieldNameInline>
              <span>The participant has started the assessment.</span>
              <br />
              <FieldNameInline label="Open (submitted): "></FieldNameInline>
              <span
                >The assessment deadline has not been reached, but the
                individual has submitted the survey.</span
              >
              <br />
              <FieldNameInline label="Closed (submitted): "></FieldNameInline>
              <span
                >The assessment deadline has been reached, and the participant
                completed and submitted it.</span
              >
              <br />
              <FieldNameInline label="Closed (unsubmitted): "></FieldNameInline>
              <span
                >The assessment deadline has been reached, but the participant
                did not complete and submit it.</span
              >
              <br />
              <FieldNameInline label="Report released immediately: "></FieldNameInline>
              <span
                >The iteration's 'Never Run Iteration' is set to 'Yes'. 
                The assessment has a tag 'immediate_report_release', 
                this releases report to the participant and coach (assigned to the participant) 
                instantaneously after the participant submits the survey.</span
              >
              <br />
              <FieldNameInline label="Report available: "></FieldNameInline>
              <span
                >The final assessment report is available for both the
                participant and the coach (provided the participant has opted to
                share with the coach).</span
              >
              <br />
              <FieldNameInline
                label="Report available for participant: "
              ></FieldNameInline>
              <span
                >The final assessment report is available for the participant
                only.</span
              >
              <br />
              <FieldNameInline
                label="Report available for coach: "
              ></FieldNameInline>
              <span
                >The final assessment report is available for the coach
                only(provided the participant has opted to share with the
                coach).</span
              >
              <br />
              <FieldNameInline label="Report expired: "></FieldNameInline>
              <span
                >The assessment report is no longer available to the participant
                or the coach.</span
              >
              <br />
              <FieldNameInline label="Dropped: "></FieldNameInline>
              <span>The participant dropped the program.</span>
              <br />
              <FieldNameInline
                label="Check survey assignment dates: "
              ></FieldNameInline>
              <span
                >A "catch all" description indicating unknown status not meeting
                any of the other status listed here.</span
              >
              <br />
              <br />
            </div>
          </div>
        </div> -->
          </div>
        </main>
        <!-- END: Main content -->
      </div>
    </form>
    <AdminDashboardBulkUploadModal v-if="show_bulk_upload_modal == true" @close-modal="show_bulk_upload_modal = false"
      :userData="userData" :brandData="brandData" />
    <AdminDashboardAddUserModal v-if="show_add_user_modal == true" @close-modal="show_add_user_modal = false"
      :userData="userData" :brandData="brandData" />
    <AdminDashboardNominationModal v-if="show_nomination_modal == true" @close-modal="show_nomination_modal = false"
      :surveyAssignmentId="surveyAssignmentId" :brandData="brandData" :userData="userData" />
    <AdminDashboardNomineeDetailsModal v-if="show_nominee_modal == true" @close-modal="show_nominee_modal = false"
      :nominee_ind_id="nominee_ind_id" :survey_assignment_id="nominee_survey_assignment_id" :brandData="brandData"
      :userData="userData" />

    <!-- Move Survey Across Org Modal -->
    <div v-if="showMoveModal" class="move-modal-backdrop" @click.self="showMoveModal = false">
      <div class="move-modal">
        <div class="move-modal-header" :style="{ backgroundColor: brandData.main_color1 || '#0e5071' }">
          <h3 style="color: white; margin: 0; font-size: 16px;">Move User Survey and Report Across Organisations</h3>
          <button class="move-modal-close" @click="showMoveModal = false">✕</button>
        </div>

        <div class="move-modal-body" v-if="!moveModalResult">
          <div class="move-modal-columns">

            <!-- FROM column -->
            <div class="move-col move-col-from">
              <div class="move-col-header">
                <span class="move-col-icon">📤</span>
                <strong>FROM (Source)</strong>
              </div>
              <div class="move-field">
                <label>Organisation</label>
                <div class="move-readonly">{{ selectedFilters.organization?.name || '—' }}</div>
              </div>
              <div class="move-field">
                <label>Sub-Organisation</label>
                <div class="move-readonly">{{ selectedFilters.subOrganization?.name || '—' }}</div>
              </div>
              <div class="move-field">
                <label>Program</label>
                <div class="move-readonly">{{ selectedFilters.program?.name || '—' }}</div>
              </div>
              <div class="move-field">
                <label>Iteration</label>
                <div class="move-readonly">{{ selectedFilters.iteration?.name || '—' }}</div>
              </div>
              <div class="move-field">
                <label>Stream</label>
                <div class="move-readonly">{{ selectedFilters.stream?.name || 'All streams' }}</div>
              </div>
              <div class="move-field">
                <label>Group</label>
                <div class="move-readonly">{{ selectedFilters.group?.name || 'All groups' }}</div>
              </div>
              <div class="move-field">
                <label>Participants selected</label>
                <div class="move-readonly move-count">{{ selected.length }}</div>
              </div>
            </div>

            <!-- Arrow -->
            <div class="move-arrow">➜</div>

            <!-- TO column -->
            <div class="move-col move-col-to">
              <div class="move-col-header">
                <span class="move-col-icon">📥</span>
                <strong>TO (Target)</strong>
              </div>

              <div class="move-field">
                <label>Organisation <span class="move-required">*</span></label>
                <select class="move-select" v-model="moveModalTarget.org_id" @change="moveModalTarget.suborg_id = null; moveModalTarget.program_id = null; moveModalTarget.iteration_id = null; moveModalTarget.stream_id = null; moveModalTarget.group_id = null;">
                  <option :value="null">— Select Org —</option>
                  <option v-for="org in userOrg" :key="org.org_id" :value="org.org_id">{{ org.org_name }}</option>
                </select>
              </div>

              <div class="move-field">
                <label>Sub-Organisation <span class="move-required">*</span></label>
                <select class="move-select" v-model="moveModalTarget.suborg_id" :disabled="!moveModalTarget.org_id" @change="moveModalTarget.program_id = null; moveModalTarget.iteration_id = null; moveModalTarget.stream_id = null; moveModalTarget.group_id = null;">
                  <option :value="null">— Select Sub-Org —</option>
                  <option v-for="sub in userSubOrg.filter(s => s.org_id === moveModalTarget.org_id && s.suborg_id !== 0)" :key="sub.suborg_id" :value="sub.suborg_id">{{ sub.suborg_name }}</option>
                </select>
              </div>

              <div class="move-field">
                <label>Program <span class="move-required">*</span></label>
                <select class="move-select" v-model="moveModalTarget.program_id" :disabled="!moveModalTarget.suborg_id" @change="moveModalTarget.iteration_id = null; moveModalTarget.stream_id = null; moveModalTarget.group_id = null;">
                  <option :value="null">— Select Program —</option>
                  <option v-for="prog in userPrograms.filter(p => p.org_id === moveModalTarget.org_id && p.suborg_id === moveModalTarget.suborg_id)" :key="prog.program_id" :value="prog.program_id">{{ prog.program_name }}</option>
                </select>
              </div>

              <div class="move-field">
                <label>Iteration <span class="move-required">*</span></label>
                <select class="move-select" v-model="moveModalTarget.iteration_id" :disabled="!moveModalTarget.program_id" @change="moveModalTarget.stream_id = null; moveModalTarget.group_id = null;">
                  <option :value="null">— Select Iteration —</option>
                  <option v-for="iter in userIterations.filter(i => i.org_id === moveModalTarget.org_id && i.suborg_id === moveModalTarget.suborg_id && i.program_id === moveModalTarget.program_id)" :key="iter.iteration_id" :value="iter.iteration_id">{{ iter.iteration_name }}</option>
                </select>
              </div>

              <div class="move-field">
                <label>Stream <span class="move-optional">(optional)</span></label>
                <select class="move-select" v-model="moveModalTarget.stream_id" :disabled="!moveModalTarget.iteration_id">
                  <option :value="null">— No stream —</option>
                  <option v-for="stream in userStreams.filter(s => s.org_id === moveModalTarget.org_id && s.suborg_id === moveModalTarget.suborg_id && s.program_id === moveModalTarget.program_id && s.iteration_id === moveModalTarget.iteration_id)" :key="stream.stream_id" :value="stream.stream_id">{{ stream.stream_name }}</option>
                </select>
              </div>

              <div class="move-field">
                <label>Group <span class="move-optional">(optional)</span></label>
                <select class="move-select" v-model="moveModalTarget.group_id" :disabled="!moveModalTarget.stream_id">
                  <option :value="null">— No group —</option>
                  <option v-for="group in userGroups.filter(g => g.stream_id === moveModalTarget.stream_id)" :key="group.group_id" :value="group.group_id">{{ group.group_name }}</option>
                </select>
              </div>

            </div>
          </div>

          <!-- Warning box -->
          <div class="move-warning">
            ⚠️ This will move <strong>{{ selected.length }} participant(s)</strong> and all associated survey data, scores, PDFs, and cohort records to the target organisation. This action cannot be undone.
          </div>

          <div class="move-modal-actions">
            <button class="move-btn-cancel" @click="showMoveModal = false">Cancel</button>
            <button 
              class="move-btn-confirm" 
              :style="{ backgroundColor: brandData.accent_color1 || '#F47820' }"
              :disabled="moveModalLoading || !moveModalTarget.org_id || !moveModalTarget.suborg_id || !moveModalTarget.program_id || !moveModalTarget.iteration_id"
              @click="executeMoveAcrossOrg"
            >
              <span v-if="moveModalLoading">⏳ Moving...</span>
              <span v-else>✅ Confirm Move</span>
            </button>
          </div>
        </div>

        <!-- Result screen -->
        <div class="move-modal-body" v-if="moveModalResult">
          <div class="move-result-header" :class="moveModalResult.status === 'success' ? 'move-result-success' : 'move-result-error'">
            <span v-if="moveModalResult.status === 'success'">✅ Move Completed Successfully</span>
            <span v-else>❌ Move Failed</span>
          </div>

          <div class="move-result-stats">
            <div class="move-stat-card">
              <div class="move-stat-number">{{ moveModalResult.participantsProcessed }}</div>
              <div class="move-stat-label">Participants Moved</div>
            </div>
            <div class="move-stat-card">
              <div class="move-stat-number">{{ moveModalResult.nomineesResolved }}</div>
              <div class="move-stat-label">Nominees Resolved</div>
            </div>
          </div>

          <div class="move-result-tables">
            <div class="move-result-tables-title">Tables Updated</div>
            <table class="move-result-table">
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Field</th>
                  <th>Rows Updated</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(tableData, tableName) in moveModalResult.tableUpdates" :key="tableName">
                  <tr v-for="(count, field) in tableData" :key="tableName + field" :class="count > 0 ? 'move-row-active' : 'move-row-zero'">
                    <td><code>{{ tableName }}</code></td>
                    <td>{{ field }}</td>
                    <td><strong>{{ count }}</strong></td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div class="move-modal-actions">
            <button 
              class="move-btn-confirm" 
              :style="{ backgroundColor: brandData.accent_color1 || '#F47820' }"
              @click="showMoveModal = false; moveModalResult = null;"
            >Close</button>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "AdminDashboardBox",
  component: [
    DescriptionInline,
    DropDownInfo,
    DropDownInfoNumbered,
    HeaderReport,
    FieldNameInline,
    ButtonSubmit,
    Multiselect,
    ClipLoader,
    AdminDashboardNominationModal,
    AdminDashboardNomineeDetailsModal,
    AdminDashboardBulkUploadModal,
    AdminDashboardAddUserModal,
  ],
  props: ["buttonLabel", "userData", "brandData"],
  data: () => ({
    currentItems: [],
    isLoading: true,
    pagination: {
        sortBy: null,
        descending: false,
        page: 1,
        itemsPerPage: 5
      },
    filters: {},
    activeFilters: {},
    disableSearchButton: false,
    setColor: "#ff6a00",
    setSize: "50px",
    showIndividualRecords: false,
    toggleIterationLog: false,
    toggleGospiPicker: true,
    sidebarCollapsed: false,
    showColumnPicker: false,
    toggle2: false,
    toggle: false,
    setLoading: false,
    surveyType: "",
    statisticsData: [],
    participantsRegisteredPercentage: 0,
    participantsSubmittedPercentage: 0,
    participantsEligiblePercentage: 0,
    totalNomineesPercentage: 0,
    totalNomineesSubmittedPercentage: 0,
    iterationLogTableRows: null,
    rowLimit: 5,
    surveyAssignmentList: [],
    table_headers: [
      "NAME",
      "program_name",
      "iteration_name",
      "stream_name",
      "group_name",
    ],
    table2_headers: [
      "full_name",
      "logged_in",
      "survey_template_name",
      "tag_names",
      "launch_date",
      "survey_reminder_date",
      "initial_deadline_date",
      "final_deadline_date",
      "coach_report_start_date",
      "coach_report_end_date",
      "participant_report_start_date",
      "stream_name",
      "group_name",
      "coach_email",
      "coach_access_granted",
      "coach_group_access_granted",
      "STATUS",
      "undropped",
      "nominations_submitted",
    ],
    user_headers_data: [
        { title: "Name", 
          key: "NAME", 
          width: "2%" 
        },
        { title: "Program", 
          key: "program_name", 
          width: "2%" 
        },
        { title: "Iteration", key: "iteration_name", width: "2%" },
        { title: "Stream", key: "stream_name", width: "2%" },
        { title: "Group", key: "group_name", width: "2%" },
    ],
    survey_headers_data: [
        { title: "Survey Invitee", 
          key: "survey_invitee", 
          isNumeric: false,
          width: '1%',
          props: 
          { 
            disabled: true
          }
        },
        { title: "Ind_Id", 
          key: "ind_id", 
          isNumeric: false,
          width: '1%',
        },
        { title: "Email", 
          key: "ind_email", 
          isNumeric: false,
          width: '1%',
          props: 
          { 
            disabled: false
          }
        },
        { 
          title: "P/N", 
          key: "p_or_n", 
          isNumeric: false,
        },
        { 
          title: "Nom Role", 
          key: "pdie_role_name", 
          isNumeric: false,
        },
        { title: "Participant", 
          key: "participant_name",
          isNumeric: false,
        },
        { title: "Participant Email", 
          key: "participant_email", 
          isNumeric: false,
          width: '1%',
          props: 
          { 
            disabled: false
          }
        },
        { 
          title: "Last Logged in", 
          key: "last_login_date", 
          isNumeric: true,
          sort: (a, b) => new Date(a?.replace(/\s\S+$/, '') || 0) - new Date(b?.replace(/\s\S+$/, '') || 0),
        },
        { 
          title: "Survey Template", 
          key: "survey_template_name", 
          isNumeric: false,
        },
        { 
          title: "Tags", 
          key: "tag_names", 
          isNumeric: false,
        },
        { 
          title: "Survey Launch", 
          key: "launch_date",  
          isNumeric: true,
          sort: (a, b) => new Date(a?.replace(/\s\S+$/, '') || 0) - new Date(b?.replace(/\s\S+$/, '') || 0),
        },
        { 
          title: "Survey Reminder", 
          key: "survey_reminder_date",  
          isNumeric: true,
          sort: (a, b) => new Date(a?.replace(/\s\S+$/, '') || 0) - new Date(b?.replace(/\s\S+$/, '') || 0),
        },
        { 
          title: "Survey Initial Deadline", 
          key: "initial_deadline_date", 
          isNumeric: true,
          sort: (a, b) => new Date(a?.replace(/\s\S+$/, '') || 0) - new Date(b?.replace(/\s\S+$/, '') || 0),
        },
        { 
          title: "Survey Final Deadline", 
          key: "final_deadline_date", 
          isNumeric: true,
          sort: (a, b) => new Date(a?.replace(/\s\S+$/, '') || 0) - new Date(b?.replace(/\s\S+$/, '') || 0),
        },
        { 
          title: "Survey Progress", 
          key: "survey_progress", 
          isNumeric: false,
        },
        { 
          title: "Report Criteria Met", 
          key: "participant_report_criteria_met", 
          isNumeric: false,
        },
        { 
          title: "Self received", 
          key: "self_recd", 
          isNumeric: false,
        },
        { 
          title: "Nominations", 
          key: "nominations_submitted", 
          isNumeric: true,
        },
        { 
          title: "PS Nominations", 
          key: "psup_submitted", 
          isNumeric: true,
        },
        { 
          title: "Stream", 
          key: "stream_name", 
          isNumeric: false,
        },
        { 
          title: "Group", 
          key: "group_name",
          isNumeric: false,
        },
        { 
          title: "Primary Coach", 
          key: "coach_email", 
        },
        { 
          title: "Secondary Coach", 
          key: "secondary_coach_email", 
          isNumeric: false,
        },
        { 
          title: "Coach Permission Given", 
          key: "coach_access_granted", 
          isNumeric: false,
        },
        { 
          title: "HR Permission Given", 
          key: "hr_access_granted", 
          isNumeric: false,
        },
        { 
          title: "Coach Group Permission Given", 
          key: "coach_group_access_granted", 
          isNumeric: false,
        },
        { 
          title: "Coach report release", 
          key: "coach_report_start_date",
          isNumeric: true,
          sort: (a, b) => new Date(a?.replace(/\s\S+$/, '') || 0) - new Date(b?.replace(/\s\S+$/, '') || 0),
        },
        { 
          title: "Coach report end", 
          key: "coach_report_end_date", 
          isNumeric: true,
          sort: (a, b) => new Date(a?.replace(/\s\S+$/, '') || 0) - new Date(b?.replace(/\s\S+$/, '') || 0),
        },
        { 
          title: "Participant report release", 
          key: "participant_report_start_date", 
          isNumeric: true,
          sort: (a, b) => new Date(a?.replace(/\s\S+$/, '') || 0) - new Date(b?.replace(/\s\S+$/, '') || 0),
        },
        { 
          title: "Report status", 
          key: "report_status", 
          isNumeric: false,
        },
        { title: "Survey Invitee", 
          key: "survey_invitee", 
          isNumeric: false,
          width: '1%',
          props: 
          { 
            disabled: true
          }
        },
    ],
    selectedHeaders: [],
    displayedHeaders: [],
    columnHideShowHeader: [],
    show_nomination_modal: false,
    show_bulk_upload_modal: false,
    show_add_user_modal: false,
    show_nominee_modal: false,
    surveyAssignmentId: "",
    nominee_ind_id: "",
    nominee_survey_assignment_id: "",
    sortDirection: 1,
    sortBy: "",
    allSelected: false,
    org_id: "Loading Data...",
    coach_email_inp: null,
    secondary_coach_email_inp: null,
    suborg_inp: null,
    program_inp: null,
    program_id: null,
    iteration_id: null,
    iteration_inp: null,
    stream_id: null,
    stream_inp: null,
    group_inp: null,
    template_id: 0,
    actions: null,
    tags: null,
    surveyTemplate: null,
    selected: [],
    firstFetchUserData: [],
    secondFetchUserData: [],
    userOrg: [],
    filtersApplied: false,
    userSubOrg: [],
    userPrograms: [],
    coachUsers: [],
    userIterations: [],
    userStreams: [],
    userGroups: [],
    userSurveyTemplate: [],
    batchActions: [],
    batchActions2: [],
    userTags: [],
    filteredData: [],
    newNameSuborg: [],
    nomineeSurveyTemplateId: 0,
    dates: {
      launch: "",
      reminder: "",
      initial_deadline: "",
      coach_report_start: "",
      coach_report_end: "",
      deadline_final: "",
      participant_report_start: "",
      modified_by: "",
    },
    survey: {
      org_id: "",
      suborg: {},
      program: {},
      iteration: {},
      stream: {},
      survey_template_id: null,
      survey_template_association_id: "",
      is_test: "",
      for_sample_report: "",
      group_id: null,
      action_program_id: null,
      action_iteration_id: null,
      action_stream_id: null,
      action_tag: null,
      // action_group_id: null,
      start: "",
      launch: "",
      reminder: "",
      deadline_initial: "",
      deadline_final: "",
      coach_report_start: "",
      coach_report_end: "",
      participant_report_start: "",
      recommended_number_of_nominations: "",
      max_number_of_nominations: "",
      report_eligible_number_of_respondents: "",
      created_by: 1,
      modified_by: 1,
    },
    selected_is_nomination: 0,
    is_nomination_options: [
      { Value: 0, label: 'Participant Only' },  // This will be default
      { Value: 1, label: 'Nominee Only' },
      { Value: "All", label: 'All' }
    ],
    batchAction: false,
    default_batch_action: [
      {
      label: 'Modify Survey batch actions',
      Value: false
      },
      {
      label: 'Assign Survey batch action',
      Value: true
      }
    ],
    generatedReleaseUrl: '',
    generatedReleaseUrlExpiry: '',
    isGeneratingToken: false,
    urlCopied: false,
      showMoveModal: false,
      moveModalTarget: {
        org_id: null,
        suborg_id: null,
        program_id: null,
        iteration_id: null,
        stream_id: null,
        group_id: null,
      },
      moveModalResult: null,
      moveModalLoading: false,
      jobPollingInterval: null, // Store polling interval ID
      panelExpanded: true, // Default to expanded
      expanded: {
        organizations: true,
        subOrganizations: false,
        programs: false,
        iterations: false,
        streams: false,
        groups: false
      },
      selectedFilters: {
        organization: null,
        subOrganization: null,
        program: null,
        iteration: null,
        stream: null,
        group: null
      },
      filterType: 0,
      selectedSurveyTemplate: '',
      // Hardcoded data
      organizations: [],
      respondentSearch: '', 
      searchTimeout: null,
  }),
  async mounted() {
    this.isLoading = true;
    const keys = ['suborg_id', 'program_id', 'iteration_id', 'stream_id', 'batch_action'];

    keys.forEach((key) => {
      const value = this.getFromCookie(key);
      if (value == undefined || value == null) {
        this.storeInCookie(key, key === 'batch_action' ? false : 0);
      }
    });

    this.survey.org_id = this.userData.org_id;

    try {
      // All independent lookups fired in parallel instead of sequentially.
      // A single failure here (403, timeout, etc.) now lands in the catch
      // block below instead of silently stranding isLoading = true forever.
      const [
        columnsRes,
        subOrgRes,
        programsRes,
        iterationsRes,
        streamsRes,
        groupsRes,
        coachRes,
        indRes,
        batchActionsRes,
        tagsRes,
        orgRes,
        surveyTemplateRes,
      ] = await Promise.all([
        iccApi.get(`/icc-selected-columns-by-id/${this.userData.ind_id}`),
        iccApi.get("sub-organizations/"),
        iccApi.get("programs"),
        iccApi.get("iterations"),
        iccApi.get("streams"),
        iccApi.get("groups"),
        iccApi.get(`/individuals/get-coach-by-org/${this.userData.org_id}`),
        iccApi.get("individuals/" + this.userData.ind_id),
        iccApi.get("batch-actions/"),
        iccApi.get("tags/"),
        iccApi.get("organizations"),
        iccApi.get("survey-templates-survey_template_name"),
      ]);

      // --- selected columns ---
      if (columnsRes.status === 200) {
        const columnsData = columnsRes.data.icc_selected_columns;
        if (typeof columnsData === 'string') {
          try {
            const parsedData = JSON.parse(columnsData);
            this.selectedHeaders = Array.isArray(parsedData) ? parsedData : [parsedData];
          } catch (parseError) {
            console.error('Error parsing JSON:', parseError);
            this.selectedHeaders = this.survey_headers_data;
          }
        } else if (Array.isArray(columnsData) || typeof columnsData === 'object') {
          this.selectedHeaders = columnsData;
        } else {
          this.selectedHeaders = this.survey_headers_data;
        }
      } else {
        this.selectedHeaders = this.survey_headers_data;
      }

      // --- renderData() equivalents ---
      this.userSubOrg = subOrgRes.data;
      this.userPrograms = programsRes.data;
      this.userIterations = iterationsRes.data;
      this.userStreams = streamsRes.data;
      this.userGroups = groupsRes.data;

      // --- remaining lookups ---
      this.coachUsers = coachRes.data;
      this.org_id = indRes.data.org_name;
      this.batchActions = batchActionsRes.data;
      this.batchActions2 = batchActionsRes.data;
      this.userTags = tagsRes.data;

      orgRes.data.map((data) => {
        this.survey.max_number_of_nominations = data.d_max_number_of_nominations;
        this.survey.recommended_number_of_nominations = data.d_recommended_number_of_nominations;
        this.survey.report_eligible_number_of_respondents = data.d_report_eligible_number_of_respondents;
      });
      this.userOrg = orgRes.data;

      this.userSurveyTemplate = surveyTemplateRes.data;

      if (this.survey.suborg) {
        this.updateCookieSuborg();
      }

      // Build organizations tree for GOSPI filter - only for current user's org
      this.organizations = this.userOrg
        .filter(org => org.org_id === this.userData.org_id)
        .map(org => ({
          id: org.org_id,
          name: org.org_name,
          subOrgs: this.userSubOrg
            .filter(sub => sub.org_id === org.org_id)
            .sort((a, b) => a.suborg_name.localeCompare(b.suborg_name))
            .map(sub => ({
              id: sub.suborg_id,
              name: sub.suborg_name,
              programs: this.userPrograms
                .filter(prog => prog.org_id === org.org_id && prog.suborg_id === sub.suborg_id)
                .sort((a, b) => a.program_name.localeCompare(b.program_name))
                .map(prog => ({
                  id: prog.program_id,
                  name: prog.program_name,
                  iterations: this.userIterations
                    .filter(iter => iter.org_id === org.org_id && iter.suborg_id === sub.suborg_id && iter.program_id === prog.program_id)
                    .sort((a, b) => a.iteration_name.localeCompare(b.iteration_name))
                    .map(iter => ({
                      id: iter.iteration_id,
                      name: iter.iteration_name,
                      streams: this.userStreams
                        .filter(stream => stream.org_id === org.org_id && stream.suborg_id === sub.suborg_id && stream.program_id === prog.program_id && stream.iteration_id === iter.iteration_id)
                        .sort((a, b) => a.stream_name.localeCompare(b.stream_name))
                        .map(stream => ({
                          id: stream.stream_id,
                          name: stream.stream_name,
                          groups: this.userGroups
                            ? this.userGroups
                                .filter(g => g.stream_id === stream.stream_id)
                                .sort((a, b) => a.group_name.localeCompare(b.group_name))
                                .map(group => ({
                                  id: group.group_id,
                                  name: group.group_name
                                }))
                            : []
                        }))
                    }))
                }))
            }))
        }));

      // Load stored values first
      this.loadStoredValues();

      // Then apply auto-selection for any missing values
      this.updateSelectedFilters();

      // Set default filter type to Participant Only if not already loaded from cookies
      if (!this.filterType && this.is_nomination_options && this.is_nomination_options.length > 0) {
        this.filterType = this.is_nomination_options[0].Value;
        this.selected_is_nomination = this.filterType;
      }

    } catch (error) {
      console.error('Error loading ICC dashboard:', error);
      this.selectedHeaders = this.survey_headers_data;

      if (error?.response?.status === 403) {
        flashMessage(
          this.$flashMessage,
          '#dc3545',
          '#ffffff',
          'Session expired or access denied loading the dashboard. Please refresh the page or log in again.'
        );
      } else if (error?.code === 'ECONNABORTED') {
        flashMessage(
          this.$flashMessage,
          '#dc3545',
          '#ffffff',
          'Dashboard took too long to load. Please try again.'
        );
      } else {
        flashMessage(
          this.$flashMessage,
          '#dc3545',
          '#ffffff',
          'Failed to load dashboard data. Please refresh and try again.'
        );
      }
    } finally {
      // This now ALWAYS runs — 403, timeout, or any other failure will
      // still clear the spinner instead of leaving it stuck indefinitely.
      this.isLoading = false;
    }
},
    
  methods: {
    handleEnterSearch() {
      if (!this.disableSearchButton) {
        this.searchResult
      }
    },
    // Add this method to keep survey and selectedFilters in sync
    syncSurveyWithSelectedFilters() {
      if (this.selectedFilters) {
        // Update survey object when selectedFilters changes
        if (this.selectedFilters.organization) {
          this.survey.org_id = this.selectedFilters.organization.id;
        }
        if (this.selectedFilters.subOrganization) {
          this.survey.suborg = { suborg_id: this.selectedFilters.subOrganization.id };
        }
        if (this.selectedFilters.program) {
          this.survey.program = { program_id: this.selectedFilters.program.id };
        }
        if (this.selectedFilters.iteration) {
          this.survey.iteration = { iteration_id: this.selectedFilters.iteration.id };
        }
        if (this.selectedFilters.stream) {
          this.survey.stream = { stream_id: this.selectedFilters.stream.id };
        }
      }
    },

    getSelectedTemplateName() {
      if (!this.selectedSurveyTemplate) return '';
      const template = this.filteredSurveyTemplate.find(t => t.survey_template_id == this.selectedSurveyTemplate);
      return template ? template.survey_template_name : '';
    },

    togglePanelExpanded() {
      this.panelExpanded = !this.panelExpanded;
    },
    
    toggleExpanded(section) {
      this.expanded[section] = !this.expanded[section];
    },

    // Drives the cascading dropdowns in the sidebar using the existing selectFilter logic
    onCascadeSelect(level, list, value) {
      const order = ['organization', 'subOrganization', 'program', 'iteration', 'stream', 'group'];
      const item = (list || []).find((i) => String(i.id) === String(value));
      if (!item) {
        // "— Select —" chosen: clear this level and everything below it
        for (let i = order.indexOf(level); i < order.length; i++) {
          this.selectedFilters[order[i]] = null;
        }
        return;
      }
      if (this.selectedFilters[level]?.id === item.id) return; // already selected (avoid selectFilter's toggle-off)
      this.selectFilter(level, item);
    },

    // Opens the existing batch-action panel, optionally pre-selecting one action
    openQuickBatch(actionId) {
      this.toggle = true;
      this.clearInput();
      this.actions = actionId;
    },

    // Buttons that appear in the design but have no feature behind them yet
    placeholderAction(name) {
      Swal.fire({
        text: `"${name}" is not available yet.`,
        icon: "info",
        confirmButtonText: "Ok",
      });
    },
    
    selectFilter(filterType, item) {
      const filterHierarchy = ['organization', 'subOrganization', 'program', 'iteration', 'stream', 'group'];
      const currentIndex = filterHierarchy.indexOf(filterType);

      // If clicking the same item, deselect it
      if (this.selectedFilters[filterType]?.id === item.id) {
        this.selectedFilters[filterType] = null;
        
        // Clear all filters below the current level
        for (let i = currentIndex + 1; i < filterHierarchy.length; i++) {
          this.selectedFilters[filterHierarchy[i]] = null;
        }
        return; // Exit early
      }

      // Clear all filters below the current level
      for (let i = currentIndex + 1; i < filterHierarchy.length; i++) {
        this.selectedFilters[filterHierarchy[i]] = null;
      }

      this.selectedFilters[filterType] = item;

      // Auto-expand the next level
      if (currentIndex < filterHierarchy.length - 1) {
        const nextLevel = filterHierarchy[currentIndex + 1];
        this.expanded[nextLevel + (nextLevel.endsWith('s') ? '' : 's')] = true;
      }
    },
    
    clearAllFilters() {
      this.selectedFilters = {
        organization: null,
        subOrganization: null,
        program: null,
        iteration: null,
        stream: null,
        group: null
      };
      this.expanded = {
        organizations: true,
        subOrganizations: false,
        programs: false,
        iterations: false,
        streams: false,
        groups: false
      };
      this.filterType = this.is_nomination_options ? this.is_nomination_options[0].Value : 0,
      this.batchAction = false;
      this.selectedSurveyTemplate = '';
      this.respondentSearch = '';
    },
    
    onFilterTypeChange() {
      this.selected_is_nomination = this.filterType;
    },
    

    async sendPasswordSetupEmail(newIndId, newEmail) {
      try {
        // First get the user data
        const res1 = await iccApi.get(`individuals/emaildetails/${newIndId}`);
        this.newUserResponse = res1.data

        // Get the email template
        const templateResponse = await iccApi.get(
          `email-templates/template-type/New associate email account/org/${
            this.newUserResponse.org_id ? this.newUserResponse.org_id : 1
          }/suborg/0/program/0`
        );
        
        let arr = templateResponse.data;
        if (arr.length <= 0 || arr == "") {
          // If no template found for the org, use the default template
          const defaultTemplateResponse = await iccApi.get(
            "email-templates/template-type/New associate email account/org/0/suborg/0/program/0"
          );
          this.emailTemplateId = defaultTemplateResponse.data.email_template_id;
          this.subject = defaultTemplateResponse.data.subject;
          this.email_body = defaultTemplateResponse.data.email_body;
        } else {
          this.emailTemplateId = templateResponse.data.email_template_id;
          this.subject = templateResponse.data.subject;
          this.email_body = templateResponse.data.email_body;
        }

        // Get email details for the user
        const detailsResponse = await iccApi.get(`individuals/emaildetails/${newIndId}`);
        const res = detailsResponse.data;
        
        // Set up tokens
        const tokens = {
          recipient_email: newEmail,
          website_url: res.website_url,
          nominee_salutation: res.nominee_salutation,
          nominee_message: res.nominee_message,
          program_name: res.program_name,
          suborg_name: res.suborg_name,
          website_sender_email: res.website_sender_email,
          website_terms_url: res.website_terms_url,
          website_privacy_url: res.website_privacy_url,
          website_contact_email: res.website_contact_email,
          survey_close_date: res.survey_close_date,
          days_until_survey_close_date: res.days_until_survey_close_date,
          survey_template_name: res.survey_template_name,
          survey_description: res.survey_description,
          user_full_name: res.first_name + " " + res.last_name,
          survey_subject_first_name: res.survey_subject_first_name,
          survey_subject_full_name: res.survey_subject_full_name,
          iteration_name: res.iteration_name,
          brand_path: res.brand_path
        };

        // Generate secure password reset token for new user
        const tokenResponse = await iccApi.post('create-password-reset-token', {
          ind_id: newIndId,
          email: tokens.recipient_email,  // ← Add email from tokens object
          expiration_days: 1              // ← Add days of expiration
        });
        const resetToken = tokenResponse.data.token;

        const password_link =
          "<a href=\"" +
          tokens.website_url +
          "#/set_password?token=" +
          encodeURIComponent(resetToken) +
          "\">Click Here</a>";

        // Replace tokens in subject and body
        const convertedSubject = replaceTokens(
          this.subject,
          tokens,
          password_link
        );
        const convertedBody = replaceTokens(
          this.email_body,
          tokens,
          password_link
        );

        // Prepare and send email
        const emailData = {
          send_from: tokens.website_sender_email,
          send_to: tokens.recipient_email,
          send_cc: "",
          send_bcc: "",
          subject: convertedSubject,
          body: convertedBody,
          org_id: this.newUserResponse.org_id,
          suborg_id: this.newUserResponse.suborgs ? this.newUserResponse.suborgs : 0,
          email_template_id: this.emailTemplateId,
          ind_id: newIndId,
        };

        await iccApi.post("sendemail", emailData);
        console.log('Password setup email sent successfully');
        
        
      } catch (error) {
        console.error('Error sending password setup email:', error);
        
        
        throw error;
      }
    },

    async ensurePrimaryEmailExists(participantIndId, participantEmail) {
      try {
        const response = await iccApi.post('/add-primary-email', {
          ind_id: participantIndId,
          email: participantEmail,
          modified_by: this.userData.ind_id
        });
        
        return response.data;
      } catch (error) {
        console.error('Error ensuring primary email exists:', error);
        throw error;
      }
    },

    // New method to handle iteration processing with background jobs
    async processIteration(surveyType) {
      try {
        // Show initial processing message
        this.showFlashMessage(
          "Starting iteration processing... Please wait.",
          this.brandData.accent_color1,
          this.brandData.flash_text_color
        );

        // Special case: vfp — no API call, just simulate success
        if (surveyType === 'vfp') {
          this.showFlashMessage(
            "Iteration run successfully.",
            this.brandData.accent_color1,
            this.brandData.flash_text_color
          );
          await this.logIterationAction("Processing Skipped (VFP)");
          await this.handleJobCompleted({
            status: 'completed',
            currentStep: 'iteration-run-successfully',
            jobId: null,
            iterationId: this.survey.iteration.iteration_id
          }, surveyType);
          return;
        }

        // For small5 and others: call backend
        const response = await iccApi.post(`/force-cohort-processing/${surveyType}`, {
          iteration_id: this.survey.iteration.iteration_id,
        });

        if (response.status >= 200 && response.status <= 299) {
          const { jobId, message, iterationId } = response.data;

          // Store current job info
          this.currentJob = {
            id: jobId,
            surveyType,
            iterationId,
            status: 'started',
            startTime: new Date()
          };

          await this.logIterationAction("Processing Started");

          this.showFlashMessage(
            `${message} - Job ID: ${jobId}`,
            this.brandData.accent_color1,
            this.brandData.flash_text_color
          );

          // small5: don't start polling
          if (surveyType === 'small5') {
            console.log(`Survey type "${surveyType}" - no polling required.`);
            return;
          }

          // Default: start polling
          this.startJobStatusPolling(jobId, surveyType);
        } else {
          this.clearJobPolling();
          throw new Error(`Failed to start processing: ${response.status}`);
        }

      } catch (error) {
        this.clearJobPolling();
        console.error('Error starting iteration processing:', error);

        await this.logIterationAction("Failed");

        this.showFlashMessage(
          "Failed to start iteration processing. Please try again.",
          '#ff0000',
          '#ffffff'
        );
      }
    },

    // Method to start polling job status
    startJobStatusPolling(jobId, surveyType) {
      // Skip polling for 'small5'
      if (surveyType === 'small5') {
        console.log(`Skipping job polling for survey type: ${surveyType}`);
        return;
      }

      // Immediately mark as success for 'vfp'
      if (surveyType === 'vfp') {
        console.log(`Auto-marking job as completed for survey type: ${surveyType}`);
        this.handleJobCompleted({
          status: 'completed',
          currentStep: 'iteration-run-successfully',
          jobId,
          iterationId: jobId.split('-')[2] || null
        }, surveyType);
        return;
      }

      // Default polling logic for 360, big5, etc.
      const maxPollingTime = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
      const pollInterval = 5000; // Poll every 5 seconds
      const startTime = Date.now();
      
      let lastStep = '';
      
      const poll = async () => {
        try {
          let statusResponse;
          if (surveyType === 'big5') {
            statusResponse = await iccApi.get(`/job-status-big5/${jobId}`);
          } else {
            statusResponse = await iccApi.get(`/job-status/${jobId}`);
          }

          if (statusResponse.status === 200) {
            const jobStatus = statusResponse.data;

            if (this.currentJob && this.currentJob.id === jobId) {
              this.currentJob = { ...this.currentJob, ...jobStatus };
            }

            this.updateJobStatusUI(jobStatus, lastStep);
            lastStep = jobStatus.currentStep;

            switch (jobStatus.status) {
              case 'completed':
                this.clearJobPolling();
                await this.handleJobCompleted(jobStatus, surveyType);
                return;
              case 'failed':
                this.clearJobPolling();
                await this.handleJobFailed(jobStatus, surveyType);
                return;
              case 'started':
                if (Date.now() - startTime < maxPollingTime) {
                  this.jobPollingInterval = setTimeout(poll, pollInterval);
                } else {
                  this.clearJobPolling();
                  await this.handleJobTimeout(jobId, surveyType);
                }
                break;
              default:
                console.warn('Unknown job status:', jobStatus.status);
                this.jobPollingInterval = setTimeout(poll, pollInterval);
            }
          } else {
            console.error('Failed to get job status:', statusResponse.status);
            this.jobPollingInterval = setTimeout(poll, pollInterval * 2);
          }
        } catch (error) {
          console.error('Error polling job status:', error);
          if (Date.now() - startTime < maxPollingTime) {
            this.jobPollingInterval = setTimeout(poll, pollInterval * 2);
          } else {
            this.clearJobPolling();
            await this.handleJobTimeout(jobId, surveyType);
          }
        }
      };

      this.jobPollingInterval = setTimeout(poll, pollInterval);
    },


    // Method to clear job polling
    clearJobPolling() {
      if (this.jobPollingInterval) {
        clearTimeout(this.jobPollingInterval);
        this.jobPollingInterval = null;
      }
    },

    // Method to update UI based on job status
    updateJobStatusUI(jobStatus, lastStep) {
      const stepMessages = {
        'initializing': 'Initializing process...',
        'step1-delete': 'Deleting existing records...',
        'step2-update': 'Updating processing flags...',
        'step3-fetch': 'Fetching survey data...',
        'step4-generate': 'Generating cohort data...',
        'step5-insert': 'Inserting cohort data...',
        'step6-histogram': 'Processing histogram data...',
        'completed': 'Processing completed successfully!',
        'failed': 'Processing failed.'
      };
      
      const currentMessage = stepMessages[jobStatus.currentStep] || `Processing: ${jobStatus.currentStep}`;
      
      // Only update if step changed to avoid too many flash messages
      if (lastStep !== jobStatus.currentStep) {
        let detailMessage = currentMessage;
        
        // Add progress details for step 5
        if (jobStatus.currentStep === 'step5-insert' && jobStatus.processedRecords && jobStatus.totalRecords) {
          const progress = Math.round((jobStatus.processedRecords / jobStatus.totalRecords) * 100);
          detailMessage += ` (${progress}% - ${jobStatus.processedRecords}/${jobStatus.totalRecords} records)`;
        }
        
        this.showFlashMessage(
          detailMessage,
          this.brandData.accent_color1,
          this.brandData.flash_text_color
        );
      }
    },

    // Handle successful job completion
    async handleJobCompleted(jobStatus, surveyType) {
      try {
        // Log successful completion
        await this.logIterationAction("Success");
        
        // Log user history
        try {
          const historyResponse = await iccApi.post(`/create-user-history-log`, {
            ind_id: this.userData.ind_id,
            email: this.userData.email,
            screen: 'ICC',
            action: `Run iteration batch action`,
            action_description: `Run iteration ${surveyType} to iteration_id ${this.survey.iteration.iteration_id} - Completed successfully`,
          });
          
          if (historyResponse.status === 200) {
            console.log('user-history-log API return:', historyResponse.data);
          }
        } catch (error) {
          console.log('Error logging user history:', error);
        }
        
        // Show success message
        this.showFlashMessage(
          "Iteration processing completed successfully!",
          '#28a745', // Bootstrap success green
          '#ffffff'
        );
        
        // Reset UI state
        this.resetUIState();
        
        // Refresh the data
        this.searchResult("iteration");
        
      } catch (error) {
        console.error('Error handling job completion:', error);
      }
    },

    // Handle job failure
    async handleJobFailed(jobStatus, surveyType) {
      try {
        // Log the failure
        await this.logIterationAction("Failed");
        
        // Log user history
        try {
          await iccApi.post(`/create-user-history-log`, {
            ind_id: this.userData.ind_id,
            email: this.userData.email,
            screen: 'ICC',
            action: `Run iteration batch action`,
            action_description: `Run iteration ${surveyType} to iteration_id ${this.survey.iteration.iteration_id} - Failed: ${jobStatus.error || 'Unknown error'}`,
          });
        } catch (error) {
          console.log('Error logging user history:', error);
        }
        
        // Show error message
        this.showFlashMessage(
          `Processing failed: ${jobStatus.error || 'Unknown error'}. Please try again.`,
          '#dc3545', // Bootstrap danger red
          '#ffffff'
        );
        
        // Reset UI state
        this.resetUIState();
        
      } catch (error) {
        console.error('Error handling job failure:', error);
      }
    },

    // Handle job timeout
    async handleJobTimeout(jobId, surveyType) {
      try {
        // Log the timeout
        await this.logIterationAction("Timeout");
        
        // Log user history
        try {
          await iccApi.post(`/create-user-history-log`, {
            ind_id: this.userData.ind_id,
            email: this.userData.email,
            screen: 'ICC',
            action: `Run iteration batch action`,
            action_description: `Run iteration ${surveyType} to iteration_id ${this.survey.iteration.iteration_id} - Timeout after 2 hours`,
          });
        } catch (error) {
          console.log('Error logging user history:', error);
        }
        
        // Show timeout message
        this.showFlashMessage(
          `Processing is taking longer than expected. Job ID: ${jobId}. Please check with administrator.`,
          '#ffc107', // Bootstrap warning yellow
          '#000000'
        );
        
        // Reset UI state
        this.resetUIState();
        
      } catch (error) {
        console.error('Error handling job timeout:', error);
      }
    },

    // Helper method to log iteration actions
    async logIterationAction(status) {
      try {
        await iccApi.post(`/iteration-log`, {
          iteration_id: this.survey.iteration.iteration_id,
          action_made: this.actions,
          action_by: this.userData.email,
          action_by_id: this.userData.ind_id,
          survey_template_id: this.template_id,
          status: status,
          org_id: this.survey.org_id,
          suborg_id: this.survey.suborg.suborg_id,
          program_id: this.survey.program.program_id,
          created_by: this.userData.ind_id,
          modified_by: this.userData.ind_id,
        });
      } catch (error) {
        console.error('Error logging iteration action:', error);
      }
    },

    // Helper method to show flash messages
    showFlashMessage(message, backgroundColor, textColor) {
      flashMessage(
        this.$flashMessage,
        backgroundColor,
        textColor,
        message
      );
    },

    // Helper method to reset UI state
    resetUIState() {
      this.allSelected = false;
      this.selected = [];
      this.actions = null;
      this.currentJob = null;
    },

    // Optional: Method to cancel ongoing job polling (useful for component cleanup)
    cancelJobProcessing() {
      this.clearJobPolling();
      this.currentJob = null;
      this.showFlashMessage(
        "Job monitoring cancelled.",
        '#6c757d', // Bootstrap secondary gray
        '#ffffff'
      );
    },


    showConfirmModalInput(title = "Select Iteration") {
      return new Promise((resolve) => {
        // Create modal container
        const modalContainer = document.createElement('div');
        modalContainer.className = 'modal-backdrop';
        modalContainer.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 998;
        `;

        // Create modal content
        const modalContent = document.createElement('div');
        modalContent.className = 'modal-content';
        modalContent.style.cssText = `
          background-color: white;
          padding: 20px;
          border-radius: 4px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          width: 400px;
          max-width: 90%;
        `;

        // Create modal header
        const modalHeader = document.createElement('div');
        modalHeader.className = 'modal-header';
        modalHeader.innerHTML = `<h3 style="margin-top: 0;">${title}</h3>`;

        // Create dropdown for iterations
        const selectContainer = document.createElement('div');
        selectContainer.style.margin = '15px 0';

        const selectElement = document.createElement('select');
        selectElement.id = 'iteration-select';
        selectElement.className = 'form-control';
        selectElement.style.width = '100%';
        selectElement.style.padding = '8px';

        // Add default option
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = '-- Select Iteration --';
        defaultOption.selected = true;
        defaultOption.disabled = true;
        selectElement.appendChild(defaultOption);

        // Add options from filteredIteration
        this.filteredIteration.forEach(iteration => {
          if (iteration.iteration_id === 0) return;

          const option = document.createElement('option');
          option.value = JSON.stringify({
            iteration_id: iteration.iteration_id,
            iteration_name: iteration.iteration_name
          });
          option.textContent = `${iteration.iteration_name} (ID: ${iteration.iteration_id})`;
          selectElement.appendChild(option);
        });


        selectContainer.appendChild(selectElement);

        // Create button container
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'modal-footer';
        buttonContainer.style.cssText = `
          display: flex;
          justify-content: flex-end;
          margin-top: 15px;
        `;

        // Create cancel button
        const cancelButton = document.createElement('button');
        cancelButton.textContent = 'Cancel';
        cancelButton.className = 'btn btn-secondary';
        cancelButton.style.marginRight = '10px';
        cancelButton.onclick = () => {
          document.body.removeChild(modalContainer);
          resolve(null);
        };

        // Create confirm button
        const confirmButton = document.createElement('button');
        confirmButton.textContent = 'Confirm';
        confirmButton.className = 'btn btn-primary';
        confirmButton.onclick = () => {
          const selectedValue = selectElement.value;
          document.body.removeChild(modalContainer);
          resolve(selectedValue || null);
        };

        // Add buttons to button container
        buttonContainer.appendChild(cancelButton);
        buttonContainer.appendChild(confirmButton);

        // Assemble modal
        modalContent.appendChild(modalHeader);
        modalContent.appendChild(selectContainer);
        modalContent.appendChild(buttonContainer);
        modalContainer.appendChild(modalContent);

        // Add modal to document
        document.body.appendChild(modalContainer);

        // Focus the select element
        selectElement.focus();

        // Close modal if clicking outside
        modalContainer.addEventListener('click', (e) => {
          if (e.target === modalContainer) {
            document.body.removeChild(modalContainer);
            resolve(null);
          }
        });
      });
    },

    async logSelectedHeaders() {

      try {
        const res = iccApi.put(`update-icc-selected-columns-by-id/${this.userData.ind_id}`, {
          newColumnString: JSON.stringify(this.selectedHeaders),
        })
        if(res.status === 200) {
          console.log(res)
        }
      } catch (error) {
        console.log('Error is :', error)
      }

    },

    async updateIconColor(column) {
      if (!column || !column.key) {
        console.error('Column or column.key is undefined', column);
        return;
      }


      this.filtersApplied = Object.values(this.filters).some(filter => filter.length > 0);
      
      // Find the index of the current header
      const columnIndex = this.surveyHeaders.findIndex(h => h.key === column.key);
      
      if (columnIndex === -1) {
        console.error('Column key not found in surveyHeaders', column.key);
        return;
      }

      // Construct the selector based on the column index
      const dropdownIconSelector = `.column-header-filter:nth-child(${columnIndex + 1}) .v-input__icon--append`;
      
      // Query for the dropdown icon using the constructed selector
      const dropdownIcon = this.$el.querySelector(dropdownIconSelector);
      
      if (dropdownIcon) {
        dropdownIcon.style.color = this.filtersApplied ? 'red' : ''; // Change 'red' to the desired color
      } else {
        console.error('Dropdown icon not found for selector', dropdownIconSelector);
      }
    },

    formatDate(dateString) {
      const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false };
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', options).format(date);
    },

    selectAll() {
      if (this.allSelected) {
        const selected = this.secondFetchUserData.map(
          (u) => u
        );
        this.selected = selected;
      } else {
        this.selected = [];
      }
    },

    encrypt(src) {
      let passphrase = "iccData"
      return CryptoJS.AES.encrypt(src, passphrase).toString();
    },

     encrypt2(src, seed) {
      return CryptoJS.AES.encrypt(src, seed).toString();
    },

    handleOptionsUpdate() {
      // console.log("Excel Data:", this.filteredData2);
    },
    async downloadExcel() {
      try {
        // Create a new array with objects having 'title' as key
        const dataForExcel = this.filteredData2.map(item => {
          const newData = {};
          this.survey_headers_data.forEach(header => {
            newData[header.title] = item[header.key];
          });
          return newData;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        // Save the workbook
        XLSX.writeFile(workbook, 'data.xlsx');
        Swal.fire({
          text: 'Excel file downloaded successfully.',
          icon: "success",
          confirmButtonText: "Ok",
          allowOutsideClick: false
        });
      } catch (error) {
        Swal.fire({
          text: 'Error during Excel download: ' + error.message,
          icon: "error",
          confirmButtonText: "Ok",
          allowOutsideClick: false
        });
        console.error('Error during Excel download:', error);
      }
    },
    updateCookieSuborg() {
      if(this.filteredSubOrg.length === 2){
          this.survey.suborg = this.filteredSubOrg[1]
        }
    },
    updateCookie() {
      //populate program and iteration automatically if only 1 data
      if(this.filteredProgram.length === 2){
        this.survey.program = this.filteredProgram[1]
        if(this.filteredIteration.length === 2){
          this.survey.iteration = this.filteredIteration[1]
        } else {
          this.survey.iteration = {}
        }
      } else{
        this.survey.program = {}
        this.survey.iteration = {}
        this.survey.stream = {}
      }
    },
    clearGridData(){
      this.survey.suborg.suborg_id = 0; // or undefined
      this.survey.program.program_id = 0; // or undefined
      this.survey.iteration.iteration_id = 0; // or undefined
      this.survey.stream.stream_id = 0; // or undefined
      this.template_id = null
      this.firstFetchUserData = [];
      this.secondFetchUserData = [];
      this.selected = [];
      this.allSelected = false;
      this.toggle = false;
      this.actions = null;
      this.surveyTemplate = null;
      this.sortBy = "";
      this.tags = null;
      this.survey.action_program_id = null;
      this.survey.action_iteration_id = null;
      this.survey.action_stream_id = null;
      this.survey.group_id = null;
      this.survey.survey_template_id = null;
      this.suborg_inp = null;
      this.program_inp = null;
      this.iteration_inp = null;
      this.stream_inp = null;
      this.group_inp = null;
      this.coach_email_inp = null;
      this.secondary_coach_email_inp = null;
      this.dates.deadline_final = "";
      this.dates.launch = "";
      this.dates.reminder = "";
      this.dates.coach_report_start = "";
      this.dates.coach_report_end = "";
      this.dates.participant_report_start = "";
      this.dates.initial_deadline = "";
      this.statisticsData = [];
      this.participantsRegisteredPercentage = 0;
      this.participantsSubmittedPercentage = 0;
      this.participantsEligiblePercentage = 0;
      this.totalNomineesPercentage = 0;
      this.totalNomineesSubmittedPercentage = 0;
      this.nomineeSurveyTemplateId = 0;
      this.iterationLogTableRows = null;
      // this.clearCookie('suborg_id');
      // this.clearCookie('program_id');
      // this.clearCookie('iteration_id');
      // this.clearCookie('stream_id');

      this.filters = {};
    },
    storeInCookie(key, value) {
      VueCookies.set(key, value, '1d'); // Store for 7 days
    },
    getFromCookie(key) {
      return VueCookies.get(key);
    },
    clearCookie(key) {
      VueCookies.remove(key);
    },
    clearAllCookies() {
      VueCookies.keys().forEach(key => {
        VueCookies.remove(key);
      });
    },
    loadStoredValues() {
      try {
        // Set organization to the first (and likely only) organization
        if (this.organizations && this.organizations[0]) {
          this.selectedFilters.organization = this.organizations[0];
        }

        // Load suborg
        const storedSuborgId = this.getFromCookie('suborg_id');
        if (storedSuborgId && this.organizations[0]) {
          const suborgId = parseInt(storedSuborgId, 10);
          const matchedSuborg = this.organizations[0].subOrgs?.find(s => s.id === suborgId);
          if (matchedSuborg) {
            this.selectedFilters.subOrganization = matchedSuborg;
          }
        }

        // Load program
        const storedProgramId = this.getFromCookie('program_id');
        if (storedProgramId && this.selectedFilters.subOrganization) {
          const programId = parseInt(storedProgramId, 10);
          const matchedProgram = this.selectedFilters.subOrganization.programs?.find(p => p.id === programId);
          if (matchedProgram) {
            this.selectedFilters.program = matchedProgram;
          }
        }

        // Load iteration
        const storedIterationId = this.getFromCookie('iteration_id');
        if (storedIterationId && this.selectedFilters.program) {
          const iterationId = parseInt(storedIterationId, 10);
          const matchedIteration = this.selectedFilters.program.iterations?.find(i => i.id === iterationId);
          if (matchedIteration) {
            this.selectedFilters.iteration = matchedIteration;
          }
        }

        // Load stream
        const storedStreamId = this.getFromCookie('stream_id');
        if (storedStreamId && this.selectedFilters.iteration) {
          const streamId = parseInt(storedStreamId, 10);
          const matchedStream = this.selectedFilters.iteration.streams?.find(s => s.id === streamId);
          if (matchedStream) {
            this.selectedFilters.stream = matchedStream;
          }
        }

        // Load group
        const storedGroupId = this.getFromCookie('group_id');
        if (storedGroupId && this.selectedFilters.stream) {
          const groupId = parseInt(storedGroupId, 10);
          const matchedGroup = this.selectedFilters.stream.groups?.find(g => g.id === groupId);
          if (matchedGroup) {
            this.selectedFilters.group = matchedGroup;
          }
        }

        // Load filter type
        const storedFilterType = this.getFromCookie('filter_type');
        if (storedFilterType !== null && storedFilterType !== undefined) {
          this.filterType = parseInt(storedFilterType);
          this.selected_is_nomination = this.filterType;
        }

        // Load batch action
        let storedBatchAction = this.getFromCookie('batch_action');

        // Convert string values back to boolean
        if (storedBatchAction === "true") {
          storedBatchAction = true;
        } else if (storedBatchAction === "false") {
          storedBatchAction = false;
        }

        if (storedBatchAction) {
          this.batchAction = storedBatchAction;
          this.showIndividualRecords = storedBatchAction;
        }


        // Load survey template
        const storedTemplate = this.getFromCookie('survey_template');
        if (storedTemplate) {
          this.selectedSurveyTemplate = storedTemplate;
        }

      } catch (error) {
        console.warn('Failed to load stored values:', error);
      }
    },
    updateSelectedFilters() {
      // Auto-populate suborg if only one option (plus the default empty one)
      if (this.organizations[0]?.subOrgs?.length === 1) {
        this.selectedFilters.subOrganization = this.organizations[0].subOrgs[0];
      }

      // Auto-populate program if only one option and suborg is selected
      if (this.selectedFilters.subOrganization?.programs?.length === 1) {
        this.selectedFilters.program = this.selectedFilters.subOrganization.programs[0];
        
        // Auto-populate iteration if only one option and program is selected
        if (this.selectedFilters.program?.iterations?.length === 1) {
          this.selectedFilters.iteration = this.selectedFilters.program.iterations[0];
          
          // Auto-populate stream if only one option and iteration is selected
          if (this.selectedFilters.iteration?.streams?.length === 1) {
            this.selectedFilters.stream = this.selectedFilters.iteration.streams[0];
            
            // Auto-populate group if only one option and stream is selected
            if (this.selectedFilters.stream?.groups?.length === 1) {
              this.selectedFilters.group = this.selectedFilters.stream.groups[0];
            }
          }
        }
      }
    },
    async sort(head) {
      const sorted = `Sorted ${
        head == "NAME"
          ? "Name"
          : head == "program_name"
          ? "Program"
          : head == "iteration_name"
          ? "Iteration"
          : head == "full_name"
          ? "Full Name"
          : head == "logged_in"
          ? "Logged In"
          : head == "survey_template_name"
          ? "Template"
          : head == "tag_names"
          ? "Tags"
          : head == "launch_date"
          ? "Launch"
          : head == "survey_reminder_date"
          ? "Deadline reminder"
          : head == "initial_deadline_date"
          ? "Deadline"
          : head == "final_deadline_date"
          ? "Final deadline"
          : head == "coach_report_start_date"
          ? "Coach report"
          : head == "participant_report_start_date"
          ? "User report"
          : head == "stream_name"
          ? "Stream"
          : head == "group_name"
          ? "Group"
          : head == "coach_email"
          ? "Coach"
          : head == "coach_access_granted"
          ? "Coach access granted"
          : head == "coach_group_access_granted"
          ? "Coaching group access granted"
          : head == "STATUS"
          ? "Status"
          : head == "nominations_submitted"
          ? "Nominations submitted"
          : head
      } column successfully.`;
      this.sortBy = head;
      this.sortDirection *= -1;

      if (head == "" && head == undefined) {
        this.searchResult();
      } else {
        this.searchResult(sorted);
      }
    },
    surveyInitFilters() {
      // Initialize filters based on the items in the data
      for (const col of this.survey_headers_data) {
        this.filters[col.key] = this.secondFetchUserData.map(item => item[col.key]).filter(
          (value, index, self) => self.indexOf(value) === index
        );
      }
      // TODO restore previous activeFilters before add/remove item
      this.activeFilters = { ...this.filters };
    },

    userInitFilters() {
      // Initialize filters based on the items in the data
      for (const col of this.user_headers_data) {
        this.filters[col.key] = this.firstFetchUserData.map(item => item[col.key]).filter(
          (value, index, self) => self.indexOf(value) === index
        );
      }
      // TODO restore previous activeFilters before add/remove item
      this.activeFilters = { ...this.filters };
    },

    surveyColumnValueList(val, key) {
        const uniqueValues = new Set();
        
        this.secondFetchUserData.forEach((d) => {
          const value = d[val];
          
          // Check if the value is not empty, null, and not already added to the Set
          if (value !== '' && value !== null && !uniqueValues.has(value)) {
            uniqueValues.add(value);
          }
        });

        // Add null value only if the key is 'group_name' and uniqueValues set is empty
        if (key === 'group_name' && !uniqueValues.has(null)) {
            uniqueValues.add(null);
        }

        // Convert the Set back to an array and return it
        return Array.from(uniqueValues);
    },

    userColumnValueList(val) {
        const uniqueValues = new Set();
        
        this.firstFetchUserData.forEach((d) => {
          const value = d[val];
          
          // Check if the value is not empty, null, and not already added to the Set
          if (value !== '' && value !== null && !uniqueValues.has(value)) {
            uniqueValues.add(value);
          }
        });

        // Convert the Set back to an array and return it
        return Array.from(uniqueValues);
    },

    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    showNominationModal(data) {
      this.show_nomination_modal = true;
      this.surveyAssignmentId = data;
    },

    showNomineeModal(ind_id, survey_assignment_id) {
      this.show_nominee_modal = true;
      this.nominee_ind_id = ind_id;
      this.nominee_survey_assignment_id = survey_assignment_id;
    },
    // sortMethods(head, direction) {
    //   return direction === 1wSurveytype
    //     ? (a, b) => (b[head] > a[head] ? -1 : a[head] > b[head] ? 1 : 0)
    //     : (a, b) => (a[head] > b[head] ? -1 : b[head] > a[head] ? 1 : 0);
    // },

    dynamicSort(property, direction) {
      if (property[0] === "-") {
        property = property.substr(1);
      }
      return function (a, b) {
        var result =
          a[property] < b[property] ? 1 : a[property] > b[property] ? -1 : 0;
        // console.log(result)
        if (a[property] === "" || a[property] === null) return 1;
        if (b[property] === "" || b[property] === null) return -1;
        if (a[property] === b[property]) return 0;
        return result * direction;
        // console.log(result * direction);
        // return result * direction;
      };
    },

    async showSurveyType() {
      this.disableSearchButton = true;
      await iccApi
        .get(`/survey-templates/survey-type/${this.template_id}`)
        .then((result) => {
            this.surveyType = result.data.survey_type;
            if (result.data.nominee_survey_template_id != null) {
              this.nomineeSurveyTemplateId = result.data.nominee_survey_template_id;
            }
        });
      setTimeout(this.enableSearchButton, 2000);
    },

    enableSearchButton() {
      this.disableSearchButton = false;
    },

    async renderData() {
      await iccApi.get("sub-organizations/").then((result) => {
        this.userSubOrg = result.data;
      });

      await iccApi.get("programs").then((result) => {
        this.userPrograms = result.data;
      });

      await iccApi.get("iterations").then((result) => {
        this.userIterations = result.data;
      });

      await iccApi.get("streams").then((result) => {
        this.userStreams = result.data;
      });

      await iccApi.get("groups").then((result) => {
        this.userGroups = result.data;
      });
    },

    incrementIndex(index) {
      return index + 1;
    },

    async swalValidation() {
      if (this.survey.iteration.iteration_id == null && this.actions == 19) {
        try {
          Swal.fire({
            text: `You didn't select an iteration at the top. Please select one.`,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          }).then((result) => {
            if (result.isConfirmed) {
              this.firstFetchUserData = [];
              this.toggle = false;
              this.allSelected = false;
              this.actions = null;
            }
          });
        } catch (error) {
          console.error(error);
        }
      } else if (this.survey.launch == "" && this.survey.launch == null) {
        try {
          Swal.fire({
            text: `Lauch date is required. Please input a date.`,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          }).then((result) => {
            if (result.isConfirmed) {
              this.survey.launch = "";
            }
          });
        } catch (error) {
          console.error(error);
        }
      } else if (
        this.survey.deadline_final == "" &&
        this.survey.deadline_final == null
      ) {
        try {
          Swal.fire({
            text: `Final deadline date is required. Please input a date.`,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          }).then((result) => {
            if (result.isConfirmed) {
              this.survey.deadline_final = "";
            }
          });
        } catch (error) {
          console.error(error);
        }
      } else {
        try {
          this.firstHandleSave();
        } catch (error) {
          console.error(error);
        }
      }
    },

    firstHandleSave: async function () {
      //Add survey
      if (this.actions == 19) {
        for (const data of this.userSurveyTemplate) {
          if (data.survey_template_id === this.survey.survey_template_id) {
            this.survey.survey_template_association_id =
              data.survey_template_association_id;
          }
        }
        if (!this.survey.deadline_initial || !this.survey.deadline_final) {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1
              ? this.brandData.accent_color1
              : "#F47820",
            this.brandData.flash_text_color
              ? this.brandData.flash_text_color
              : "#ffffff",
            "Initial deadline and final deadline are required fields"
          );
        } else {
          var pass = this.validateDates(
            this.survey.deadline_initial,
            this.survey.deadline_final
          );
          if (!pass) {
            return flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color
                ? this.brandData.flash_text_color
                : "#ffffff",
              "Please make sure the deadline final date is after the deadline initial date"
            );
          }
          if (this.survey.coach_report_start && this.survey.coach_report_end) {
            pass = this.validateDates(
              this.survey.coach_report_start,
              this.survey.coach_report_end
            );
            if (!pass) {
              return flashMessage(
                this.$flashMessage,
                this.brandData.accent_color1
                  ? this.brandData.accent_color1
                  : "#F47820",
                this.brandData.flash_text_color
                  ? this.brandData.flash_text_color
                  : "#ffffff",
                "Please make sure the coach report end date is after the coach report start date"
              );
            }
          }
        }
        this.actions = null;
        this.toggle = false;

        console.log("Survey data before API request:", this.survey);
        for (var i = 0; i < this.selected.length; i++) {
          console.log("Selected user:", this.selected[i]);
          await iccApi
          .post("survey-assignments", {
            ...this.survey,
            org_id: this.survey.org_id,
            suborg_id: this.survey.suborg.suborg_id,
            program_id: this.survey.program.program_id,
            iteration_id: this.survey.iteration.iteration_id,
            stream_id: this.survey.stream.stream_id,
            logged_in_user: this.userData.ind_id,
            ind_id: this.selected[i].ind_id,
            recipient_email: this.selected[i].email,
          })
          .then(async (res) => {
            const surveyAssignmentId = res.data.id;
            await this.prepopulateSurvey(
              surveyAssignmentId,
              this.survey.survey_template_id,
              this.survey.org_id,
              this.survey.suborg.suborg_id,
              this.userData.ind_id
            );
            console.log("add survey",res.data)
              flashMessage(
                this.$flashMessage,
                this.brandData.accent_color1
                  ? this.brandData.accent_color1
                  : "#F47820",
                this.brandData.flash_text_color
                  ? this.brandData.flash_text_color
                  : "#ffffff",
                this.selected.length > 1
                  ? "Adding survey to selected users... Please wait for this message to be gone."
                  : "Successfully added survey to the selected user."
              );
              try {
                const res = iccApi.post(`/create-user-history-log`, {
                  ind_id: this.userData.ind_id,
                  email: this.userData.email,
                  screen: 'ICC',
                  action: `Add survey batch action`,
                  action_description: `Assigned survey_template_id ${this.survey.survey_template_id} to email ${this.selected[i].email} with ind_id ${this.selected[i].ind_id}`,
                })
                if(res.status === 200) {
                  console.log('user-history-log API return :', res)
                }
              } catch (error) {
                console.log('Error is :', error)
              }
            })
            .catch((error) => {
              console.log(error);
            });
        }
        //Add additional iteration
      } else if (this.actions == 21) {
        this.actions = null;
        this.toggle = false;
        if (
          this.survey.action_program_id != null &&
          this.survey.suborg.suborg_id != null
        ) {
          for (var j = 0; j < this.selected.length; j++) {
            await iccApi
              .put(
                `individuals/ind-group-program-iteration/${this.selected[j].ind_id}`,
                {
                  ...this.survey,
                }
              )
              .then(() => {
                flashMessage(
                  this.$flashMessage,
                  this.brandData.accent_color1
                    ? this.brandData.accent_color1
                    : "#F47820",
                  this.brandData.flash_text_color
                    ? this.brandData.flash_text_color
                    : "#ffffff",
                  "Modifying selected user(s)... Please wait for this message to be gone."
                );
                try {
                const res = iccApi.post(`/create-user-history-log`, {
                  ind_id: this.userData.ind_id,
                  email: this.userData.email,
                  screen: 'ICC',
                  action: `Update program/iteration batch action`,
                  action_description: `Update ind_id ${this.selected[j].ind_id} from program_id ${this.survey.program.program_id} and iteration_id ${this.survey.iteration.iteration_id} to new program_id ${this.survey.action_program_id} and new iteration_id ${this.survey.action_iteration_id}`,
                })
                if(res.status === 200) {
                  console.log('user-history-log API return :', res)
                }
              } catch (error) {
                console.log('Error is :', error)
              }
              })
              .catch((error) => {
                console.log(error);
              });
          }
        } else {
          try {
            Swal.fire({
              text: `You didn't select a sub-organization at the top. Please select one.`,
              confirmButtonColor: "#3085d6",
              confirmButtonText: "Ok",
            }).then((result) => {
              if (result.isConfirmed) {
                this.firstFetchUserData = [];
                this.toggle = false;
                this.allSelected = false;
                this.actions = null;
              }
            });
          } catch (error) {
            console.error(error);
          }
        }
        //Assign group
      } else if (this.actions == 22) {
        this.actions = null;
        this.toggle = false;
        if (
          this.survey.action_stream_id != null &&
          this.survey.suborg.suborg_id != null &&
          this.survey.program.program_id !== null &&
          this.survey.iteration.iteration_id != null
        ) {
          for (var k = 0; k < this.selected.length; k++) {
            await iccApi
              .put(
                `individuals/ind-group-stream-group/${this.selected[k].ind_id}`,
                {
                  ...this.survey,
                }
              )
              .then(() => {
                flashMessage(
                  this.$flashMessage,
                  this.brandData.accent_color1
                    ? this.brandData.accent_color1
                    : "#F47820",
                  this.brandData.flash_text_color
                    ? this.brandData.flash_text_color
                    : "#ffffff",
                  "Modifying selected user(s)... Please wait for this message to be gone."
                );
                try {
                  const res = iccApi.post(`/create-user-history-log`, {
                    ind_id: this.userData.ind_id,
                    email: this.userData.email,
                    screen: 'ICC',
                    action: `Assign group batch action`,
                    action_description: `Assign group to ind_id ${this.selected[k].ind_id} stream_id = ${this.survey.action_stream_id} and group_id ${this.survey.group_id}`,
                  })
                  if(res.status === 200) {
                    console.log('user-history-log API return :', res)
                  }
                } catch (error) {
                  console.log('Error is :', error)
                }
              })
              .catch((error) => {
                console.log(error);
              });
          }
        }
      }
      this.survey.action_program_id = null;
      this.survey.action_iteration_id = null;
      this.survey.action_stream_id = null;
      this.survey.group_id = null;
      this.allSelected = false;
      this.selected = [];
      this.searchResult();
    },

    secondHandleSave: async function () {
      //Add tag
      if (this.actions == 20) {
        this.actions = null;
        this.toggle = false;

        const tag_id = this.survey.action_tag.join(",");

        const promises = this.selected.map((assignment) => {
          return iccApi.post("survey_assignment_tags/", {
            survey_assignment_id: assignment.survey_assignment_id,
            tag_id: tag_id,
            org_id: this.survey.org_id,
          });
        });

        Promise.all(promises)
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Adding tag(s) to selected users... Please wait for this message to be gone."
                : "Successfully added tag(s) to the selected user."
            );
            const historyLogPromises = this.selected.map((assignment) => {
              return iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Add tag batch action`,
                action_description: `Add tag_id ${tag_id} to ind_id ${assignment.ind_id}`,
              });
            });

            Promise.all(historyLogPromises)
              .then((res) => {
                console.log('user-history-log API return :', res)
              })
              .catch((error) => {
                console.log('Error is :', error)
              });

            this.allSelected = false;
            this.selected = [];
            this.tags = null;
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
      }
      //Change final deadline date
      else if (this.actions == 7) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/dates/${newValue}`, {
            deadline_final: this.dates.deadline_final,
            modified_at: "",
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change final deadline date batch action`,
                action_description: `Change final deadline date for survey_assignment_id ${newValue} to final deadline date ${this.dates.deadline_final}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            iccApi.get(`survey-assignment/earliest-final-deadline-date/${this.survey.iteration.iteration_id}`)
            .then((result)=>{
                iccApi.put(`iterations/final-deadline-date-update/${result.data.iteration_id}`, {
                  final_deadline_date: result.data.earliest_final_deadline,
                  modified_by: this.userData.ind_id
                }).then(()=>{
                  flashMessage(
                    this.$flashMessage,
                    this.brandData.accent_color1
                      ? this.brandData.accent_color1
                      : "#F47820",
                    this.brandData.flash_text_color,
                    this.selected.length > 1
                      ? "Updating final deadline date to selected users... Please wait for this message to be gone."
                      : "Successfully update final deadline date of the selected user."
                  );
                })
            })
            this.dates.deadline_final = "";
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
        //Change launch date
      } else if (this.actions == 8) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/dates/${newValue}`, {
            launch: this.dates.launch,
            modified_at: "",
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Updating launch date to selected users... Please wait for this message to be gone."
                : "Successfully update launch date of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change launch date batch action`,
                action_description: `Change launch date for survey_assignment_id ${newValue} to launch date ${this.dates.launch}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.dates.launch = "";
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
        //Change survey reminder date
      } else if (this.actions == 9) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/dates/${newValue}`, {
            reminder: this.dates.reminder,
            modified_at: "",
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Updating survey reminder date to selected users... Please wait for this message to be gone."
                : "Successfully update survey reminder date of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change survey reminder date batch action`,
                action_description: `Change survey reminder date for survey_assignment_id ${newValue} to Survey reminder date ${this.dates.reminder}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.dates.reminder = "";
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
        //Change coach report start date
      } else if (this.actions == 10) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/dates/${newValue}`, {
            coach_report_start: this.dates.coach_report_start,
            modified_at: "",
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Updating coach report start date to selected users... Please wait for this message to be gone."
                : "Successfully update coach report start date of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change coach report start date batch action`,
                action_description: `Change coach report start date for survey_assignment_id ${newValue} to Coach report start date ${this.dates.coach_report_start}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.dates.coach_report_start = "";
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
        //Change coach report end date
      } else if (this.actions == 11) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/dates/${newValue}`, {
            coach_report_end: this.dates.coach_report_end,
            modified_at: "",
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Updating coach report end date to selected users... Please wait for this message to be gone."
                : "Successfully update coach report end date of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change coach report end date batch action`,
                action_description: `Change coach report end date for survey_assignment_id ${newValue} to Coach report end date ${this.dates.coach_report_end}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.dates.coach_report_end = "";
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
        //Change participant report start date
      } else if (this.actions == 12) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/dates/${newValue}`, {
            participant_report_start: this.dates.participant_report_start,
            modified_at: "",
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Updating participant report start date to selected users... Please wait for this message to be gone."
                : "Successfully update participant report start date of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change participant report start date batch action`,
                action_description: `Change participant report start date for survey_assignment_id ${newValue} to Participant report start date ${this.dates.participant_report_start}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.dates.participant_report_start = "";
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
        //Change initial deadline date
      } else if (this.actions == 13) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/dates/${newValue}`, {
            initial_deadline: this.dates.initial_deadline,
            modified_at: "",
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Updating initial deadline date to selected users... Please wait for this message to be gone."
                : "Successfully update initial deadline date of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change initial deadline date batch action`,
                action_description: `Change initial deadline date for survey_assignment_id ${newValue} to Initial deadline date ${this.dates.initial_deadline}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.dates.initial_deadline = "";
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
      }
      // change group name
      else if (this.actions == 5) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/group-name/${newValue}`, {
            group_id: this.group_inp,
            coach_id: null,
            secondary_coach_id: null,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `change group name batch action`,
                action_description: `change group for survey_assignment_id ${newValue} to group_id ${this.group_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            iccApi
              .put(`/survey-assignment/change-nominee-group/${newValue}`, {
                group_id: this.group_inp,
                coach_id: null,
                secondary_coach_id: null,
                modified_by: this.userData.ind_id,
              })
              .then(() => {
                flashMessage(
                  this.$flashMessage,
                  this.brandData.accent_color1
                    ? this.brandData.accent_color1
                    : "#F47820",
                  this.brandData.flash_text_color,
                  this.selected.length > 1
                    ? "Updating group to selected users... Please wait for this message to be gone."
                    : "Successfully update group of the selected user."
                );
                this.group_inp = null;
                this.allSelected = false;
                this.selected = [];
                this.searchResult();
              });
          })
          .catch((error) => {
            console.log(error);
          });
      }
      // change stream name
      else if (this.actions == 4) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/stream-name/${newValue}`, {
            stream_id: this.stream_inp,
            coach_id: null,
            secondary_coach_id: null,
            group_id: 0,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `change stream name batch action`,
                action_description: `change stream name for survey_assignment_id ${newValue} to stream_id ${this.stream_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            iccApi
              .put(`/survey-assignment/change-nominee-stream/${newValue}`, {
                stream_id: this.stream_inp,
                coach_id: null,
                secondary_coach_id: null,
                group_id: 0,
                modified_by: this.userData.ind_id,
              })
              .then(() => {
                flashMessage(
                  this.$flashMessage,
                  this.brandData.accent_color1
                    ? this.brandData.accent_color1
                    : "#F47820",
                  this.brandData.flash_text_color,
                  this.selected.length > 1
                    ? "Updating stream to selected users... Please wait for this message to be gone."
                    : "Successfully update stream of the selected user."
                );
                this.stream_inp = null;
                this.allSelected = false;
                this.selected = [];
                this.searchResult();
              });
          })
          .catch((error) => {
            console.log(error);
          });
      }
      // change iteration name
      else if (this.actions == 3) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/iteration-name/${newValue}`, {
            iteration_id: this.iteration_inp,
            coach_id: null,
            secondary_coach_id: null,
            stream_id: 0,
            group_id: 0,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `change iteration name batch action`,
                action_description: `change iteration for survey_assignment_id ${newValue} to iteration_id ${this.iteration_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            iccApi
              .put(`/survey-assignment/change-nominee-iteration/${newValue}`, {
                iteration_id: this.iteration_inp,
                coach_id: null,
                secondary_coach_id: null,
                stream_id: 0,
                group_id: 0,
                modified_by: this.userData.ind_id,
              })
              .then(() => {
                flashMessage(
                  this.$flashMessage,
                  this.brandData.accent_color1
                    ? this.brandData.accent_color1
                    : "#F47820",
                  this.brandData.flash_text_color,
                  this.selected.length > 1
                    ? "Updating iteration to selected users... Please wait for this message to be gone."
                    : "Successfully update iteration of the selected user."
                );
                this.iteration_inp = null;
                this.allSelected = false;
                this.selected = [];
                this.searchResult();
              });
          })
          .catch((error) => {
            console.log(error);
          });
      }
      // change program name
      else if (this.actions == 2) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/program-name/${newValue}`, {
            program_id: this.program_inp,
            coach_id: null,
            secondary_coach_id: null,
            iteration_id: 0,
            stream_id: 0,
            group_id: 0,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `change program name batch action`,
                action_description: `change program for survey_assignment_id ${newValue} to program_id ${this.program_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            iccApi
              .put(`/survey-assignment/change-nominee-program/${newValue}`, {
                program_id: this.program_inp,
                coach_id: null,
                secondary_coach_id: null,
                iteration_id: 0,
                stream_id: 0,
                group_id: 0,
                modified_by: this.userData.ind_id,
              })
              .then(() => {
                flashMessage(
                  this.$flashMessage,
                  this.brandData.accent_color1
                    ? this.brandData.accent_color1
                    : "#F47820",
                  this.brandData.flash_text_color,
                  this.selected.length > 1
                    ? "Updating program to selected users... Please wait for this message to be gone."
                    : "Successfully update program of the selected user."
                );
                this.iteration_inp = null;
                this.allSelected = false;
                this.selected = [];
                this.searchResult();
              });
          })
          .catch((error) => {
            console.log(error);
          });
      }
      // change suborg name
      else if (this.actions == 1) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/change-suborg/${newValue}`, {
            suborg_id: this.suborg_inp,
            coach_id: null,
            secondary_coach_id: null,
            program_id: 0,
            iteration_id: 0,
            stream_id: 0,
            group_id: 0,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `change suborg name batch action`,
                action_description: `change suborg for survey_assignment_id ${newValue} to suborg_id ${this.suborg_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            iccApi
              .put(`/survey-assignment/change-nominee-suborg/${newValue}`, {
                suborg_id: this.suborg_inp,
                coach_id: null,
                secondary_coach_id: null,
                program_id: 0,
                iteration_id: 0,
                stream_id: 0,
                group_id: 0,
                modified_by: this.userData.ind_id,
              })
              .then(() => {
                flashMessage(
                  this.$flashMessage,
                  this.brandData.accent_color1
                    ? this.brandData.accent_color1
                    : "#F47820",
                  this.brandData.flash_text_color,
                  this.selected.length > 1
                    ? "Updating suborg to selected users... Please wait for this message to be gone."
                    : "Successfully update suborg of the selected user."
                );
                this.suborg_inp = null;
                this.allSelected = false;
                this.selected = [];
                this.searchResult();
              });
          })
          .catch((error) => {
            console.log(error);
          });
      }
      // drop
      else if (this.actions == 14) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/drop-undrop/${newValue}`, {
            dropped_status: 1,
            undropped_date: null,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `drop batch action`,
                action_description: `drop survey_assignment_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Updating drop selected users... Please wait for this message to be gone."
                : "Successfully drop selected user."
            );
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
      }
      //undrop
      else if (this.actions == 15) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/drop-undrop/${newValue}`, {
            dropped_status: 0,
            dropped_date: null,
            undropped: 1,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `undrop batch action`,
                action_description: `undrop survey_assignment_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Updating undrop selected users... Please wait for this message to be gone."
                : "Successfully undrop the selected user."
            );
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
      }
      // Resend survey launch email
      else if (this.actions == 16) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .get(`/survey-assignment/trigger-resend-survey-launch/${newValue}`)
          .then((res) => {
            console.log(res.data)
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Resend survey launch batch action`,
                action_description: `Resend survey-launch to survey_assignment_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Resending survey launch email to selected users... Please wait for this message to be gone."
                : "Successfully resend survey launch email to the selected user."
            );
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
      }
      //Un-Submit a Survey
      else if (this.actions == 23) {
          this.actions = null;
          this.toggle = false;
          let totalProcessed = 0; // Counter to track the number of processed surveys
          let totalNotSubmitted = 0; // Counter to track the number of surveys not submitted
          for (var j = 0; j < this.selected.length; j++) {
            console.log('e2', this.selected[j].survey_assignment_id)
            await iccApi
              .get(`/survey-assignments/${this.selected[j].survey_assignment_id}`) // jhun quick fix for the un-submit not working, need to review.
              .then((result) => {
                let selectedSubmittedStatus = result.data.submitted_status;
                let selectedFullname = result.data.user_full_name;
                // console.log(selectedSubmittedStatus);
                // console.log(selectedFullname);
                totalProcessed++; // Increment the processed counter

                if (selectedSubmittedStatus != 1) {
                  alert(`Individual ${selectedFullname} did not submit the survey yet.`);
                  totalNotSubmitted++; // Increment the not submitted counter
                } else if (selectedSubmittedStatus == 1) {
                  iccApi.put(
                    `/survey-assignment/un-submit-survey/${this.selected[j].survey_assignment_id}`,
                    {
                      submitted_status: 0,
                      modified_by: this.userData.ind_id,
                    }
                  ).then(() => {
                    // try {
                    //   const res = iccApi.post(`/create-user-history-log`, {
                    //     ind_id: this.userData.ind_id,
                    //     email: this.userData.email,
                    //     screen: 'ICC',
                    //     action: `Un-Submit a Survey batch action`,
                    //     action_description: `Un-Submit Survey for survey_assignment_id ${this.selected[j].survey_assignment_id}`,
                    //   });
                    //   if(res.status === 200) {
                    //     console.log('user-history-log API return :', res);
                    //   }
                    // } catch (error) {
                    //   console.log('Error is :', error);
                    // }
                  }).catch((error) => {
                    console.log('Error in un-submitting survey:', error);
                  });
                }
              }).catch((error) => {
                console.log('Error in fetching survey assignment:', error);
              });
          }

          // Display the alert with the summary of the unsubmission process
          alert(`Un-Submit Survey(s) Completed! \nTotal Processed: ${totalProcessed} \nTotal Un-Submitted: ${totalNotSubmitted}`);
      }
      //remove tag
      else if (this.actions == 24) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");
        const tag_id = this.survey.action_tag.join(",");

        iccApi
          .delete(
            `del-survey_assignment_tags/survey-assignment/${newValue}/tag/${tag_id}`
          )
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Deleting tag(s) of selected users... Please wait for this message to be gone."
                : "Successfully delete tag(s) of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Remove tag batch action`,
                action_description: `Remove tag_id ${tag_id} to ind_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
      }
      //run iteration
      else if (this.actions == 25) {
        this.toggle = false;

        let surveyType;

        if (this.surveyType == 1) {
          surveyType = "big5";
        } else if (this.surveyType == 2) {
          surveyType = "360";
        } else if (this.surveyType == 4) {
          surveyType = "qsort";
        } else if (this.surveyType == 5) {
          surveyType = "vfp";
        } else if (this.surveyType == 6) {
          surveyType = "small5";
        }

        Swal.fire({
          text: "Running the Iteration will recalculate the cohort scores and regenerate PDF's for every participant. Are you sure?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: this.brandData.accent_color1,
          confirmButtonText: "Yes",
          cancelButtonColor: this.brandData.accent_color1,
          cancelButtonText: "No",
        }).then((result) => {
          if (result.isConfirmed) {
            this.processIteration(surveyType);
          }
        });
      }
    
      // add primary coach email
      else if (this.actions == 61) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/add-coach-email/${newValue}`, {
            type_of_coach: "primary_coach",
            coach_id: this.coach_email_inp,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Adding primary coach of the selected users... Please wait for this message to be gone."
                : "Successfully add primary coach of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Add primary coach email batch action`,
                action_description: `Add primary coach email for survey_assignment_id ${newValue} with coach_id ${this.coach_email_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
          
        }
      // change primary coach email
      else if (this.actions == 6) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/change-coach-email/${newValue}`, {
            type_of_coach: "primary_coach",
            coach_id: this.coach_email_inp,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Changing primary coach of the selected users... Please wait for this message to be gone."
                : "Successfully changed primary coach of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change primary coach email batch action`,
                action_description: `Change primary coach email for survey_assignment_id ${newValue} with coach_id ${this.coach_email_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
          
        }
    // remove primary coach email
      else if (this.actions == 62) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/remove-coach-email/${newValue}`, {
            type_of_coach: "primary_coach",
            coach_id: null,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Removing primary coach of the selected users... Please wait for this message to be gone."
                : "Successfully remove primary coach of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Remove primary coach email batch action`,
                action_description: `Remove primary coach email for survey_assignment_id ${newValue} with coach_id ${this.coach_email_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
          
        }
    // add secondary coach email
      else if (this.actions == 63) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/add-coach-email/${newValue}`, {
            type_of_coach: "secondary_coach",
            secondary_coach_id: this.secondary_coach_email_inp,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Adding secondary coach of the selected users... Please wait for this message to be gone."
                : "Successfully add secondary coach of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Add secondary coach email batch action`,
                action_description: `Add secondary coach email for survey_assignment_id ${newValue} with secondary_coach_id ${this.secondary_coach_email_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
          
        }
      // change secondary coach email
      else if (this.actions == 64) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/change-coach-email/${newValue}`, {
            type_of_coach: "secondary_coach",
            secondary_coach_id: this.secondary_coach_email_inp,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Changing secondary coach of the selected users... Please wait for this message to be gone."
                : "Successfully change secondary coach of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Change secondary coach email batch action`,
                action_description: `Change secondary coach email for survey_assignment_id ${newValue} with secondary_coach_id ${this.secondary_coach_email_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
          
        }
    // remove secondary coach email
      else if (this.actions == 65) {
        this.actions = null;
        this.toggle = false;
        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");

        await iccApi
          .put(`/survey-assignment/remove-coach-email/${newValue}`, {
            type_of_coach: "secondary_coach",
            secondary_coach_id: null,
            modified_by: this.userData.ind_id,
          })
          .then(() => {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Removing secondary coach of the selected users... Please wait for this message to be gone."
                : "Successfully remove secondary coach of the selected user."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Remove secondary coach email batch action`,
                action_description: `Remove secondary coach email for survey_assignment_id ${newValue} with secondary_coach_id ${this.secondary_coach_email_inp}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
          
        }
        //Calculate selected participants scores
        else if(this.actions == 58){
        this.actions = null;
        this.toggle = false;
        this.surveyAssignmentList = this.selected.map((item) => ({
          survey_assignment_id: item.survey_assignment_id,
        }));
        // new output sample - [
        // {survey_assignment_id: 2984},
        // {survey_assignment_id: 2985},
        // ]
        await iccApi
          .post(`/CalculateParticipant`, this.surveyAssignmentList)
          .then(() => {

            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length > 1
                ? "Calculating scores of the selected user(s)... Please wait for this message to be gone."
                : "Successfully calculated scores of the selected user(s)."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Calculate selected participants scores batch action`,
                action_description: `Calculate selected participants scores for survey_assignment_id ${this.surveyAssignmentList}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.searchResult();
          })
          .catch((error) => {
            console.log(error);
          });
          //Generate PDF's
      }else if(this.actions == 59){
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;
        // new output sample - [
        // {survey_assignment_id: 2984},
        // {survey_assignment_id: 2985},
        // ]
        this.surveyAssignmentList = this.selected.map((item) => ({
          survey_assignment_id: item.survey_assignment_id,
        }));
        console.log('frontend id', this.surveyAssignmentList)
        await iccApi
          .post(`/process-generate-pdf-by-survey-assignment-id`, this.surveyAssignmentList)
          .then((res) => {
            console.log(res)
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              this.selected.length >= 1
                ? 'Generated PDF for '+ this.selected.length + ' participant(s).'
                : 'Generated PDF for '+ this.selected.length + ' participant(s).'
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Generate PDF's batch action`,
                action_description: `Generate PDF's for survey_assignment_id ${this.surveyAssignmentList}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.setLoading = false;
          })
          .catch((error) => {
            console.log(error);
          });
        //make pdf available
      }else if(this.actions == 60){
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");
        
        await iccApi
          .put(`/survey-assignment/pdf-available/${newValue}`,{
            modified_by: this.userData.ind_id,
          })
          .then((res) => {
            console.log(res)
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              "Pdf of "+ this.selected.length +" user(s) is now available."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Make pdf available batch action`,
                action_description: `Make pdf available for survey_assignment_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.setLoading = false;
          })
          .catch((error) => {
            console.log(error);
          });

      }

      // hide PDF
        else if(this.actions == 68){
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");
        
        await iccApi
          .put(`/hide-pdf/${newValue}`,{
            modified_by: this.userData.ind_id,
          })
          .then((res) => {
            console.log(res)
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              "Pdf of "+ this.selected.length +" user(s) is now available."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Hide pdf batch action`,
                action_description: `Hide pdf for survey_assignment_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.setLoading = false;
          })
          .catch((error) => {
            console.log(error);
          });

      }
      // hide report
      else if(this.actions == 66){
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");
        
        await iccApi
          .put(`/hide-report/${newValue}`,{
            modified_by: this.userData.ind_id,
          })
          .then((res) => {
            console.log(res)
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              "Pdf of "+ this.selected.length +" user(s) is now available."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Hide report button batch action`,
                action_description: `Hide report button for survey_assignment_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.setLoading = false;
          })
          .catch((error) => {
            console.log(error);
          });

      }
       // show report
       else if(this.actions == 67){
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        const selectedMappedValue = this.selected.map(
          (u) => u.survey_assignment_id
        );
        const newValue = selectedMappedValue.join(",");
        
        await iccApi
          .put(`/show-report/${newValue}`,{
            modified_by: this.userData.ind_id,
          })
          .then((res) => {
            console.log(res)
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              "Pdf of "+ this.selected.length +" user(s) is now available."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Show report button batch action`,
                action_description: `Show report button for survey_assignment_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.setLoading = false;
          })
          .catch((error) => {
            console.log(error);
          });

      }
      // Enable PDF Upload
      else if(this.actions == 69){
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        const selectedMappedValue = this.selected.map(
          (u) => u.ind_id
        );
        const newValue = selectedMappedValue.join(",");
        // Convert to array
        const indArray = newValue.split(",").map(Number);
        await iccApi
          .put(`/update-upload-PDF/`,{
            ind_id: indArray,
            enable: true
          })
          .then((res) => {
            console.log(res)
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              "Pdf of "+ this.selected.length +" user(s) is now available."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Enable PDF Upload batch action`,
                action_description: `Enable PDF Upload for ind_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.setLoading = false;
          })
          .catch((error) => {
            console.log(error);
          });

      }
      // Inhibit PDF Upload
      else if(this.actions == 70){
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        const selectedMappedValue = this.selected.map(
          (u) => u.ind_id
        );
        const newValue = selectedMappedValue.join(",");
        // Convert to array
        const indArray = newValue.split(",").map(Number);

        await iccApi
          .put(`/update-upload-PDF/`,{
            ind_id: indArray,
            enable: false
          })
          .then((res) => {
            console.log(res)
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color,
              "Pdf of "+ this.selected.length +" user(s) is now available."
            );
            try {
              const res = iccApi.post(`/create-user-history-log`, {
                ind_id: this.userData.ind_id,
                email: this.userData.email,
                screen: 'ICC',
                action: `Inhibit PDF Upload batch action`,
                action_description: `Inhibit PDF Upload for ind_id ${newValue}`,
              })
              if(res.status === 200) {
                console.log('user-history-log API return :', res)
              }
            } catch (error) {
              console.log('Error is :', error)
            }
            this.allSelected = false;
            this.selected = [];
            this.setLoading = false;
          })
          .catch((error) => {
            console.log(error);
          });

      }
      // Upload PDF
      else if(this.actions == 71) {
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        const selectedMappedValue = this.selected.map(u => ({
          ind_id: u.ind_id,
          participant_name: u.participant_name
        }));
        
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".pdf";

        const fileSelected = new Promise((resolve) => {
          input.onchange = (e) => resolve(e.target.files[0] || null);
          input.oncancel = () => resolve(null);
        });

        input.click();

        const file = await fileSelected;
        if (!file) {
          this.setLoading = false;
          return;
        }

        if (!file.name.endsWith(".pdf")) {
          alert("Only PDF files are allowed.");
          this.setLoading = false;
          return;
        }

        const newFileName = await this.showRenameModal(file.name.replace(".pdf", ""));
        if (!newFileName) {
          alert("File name is required.");
          this.setLoading = false;
          return;
        }

        if (newFileName.length > 15) {
          alert("File name must be 15 characters or less.");
          this.setLoading = false;
          return;
        }

        const renamedFile = new File([file], `${newFileName}.pdf`, { type: file.type });

        try {
          // Get all ind_ids
          const indArray = selectedMappedValue.map(u => u.ind_id);
          
          // Check existence for all selected users in one request
          const checkResponse = await iccApi.post("/check-PDF-exists", {
            ind_id: indArray,
            pdfName: renamedFile.name,
          });

          // Process the results to identify existing and non-existing files
          const existingResults = checkResponse.data.results || [];
          const usersWithExistingFiles = selectedMappedValue.filter(user => 
            existingResults.some(result => result.ind_id === user.ind_id && result.exists)
          );
          const usersWithoutFiles = selectedMappedValue.filter(user => 
            !existingResults.some(result => result.ind_id === user.ind_id && result.exists)
          );

          // Show first modal with check results if there are existing files
          if (usersWithExistingFiles.length > 0) {
            const message = `File "${renamedFile.name}" already exists for:\n\n${
              usersWithExistingFiles.map(u => `- ${u.participant_name}`).join('\n')
            }\n\nTotal users with existing files: ${usersWithExistingFiles.length}\nTotal users without existing files: ${usersWithoutFiles.length}\n\nProceed with upload for users without existing files?`;

            if (!confirm(message)) {
              this.setLoading = false;
              return;
            }
          }

          // Proceed with upload for users without existing files
          const uploadResults = [];
          
          for (const user of usersWithoutFiles) {
            const formData = new FormData();
            formData.append("pdfBuffer", renamedFile);
            formData.append("ind_id", JSON.stringify([user.ind_id]));

            try {
              const response = await iccApi.post("/upload-PDF-by-user", formData, {
                headers: { "Content-Type": "multipart/form-data" },
              });

              uploadResults.push({
                ...user,
                status: response.data.status === "success" ? "success" : "failed",
                message: response.data.message
              });
            } catch (error) {
              uploadResults.push({
                ...user,
                status: "failed",
                message: error.response?.data?.message || error.message
              });
            }
          }

          // Show final results modal
          const successfulUploads = uploadResults.filter(r => r.status === "success");
          const failedUploads = uploadResults.filter(r => r.status === "failed");
          const resultMessage = `Upload Results:\n\nFailed Uploads (${failedUploads.length}):\n${
            failedUploads.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }\n\n${
            usersWithExistingFiles.length > 0 ? 
            `Skipped (File Already Exists) (${usersWithExistingFiles.length}):\n${
              usersWithExistingFiles.map(u => `- ${u.participant_name}`).join('\n')
            }\n\n` : ''
          }Successful Uploads (${successfulUploads.length}):\n${
            successfulUploads.map(u => `- ${u.participant_name}`).join('\n')
          }`;


          alert(resultMessage);

          // Create history log
          try {
            const res = await iccApi.post(`/create-user-history-log`, {
              ind_id: this.userData.ind_id,
              email: this.userData.email,
              screen: 'ICC',
              action: `Upload PDF batch action`,
              action_description: `Upload PDF for users with ind_ids: ${successfulUploads.map(u => u.ind_id).join(', ')}. File: ${renamedFile.name}`,
            });
            if(res.status === 200) {
              console.log('user-history-log API return:', res);
            }
          } catch (error) {
            console.error('History log error:', error);
          }

          this.allSelected = false;
          this.selected = [];
          this.setLoading = false;

        } catch (error) {
          console.error("Error in upload process:", error);
          alert(`Error in upload process: ${error.message || "Unknown error occurred"}`);
          this.setLoading = false;
        }
      }
      // Delete PDF
      else if(this.actions == 72) {
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        const selectedMappedValue = this.selected.map(u => ({
          ind_id: u.ind_id,
          participant_name: u.participant_name
        }));
        
        let fileName = await this.showDeleteConfirmModal("Enter PDF name to delete");
        if (!fileName) {
          alert("File name is required.");
          this.setLoading = false;
          return;
        }

        if (!fileName.endsWith(".pdf")) {
          fileName += ".pdf";
        }

        try {
          // Get all ind_ids
          const indArray = selectedMappedValue.map(u => u.ind_id);
          
          // Check existence for all selected users
          const checkResponse = await iccApi.post("/check-PDF-exists", {
            ind_id: indArray,
            pdfName: fileName,
          });

          const existingResults = checkResponse.data.results || [];
          const usersWithFile = selectedMappedValue.filter(user => 
            existingResults.some(result => result.ind_id === user.ind_id && result.exists)
          );

          if (usersWithFile.length === 0) {
            alert(`No users have the file "${fileName}"`);
            this.setLoading = false;
            return;
          }

          const message = `Permanently delete "${fileName}" from the TalentSage system? for the selected users\n\n${
            usersWithFile.map(u => `- ${u.participant_name}`).join('\n')
          }\n\nTotal users with this file: ${usersWithFile.length}\n\nProceed?`;

          if (!confirm(message)) {
            this.setLoading = false;
            return;
          }

          // Proceed with deletion
          const deleteResults = [];
          
          for (const user of usersWithFile) {
            try {
              const response = await iccApi.post("/delete-PDF-by-user", {
                ind_id: [user.ind_id],
                pdfName: fileName,
              });

              deleteResults.push({
                ...user,
                status: response.data.status === "success" ? "success" : "failed",
                message: response.data.message
              });
            } catch (error) {
              deleteResults.push({
                ...user,
                status: "failed",
                message: error.response?.data?.message || error.message
              });
            }
          }

          // Show final results modal
          const successfulDeletes = deleteResults.filter(r => r.status === "success");
          const failedDeletes = deleteResults.filter(r => r.status === "failed");

          const resultMessage = `Delete Results:\n\nFailed Deletions (${failedDeletes.length}):\n${
            failedDeletes.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }\n\nSuccessful Deletions (${successfulDeletes.length}):\n${
            successfulDeletes.map(u => `- ${u.participant_name}`).join('\n')
          }`;


          alert(resultMessage);

          // Create history log
          try {
            const res = await iccApi.post(`/create-user-history-log`, {
              ind_id: this.userData.ind_id,
              email: this.userData.email,
              screen: 'ICC',
              action: `Delete PDF batch action`,
              action_description: `Deleted PDF for users with ind_ids: ${successfulDeletes.map(u => u.ind_id).join(', ')}. File: ${fileName}`,
            });
            if(res.status === 200) {
              console.log('user-history-log API return:', res);
            }
          } catch (error) {
            console.error('History log error:', error);
          }

          this.allSelected = false;
          this.selected = [];
          this.setLoading = false;

        } catch (error) {
          console.error("Error in delete process:", error);
          alert(`Error in delete process: ${error.message || "Unknown error occurred"}`);
          this.setLoading = false;
        }
      }
      // Change a Participant's Iteration
      else if (this.actions === 75) {
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        if (!this.selected.length) {
          alert("Please select at least one participant.");
          this.setLoading = false;
          return;
        }

        const selectedMapped = this.selected.map(u => ({
          survey_assignment_id: u.survey_assignment_id,
          participant_name: u.participant_name,
        }));

        let newIteration = JSON.parse(await this.showConfirmModalInput("Choose new Iteration"))
        console.log('newIteration', newIteration);
        if (!newIteration) {
          alert("Need to select an Iteration!");
          this.setLoading = false;
          return;
        }

        const confirmText = `Change iteration to "${newIteration.iteration_name}" for the following participants?\n\n${
          selectedMapped.map(u => `- ${u.participant_name}`).join('\n')
        }\n\nProceed?`;

        if (!confirm(confirmText)) {
          this.setLoading = false;
          return;
        }

        const resultSummary = [];

        for (const participant of selectedMapped) {
          try {
            const response = await iccApi.post('/change-iteration', {
              survey_assignment_id: participant.survey_assignment_id,
              iteration_id: newIteration.iteration_id,
              modified_by: this.userData.ind_id,
            });

            resultSummary.push({
              ...participant,
              status: response.data.status === "success" ? "success" : "failed",
              message: response.data.message || "Updated",
            });
          } catch (error) {
            resultSummary.push({
              ...participant,
              status: "failed",
              message: error.response?.data?.message || error.message
            });
          }
        }

        const successful = resultSummary.filter(r => r.status === "success");
        const failed = resultSummary.filter(r => r.status === "failed");

        const message = `Change Iteration Results:\n\nFailed (${failed.length}):\n${
          failed.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
        }\n\nSuccess (${successful.length}):\n${
          successful.map(u => `- ${u.participant_name}`).join('\n')
        }`;

        alert(message);

        // Add to user log
        try {
          await iccApi.post(`/create-user-history-log`, {
            ind_id: this.userData.ind_id,
            email: this.userData.email,
            screen: 'ICC',
            action: `Change Iteration Batch Action`,
            action_description: `Changed iteration to "${newIteration.iteration_name}" for: ${successful.map(u => u.participant_name).join(', ')}`,
          });
        } catch (error) {
          console.error('History log error:', error);
        }

        this.allSelected = false;
        this.selected = [];
        this.setLoading = false;
      }
      // Batch Link Backup Email Action
      else if (this.actions === 78) {
        this.toggle = false;
        this.setLoading = true;

        if (!this.selected.length) {
          alert("Please select at least one participant.");
          this.setLoading = false;
          return;
        }

        const selectedMapped = this.selected.map(u => ({
          survey_assignment_id: u.survey_assignment_id,
          participant_name: u.participant_name,
          ind_id: u.ind_id,
          email: u.ind_email,
        }));

        const resultSummary = [];

        for (const participant of selectedMapped) {
          const backupEmail = prompt(`Enter backup email for ${participant.participant_name} (${participant.email}):`);
          console.log('participant details', participant);

          if (!backupEmail || !backupEmail.trim()) {
            resultSummary.push({
              ...participant,
              status: "failed",
              message: "No valid backup email entered.",
            });
            continue;
          }

          try {
            // Step 1: Check if backup email already exists
            const { data } = await iccApi.get(`/individuals-check-email-exist/${backupEmail}`);

            if (data.message !== "exist") {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: `Email ${backupEmail} does not exist in the platform.`,
              });
              continue;
            }

            const backupIndId = data.data.ind_id;

            // Step 2: Add backup email relationship
            await this.ensurePrimaryEmailExists(participant.ind_id, participant.email);

            const response = await iccApi.post('/add-backup-email', {
              primary_ind_id: participant.ind_id,
              ind_id: backupIndId,
              email: backupEmail,
              verified: 1,
              modified_by: this.userData.ind_id,
            });

            console.log('add-backup-email-response', response);

            resultSummary.push({
              ...participant,
              status: "success",
              message: `Email ${backupEmail} exists. Linked successfully to primary email ${participant.email}`,
            });

          } catch (error) {
            resultSummary.push({
              ...participant,
              status: "failed",
              message: error.response?.data?.message || error.message
            });
          }
        }

        // Step 3: Show results
        const successful = resultSummary.filter(r => r.status === "success");
        const failed = resultSummary.filter(r => r.status === "failed");

        const message = `Add Backup Email Results:\n\nFailed (${failed.length}):\n${failed.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }\n\nSuccess (${successful.length}):\n${successful.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }`;

        alert(message);

        try {
          await iccApi.post(`/create-user-history-log`, {
            ind_id: this.userData.ind_id,
            email: this.userData.email,
            screen: 'ICC',
            action: `Add Backup Email Batch Action`,
            action_description: `Added backup emails for: ${successful.map(u => u.participant_name).join(', ')}`,
          });
        } catch (err) {
          console.error('History log error:', err);
        }

        this.allSelected = false;
        this.selected = [];
        this.setLoading = false;
      }
      // Batch Un-Link Backup Email Action
      else if (this.actions === 79) {
        this.toggle = false;
        this.setLoading = true;

        if (!this.selected.length) {
          alert("Please select at least one participant.");
          this.setLoading = false;
          return;
        }

        const selectedMapped = this.selected.map(u => ({
          survey_assignment_id: u.survey_assignment_id,
          participant_name: u.participant_name,
          ind_id: u.ind_id,
          current_email: u.ind_email, // This might not always be the primary email
        }));

        const resultSummary = [];

        for (const participant of selectedMapped) {
          try {
            // Step 1: Fetch all associated emails for this participant
            const { data: emails } = await iccApi.get(`/get-associated-emails/${participant.ind_id}`);

            if (!emails || emails.length === 0) {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: "No associated emails found.",
              });
              continue;
            }

            // Step 2: Identify primary email (where primary = 1)
            const primaryEmailObj = emails.find(e => e.primary === 1);
            if (!primaryEmailObj) {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: "No primary email found in associated emails.",
              });
              continue;
            }
            const primaryEmail = primaryEmailObj.email;

            // Step 3: Filter out the primary email from backup emails list
            const backupEmails = emails.filter(e => e.email !== primaryEmail);

            if (backupEmails.length === 0) {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: "No backup emails found to unlink (excluding primary email).",
              });
              continue;
            }

            // Step 4: For each participant, show prompt to select which email to unlink
            const emailList = backupEmails.map(e => e.email).join('\n');
            const emailToRemove = prompt(
              `Participant: ${participant.participant_name}\nPrimary Email: ${primaryEmail}\n\nBackup Emails:\n${emailList}\n\nEnter the backup email you want to unlink:`
            );

            if (!emailToRemove || !emailToRemove.trim()) {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: "No email selected for unlinking.",
              });
              continue;
            }

            // Verify the entered email exists in the backup emails (and isn't the primary)
            const emailExists = backupEmails.some(e => e.email === emailToRemove);
            if (!emailExists) {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: `Email ${emailToRemove} not found in backup emails (or is primary email).`,
              });
              continue;
            }

            // Step 5: Confirm removal
            const confirmed = confirm(`Are you sure you want to permanently unlink ${emailToRemove} from ${primaryEmail}?`);
            if (!confirmed) {
              resultSummary.push({
                ...participant,
                status: "cancelled",
                message: "Unlinking cancelled by user.",
              });
              continue;
            }

            // Step 6: Perform the unlinking
            const response = await iccApi.delete('/remove-email', {
              data: {
                primary_ind_id: participant.ind_id,
                email: emailToRemove,
                modified_by: this.userData.ind_id
              }
            });
            console.log('remove-email response', response);

            resultSummary.push({
              ...participant,
              status: "success",
              message: `Successfully unlinked ${emailToRemove} from ${primaryEmail}`,
            });

          } catch (error) {
            resultSummary.push({
              ...participant,
              status: "failed",
              message: error.response?.data?.message || error.message
            });
          }
        }

        // Step 7: Show results
        const successful = resultSummary.filter(r => r.status === "success");
        const failed = resultSummary.filter(r => r.status === "failed");
        const cancelled = resultSummary.filter(r => r.status === "cancelled");

        const message = `Unlink Backup Email Results:\n\nFailed (${failed.length}):\n${failed.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }\n\nCancelled (${cancelled.length}):\n${cancelled.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }\n\nSuccess (${successful.length}):\n${successful.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }`;

        alert(message);

        try {
          await iccApi.post(`/create-user-history-log`, {
            ind_id: this.userData.ind_id,
            email: this.userData.email,
            screen: 'ICC',
            action: `Unlink Backup Email Batch Action`,
            action_description: `Unlinked backup emails for: ${successful.map(u => u.participant_name).join(', ')}`,
          });
        } catch (err) {
          console.error('History log error:', err);
        }

        this.allSelected = false;
        this.selected = [];
        this.setLoading = false;
      }
      // Move User Survey and Report Across Organisations
      else if (this.actions === 76) {
        this.toggle = false;

        if (!this.selected.length) {
          alert("Please select at least one participant.");
          return;
        }

        // Reset modal state and open it
        this.moveModalTarget = {
          org_id: null,
          suborg_id: null,
          program_id: null,
          iteration_id: null,
          stream_id: null,
          group_id: null,
        };
        this.moveModalResult = null;
        this.moveModalLoading = false;
        this.showMoveModal = true;
        // Don't reset this.actions here — keep it so the modal knows context
      }
      // Batch Set Primary Email Action
      else if (this.actions === 80) {
        this.toggle = false;
        this.setLoading = true;

        if (!this.selected.length) {
          alert("Please select at least one participant.");
          this.setLoading = false;
          return;
        }

        const selectedMapped = this.selected.map(u => ({
          survey_assignment_id: u.survey_assignment_id,
          participant_name: u.participant_name,
          ind_id: u.ind_id,
          current_email: u.ind_email,
        }));

        const resultSummary = [];

        for (const participant of selectedMapped) {
          try {
            // Step 1: Fetch all associated emails for this participant
            const { data: emails } = await iccApi.get(`/get-associated-emails/${participant.ind_id}`);

            if (!emails || emails.length === 0) {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: "No associated emails found.",
              });
              continue;
            }

            // Step 2: Show prompt to select which email to set as primary
            const emailList = emails.map(e => e.email).join('\n');
            const newPrimaryEmail = prompt(
              `Participant: ${participant.participant_name}\nCurrent Primary Email: ${participant.current_email}\n\nAll Associated Emails:\n${emailList}\n\nEnter the email you want to set as primary:`
            );

            if (!newPrimaryEmail || !newPrimaryEmail.trim()) {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: "No email selected.",
              });
              continue;
            }

            // Verify the entered email exists in the associated emails
            const emailExists = emails.some(e => e.email === newPrimaryEmail);
            if (!emailExists) {
              resultSummary.push({
                ...participant,
                status: "failed",
                message: `Email ${newPrimaryEmail} not found in associated emails.`,
              });
              continue;
            }

            // Skip if already primary
            if (newPrimaryEmail === participant.current_email) {
              resultSummary.push({
                ...participant,
                status: "skipped",
                message: "Email is already the primary email.",
              });
              continue;
            }

            // Step 3: Confirm change
            const confirmed = confirm(`Are you sure you want to set ${newPrimaryEmail} as the primary email for ${participant.participant_name}?\n\nCurrent primary: ${participant.current_email}`);
            if (!confirmed) {
              resultSummary.push({
                ...participant,
                status: "cancelled",
                message: "Change cancelled by user.",
              });
              continue;
            }

            // Step 4: Perform the change
            const response = await iccApi.post('/set-primary-email', {
              individual_id: participant.ind_id,
              email: newPrimaryEmail,
              modified_by: this.userData.ind_id
            });
            console.log('set-primary-email response', response);

            resultSummary.push({
              ...participant,
              status: "success",
              message: `Successfully set ${newPrimaryEmail} as primary email.`,
              previous_email: participant.current_email,
              new_email: newPrimaryEmail
            });

          } catch (error) {
            resultSummary.push({
              ...participant,
              status: "failed",
              message: error.response?.data?.message || error.message
            });
          }
        }

        // Step 5: Show results
        const successful = resultSummary.filter(r => r.status === "success");
        const failed = resultSummary.filter(r => r.status === "failed");
        const cancelled = resultSummary.filter(r => r.status === "cancelled");
        const skipped = resultSummary.filter(r => r.status === "skipped");

        const message = `Set Primary Email Results:\n\nFailed (${failed.length}):\n${failed.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }\n\nCancelled (${cancelled.length}):\n${cancelled.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }\n\nSkipped (${skipped.length}):\n${skipped.map(u => `- ${u.participant_name}: ${u.message}`).join('\n')
          }\n\nSuccess (${successful.length}):\n${successful.map(u => `- ${u.participant_name}: Changed from ${u.previous_email} to ${u.new_email}`).join('\n')
          }`;

        alert(message);

        // Step 6: Log history for successful changes
        try {
          await iccApi.post(`/create-user-history-log`, {
            ind_id: this.userData.ind_id,
            email: this.userData.email,
            screen: 'ICC',
            action: `Set Primary Email Batch Action`,
            action_description: `Changed primary emails for: ${successful.map(u =>
              `${u.participant_name} (${u.previous_email} → ${u.new_email})`
            ).join(', ')}`,
          });
        } catch (err) {
          console.error('History log error:', err);
        }

        this.allSelected = false;
        this.selected = [];
        this.setLoading = false;
      }
      // Apply HR Permission to iteration
      else if (this.actions == 81) {
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        try {
          const response = await iccApi.post('/survey-assignment-tags/apply-by-gospi', {
            org_id: this.selectedFilters.organization?.id || this.survey.org_id,
            suborg_id: this.selectedFilters.subOrganization?.id || this.survey.suborg.suborg_id,
            iteration_id: this.selectedFilters.iteration?.id || this.survey.iteration.iteration_id,
            tag_type: 'hr_report'
          });

          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1 || '#F47820',
            this.brandData.flash_text_color || '#ffffff',
            `HR Permission applied to ${response.data.totalTagged} participant(s).`
          );

          try {
            await iccApi.post('/create-user-history-log', {
              ind_id: this.userData.ind_id,
              email: this.userData.email,
              screen: 'ICC',
              action: 'Apply HR Permission batch action',
              action_description: `Applied hr_report tag to iteration_id ${this.selectedFilters.iteration?.id} - ${response.data.totalTagged} tagged`,
            });
          } catch (err) {
            console.error('History log error:', err);
          }
        } catch (error) {
          console.error('Error applying HR permission:', error);
          flashMessage(
            this.$flashMessage,
            '#dc3545',
            '#ffffff',
            'Failed to apply HR permission. Please try again.'
          );
        }

        this.allSelected = false;
        this.selected = [];
        this.setLoading = false;
      }
      // Remove HR Permission from iteration
      else if (this.actions == 82) {
        this.actions = null;
        this.toggle = false;
        this.setLoading = true;

        try {
          const response = await iccApi.delete('/survey-assignment-tags/remove-by-gospi', {
            data: {
              org_id: this.selectedFilters.organization?.id || this.survey.org_id,
              suborg_id: this.selectedFilters.subOrganization?.id || this.survey.suborg.suborg_id,
              iteration_id: this.selectedFilters.iteration?.id || this.survey.iteration.iteration_id,
              tag_type: 'hr_report'
            }
          });

          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1 || '#F47820',
            this.brandData.flash_text_color || '#ffffff',
            `HR Permission removed from ${response.data.totalRemoved} participant(s).`
          );

          try {
            await iccApi.post('/create-user-history-log', {
              ind_id: this.userData.ind_id,
              email: this.userData.email,
              screen: 'ICC',
              action: 'Remove HR Permission batch action',
              action_description: `Removed hr_report tag from iteration_id ${this.selectedFilters.iteration?.id} - ${response.data.totalRemoved} removed`,
            });
          } catch (err) {
            console.error('History log error:', err);
          }
        } catch (error) {
          console.error('Error removing HR permission:', error);
          flashMessage(
            this.$flashMessage,
            '#dc3545',
            '#ffffff',
            'Failed to remove HR permission. Please try again.'
          );
        }

        this.allSelected = false;
        this.selected = [];
        this.setLoading = false;
      }
    },
    // showDeleteConfirmModal function
    showDeleteConfirmModal(defaultName) {
      return new Promise((resolve) => {
        // Create modal elements
        const modal = document.createElement("div");
        modal.style.position = "fixed";
        modal.style.top = "50%";
        modal.style.left = "50%";
        modal.style.transform = "translate(-50%, -50%)";
        modal.style.background = "#fff";
        modal.style.padding = "40px";
        modal.style.boxShadow = "0px 0px 10px rgba(0,0,0,0.3)";
        modal.style.borderRadius = "8px";
        modal.style.zIndex = "1000";
        modal.style.textAlign = "center";
        modal.innerHTML = `
          <h4>Please enter the filename to delete. Filenames are case-sensitive</h4>
          <input type="text" id="renameInput" value="${defaultName}" maxlength="15" 
                style="width: 90%; padding: 5px; font-size: 16px; border: 1px solid #ccc;">
          <p id="charCount" style="font-size: 12px; color: gray;">${defaultName.length}/15</p>
          <button id="renameConfirm" style="margin-right: 10px; padding: 5px 10px;">OK</button>
          <button id="renameCancel" style="padding: 5px 10px;">Cancel</button>
        `;

        document.body.appendChild(modal);

        const input = modal.querySelector("#renameInput");
        const charCount = modal.querySelector("#charCount");

        // Update character count as user types
        input.addEventListener("input", () => {
          input.value = input.value.substring(0, 15); // Enforce character limit
          charCount.textContent = `${input.value.length}/15`;
        });

        // Handle confirmation
        modal.querySelector("#renameConfirm").addEventListener("click", () => {
          const finalName = input.value.trim();
          document.body.removeChild(modal);
          resolve(finalName || null);
        });

        // Handle cancellation
        modal.querySelector("#renameCancel").addEventListener("click", () => {
          document.body.removeChild(modal);
          resolve(null);
        });
      });
    },
    // showRenameModal function
    showRenameModal(defaultName) {
      return new Promise((resolve) => {
        // Create modal elements
        const modal = document.createElement("div");
        modal.style.position = "fixed";
        modal.style.top = "50%";
        modal.style.left = "50%";
        modal.style.transform = "translate(-50%, -50%)";
        modal.style.background = "#fff";
        modal.style.padding = "40px";
        modal.style.boxShadow = "0px 0px 10px rgba(0,0,0,0.3)";
        modal.style.borderRadius = "8px";
        modal.style.zIndex = "1000";
        modal.style.textAlign = "center";
        modal.innerHTML = `
          <h4>Please enter a friendly name for your uploaded file. Filenames are case-sensitive</h4>
          <input type="text" id="renameInput" value="${defaultName}" maxlength="15" 
                style="width: 90%; padding: 5px; font-size: 16px; border: 1px solid #ccc;">
          <p id="charCount" style="font-size: 12px; color: gray;">${defaultName.length}/15</p>
          <button id="renameConfirm" style="margin-right: 10px; padding: 5px 10px;">OK</button>
          <button id="renameCancel" style="padding: 5px 10px;">Cancel</button>
        `;

        document.body.appendChild(modal);

        const input = modal.querySelector("#renameInput");
        const charCount = modal.querySelector("#charCount");

        // Update character count as user types
        input.addEventListener("input", () => {
          input.value = input.value.substring(0, 15); // Enforce character limit
          charCount.textContent = `${input.value.length}/15`;
        });

        // Handle confirmation
        modal.querySelector("#renameConfirm").addEventListener("click", () => {
          const finalName = input.value.trim();
          document.body.removeChild(modal);
          resolve(finalName || null);
        });

        // Handle cancellation
        modal.querySelector("#renameCancel").addEventListener("click", () => {
          document.body.removeChild(modal);
          resolve(null);
        });
      });
    },
    async prepopulateSurvey(surveyAssignmentId, surveyTemplateId, orgId, suborgId, indId) {
      try {
        const endpointRes = await iccApi.get(`survey-templates/prepopulate/${surveyTemplateId}`);
        const prepopulateEndpoint = endpointRes.data.pre_populate_survey_endpoint;

        const typeRes = await iccApi.get(`/survey-templates/survey-type/${surveyTemplateId}`);
        const surveyVersion = typeRes.data.survey_version;
        const prepopulateData = typeRes.data.prepopulate_data;

        if (surveyVersion === 1) {
          const res = await iccApi.post(`survey-results/populate/${prepopulateEndpoint}`, {
            survey_assignment_id: surveyAssignmentId,
            org_id: orgId,
            suborg_id: suborgId,
          });
          if (res.status === 200) {
            await iccApi.put(`survey-assignment/survey-opened/${surveyAssignmentId}`, {
              survey_opened: 1,
              modified_by: indId,
            });
          }
        }

        if (surveyVersion === 2) {
          const res = await iccApi.post(
            `/survey-results/populate/${surveyAssignmentId}/${orgId}/${suborgId}`,
            prepopulateData
          );
          if (res.status === 200) {
            await iccApi.put(`survey-assignment/survey-opened/${surveyAssignmentId}`, {
              survey_opened: 1,
              modified_by: indId,
            });
          }
        }
      } catch (e) {
        console.log('Prepopulate error:', e);
      }
},

validateDates: function (a, b) {
      if (a && b) {
        const first = new Date(a);
        const second = new Date(b);
        if (second < first) {
          return false;
        }
      }
      return true;
    },

    readDate(data) {
      var parsedDate = Date.parse(data);

      if (isNaN(data) && !isNaN(parsedDate)) {
        const humanReadableDateTime = new Date(data).toLocaleString();
        return humanReadableDateTime;
      } else {
        return data;
      }
    },

    async searchResult(sorted) {
      this.syncSurveyWithSelectedFilters();
      
      // Validate filter hierarchy before proceeding
      const validationErrors = this.validateFilterHierarchy();
      if (validationErrors.length > 0) {
        console.warn('Filter hierarchy validation warnings:', validationErrors);
        // Optionally show warning to user
        // this.showWarningMessage(`Filter hierarchy issues: ${validationErrors.join(', ')}`);
      }

      // Store all filter values in cookies for persistence
      this.storeAllFilterValues();

      // Set loading states
      this.setLoading = true;
      this.isLoading = true;
      this.firstFetchUserData = [];
      this.secondFetchUserData = [];
      this.selected = [];
      this.allSelected = false;

      try {
        // Get iteration ID safely
        const iterationId = this.selectedFilters?.iteration?.id || this.survey?.iteration?.iteration_id;
        
        // Fetch statistics if iteration exists and template conditions are met
        if (iterationId && (this.template_id != null && this.template_id > 0)) {
          const statsRes = await iccApi.get(
            `/survey-assignments/statistics-by-iterationAndTemplate/${iterationId}/${this.template_id}/${this.nomineeSurveyTemplateId}`
          );
          this.statisticsData = statsRes.data;
        } else if (iterationId && (this.template_id == null || this.template_id == 0)) {
          const statsRes = await iccApi.get(
            `/survey-assignments/statistics-by-iteration/${iterationId}`
          );
          this.statisticsData = statsRes.data;
        }

        // Fetch iteration log if iteration exists
        if (iterationId) {
          const logRes = await iccApi.get(`/iteration-log/${iterationId}`);
          this.iterationLogTableRows = logRes.data;
        }

        // Build filter parameters for the unified API call
        const filterPayload = this.buildFilterPayload();
        
        // Check if filterPayload is null (validation failed)
        if (filterPayload === null) {
          return; // Exit early if validation failed
        }

        // Single unified API call
        const response = await iccApi.post('/icc-filter', filterPayload);
        
        // Assign response to appropriate data property
        if (this.showIndividualRecords) {
          this.firstFetchUserData = response.data;
        } else {
          this.secondFetchUserData = response.data;
        }

        // Show success message
        this.showSuccessMessage(response.data.length, sorted);

      } catch (error) {
        // Handle errors
        console.error('Search error:', error);
        this.showErrorMessage(error.message || 'An error occurred while searching');
      } finally {
        // Reset states
        this.toggle = false;
        this.actions = null;
        this.selectedSurveyTemplate = null;
        this.setLoading = false;
        this.isLoading = false;
        this.filters = {};
      }
    },

    // Helper method to build filter parameters
    buildFilterPayload() {
      this.showIndividualRecords = this.batchAction;

      // Validate search query if it's not empty
      const searchQuery = this.respondentSearch || '';
      if (searchQuery && searchQuery.trim().length > 0 && searchQuery.trim().length < 3) {
        alert('Please type at least 3 characters for respondent search');
        return null; // Return null to indicate validation failure
      }

      const payload = {
        org_id: this.userData?.org_id || this.selectedFilters?.organization?.id || 0,
        suborg_id: this.selectedFilters?.subOrganization?.id || 0,
        program_id: this.selectedFilters?.program?.id || 0,
        iteration_id: this.selectedFilters?.iteration?.id || 0,
        stream_id: this.selectedFilters?.stream?.id || 0,
        group_id: this.selectedFilters?.group?.id || 0,
        is_nomination: this.selected_is_nomination || 0,
        template_id: this.template_id || 0,
        show_individual_records: this.showIndividualRecords,
        search_query: searchQuery
      };

      return payload;
    },

    getSelectionCount() {
      let count = 0;
      if (this.selectedFilters?.organization) count++;
      if (this.selectedFilters?.subOrganization) count++;
      if (this.selectedFilters?.program) count++;
      if (this.selectedFilters?.iteration) count++;
      if (this.selectedFilters?.stream) count++;
      if (this.selectedFilters?.group) count++;
      return count;
    },

    // Helper method to validate filter hierarchy
    validateFilterHierarchy() {
      const errors = [];
      
      // Check if child selections exist without parent selections
      if (this.selectedFilters?.subOrganization && !this.selectedFilters?.organization) {
        errors.push('Sub-organization selected without organization');
      }
      
      if (this.selectedFilters?.program && !this.selectedFilters?.subOrganization) {
        errors.push('Program selected without sub-organization');
      }
      
      if (this.selectedFilters?.iteration && !this.selectedFilters?.program) {
        errors.push('Iteration selected without program');
      }
      
      if (this.selectedFilters?.stream && !this.selectedFilters?.iteration) {
        errors.push('Stream selected without iteration');
      }
      
      if (this.selectedFilters?.group && !this.selectedFilters?.stream) {
        errors.push('Group selected without stream');
      }
      
      return errors;
    },

    // Helper method to show success messages
    showSuccessMessage(dataLength, sorted) {
      // Clear any existing flash messages first
      if (this.$flashMessage && this.$flashMessage.destroy) {
        this.$flashMessage.destroy();
      }
      
      const message = sorted === undefined 
        ? `${dataLength} result(s) found.`
        : sorted.toString().includes("successfully")
        ? sorted
        : sorted.toString().includes("iteration")
        ? "Iteration run successful"
        : `${dataLength} result(s) found.`;

      // Small delay before showing new message to ensure old one is cleared
      setTimeout(() => {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || "#F47820",
          this.brandData.flash_text_color || "#ffffff",
          message,
          {
            timeout: 0,
            clickable: true,
            zIndex: 998 // High z-index value
          }
        );
      }, 100);
    },

    // Helper method to show error messages
    showErrorMessage(errorMessage) {
      flashMessage(
        this.$flashMessage,
        this.brandData.accent_color1 || "#F47820",
        this.brandData.flash_text_color || "#ffffff",
        errorMessage
      );
    },

    // Helper method to find items in the organizations structure
    findInOrganizations(type, id) {
      if (!this.organizations || !this.organizations[0]) return null;
      
      switch (type) {
        case 'suborg':
          return this.organizations[0].subOrgs?.find(s => s.id === id) || null;
        default:
          return null;
      }
    },

    // Enhanced cookie storage for all filter values
    storeAllFilterValues() {
      if (this.selectedFilters.subOrganization?.id) {
        this.storeInCookie('suborg_id', this.selectedFilters.subOrganization.id);
      }
      if (this.selectedFilters.program?.id) {
        this.storeInCookie('program_id', this.selectedFilters.program.id);
      }
      if (this.selectedFilters.iteration?.id) {
        this.storeInCookie('iteration_id', this.selectedFilters.iteration.id);
      }
      if (this.selectedFilters.stream?.id) {
        this.storeInCookie('stream_id', this.selectedFilters.stream.id);
      }
      if (this.selectedFilters.group?.id) {
        this.storeInCookie('group_id', this.selectedFilters.group.id);
      }
      
      // Store additional filter values
      this.storeInCookie('filter_type', this.filterType);
      this.storeInCookie('batch_action', this.batchAction);
      this.storeInCookie('survey_template', this.selectedSurveyTemplate || '');
    },

    clearData() {
      this.firstFetchUserData = [];
      this.secondFetchUserData = [];
      this.selected = [];
      this.allSelected = false;
      this.toggle = false;
      this.actions = null;
      this.surveyTemplate = null;
      this.sortBy = "";
      this.tags = null;
      this.survey.action_program_id = null;
      this.survey.action_iteration_id = null;
      this.survey.action_stream_id = null;
      this.survey.group_id = null;
      this.survey.survey_template_id = null;
      this.suborg_inp = null;
      this.program_inp = null;
      this.iteration_inp = null;
      this.stream_inp = null;
      this.group_inp = null;
      this.coach_email_inp = null;
      this.secondary_coach_email_inp = null;
      this.dates.deadline_final = "";
      this.dates.launch = "";
      this.dates.reminder = "";
      this.dates.coach_report_start = "";
      this.dates.coach_report_end = "";
      this.dates.participant_report_start = "";
      this.dates.initial_deadline = "";
      this.statisticsData = [];
      this.participantsRegisteredPercentage = 0;
      this.participantsSubmittedPercentage = 0;
      this.participantsEligiblePercentage = 0;
      this.totalNomineesPercentage = 0;
      this.totalNomineesSubmittedPercentage = 0;
      this.nomineeSurveyTemplateId = 0;
      this.iterationLogTableRows = null;
    },

    clearInput() {
      this.tags = null;
      this.survey.action_program_id = null;
      this.survey.action_iteration_id = null;
      this.survey.action_stream_id = null;
      this.survey.action_tag = null;
      this.survey.group_id = null;
      this.survey.survey_template_id = null;
      this.suborg_inp = null;
      this.program_inp = null;
      this.iteration_inp = null;
      this.stream_inp = null;
      this.group_inp = null;
      this.coach_email_inp = null;
      this.secondary_coach_email_inp = null;
      this.dates.deadline_final = "";
      this.dates.launch = "";
      this.dates.reminder = "";
      this.dates.coach_report_start = "";
      this.dates.coach_report_end = "";
      this.dates.participant_report_start = "";
      this.dates.initial_deadline = "";
    },

    userSelectAll() {
      if (this.allSelected) {
        const selected = this.firstFetchUserData.map((u) => u);
        this.selected = selected;
      } else {
        this.selected = [];
      }
    },

    surveySelectAll() {
      if (this.allSelected) {
        const selected = this.secondFetchUserData.map(
          (u) => u
        );
        this.selected = selected;
      } else {
        this.selected = [];
      }
    },

    async executeMoveAcrossOrg() {
      if (!this.moveModalTarget.org_id || !this.moveModalTarget.suborg_id || !this.moveModalTarget.program_id || !this.moveModalTarget.iteration_id) {
        alert("Please select at least Organisation, Sub-Org, Program, and Iteration.");
        return;
      }

      this.moveModalLoading = true;

      const surveyAssignmentIds = this.selected.map(u => u.survey_assignment_id);

      const payload = {
        survey_assignment_ids: surveyAssignmentIds,
        source_org_id: this.selectedFilters.organization?.id || this.userData.org_id,
        source_suborg_id: this.selectedFilters.subOrganization?.id || 0,
        source_program_id: this.selectedFilters.program?.id || 0,
        source_iteration_id: this.selectedFilters.iteration?.id || 0,
        target_org_id: this.moveModalTarget.org_id,
        target_suborg_id: this.moveModalTarget.suborg_id,
        target_program_id: this.moveModalTarget.program_id,
        target_iteration_id: this.moveModalTarget.iteration_id,
        target_stream_id: this.moveModalTarget.stream_id || 0,
        target_group_id: this.moveModalTarget.group_id || 0,
        modified_by: this.userData.ind_id,
      };

      try {
        const response = await iccApi.post('/move-survey-across-org', payload);

        this.moveModalResult = response.data;
        this.moveModalLoading = false;

        // Log history
        try {
          await iccApi.post('/create-user-history-log', {
            ind_id: this.userData.ind_id,
            email: this.userData.email,
            screen: 'ICC',
            action: 'Move User Survey and Report Across Organisations',
            action_description: `Moved ${response.data.participantsProcessed} participants from org ${payload.source_org_id}/suborg ${payload.source_suborg_id}/program ${payload.source_program_id}/iteration ${payload.source_iteration_id} to org ${payload.target_org_id}/suborg ${payload.target_suborg_id}/program ${payload.target_program_id}/iteration ${payload.target_iteration_id}`,
          });
        } catch (err) {
          console.error('History log error:', err);
        }

        // Refresh table after short delay so user sees result
        setTimeout(() => {
          this.allSelected = false;
          this.selected = [];
          this.actions = null;
          this.searchResult();
        }, 500);

      } catch (error) {
        this.moveModalLoading = false;
        this.moveModalResult = {
          status: 'error',
          participantsProcessed: 0,
          nomineesResolved: 0,
          tableUpdates: {},
        };
        console.error('Move error:', error);
        alert(`Error: ${error.response?.data?.message || error.message || 'Unknown error occurred'}`);
      }
    },

    async releaseReportsNow() {
      if (!this.selectedFilters.iteration) {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || '#F47820',
          this.brandData.flash_text_color || '#ffffff',
          'Please select an iteration first.'
        );
        return;
      }

      const iterationName = this.selectedFilters.iteration.name;
      const streamName = this.selectedFilters.stream?.name;
      const groupName = this.selectedFilters.group?.name;

      let scopeText = `Iteration: ${iterationName}`;
      if (streamName) scopeText += ` → Stream: ${streamName}`;
      if (groupName) scopeText += ` → Group: ${groupName}`;

      const result = await Swal.fire({
        title: 'Release Reports Now',
        html: `This will immediately release reports for all eligible participants with a <strong>participant_report_start_date set to today</strong>.<br><br><strong>${scopeText}</strong><br><br>Are you sure?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: this.brandData.accent_color1 || '#F47820',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, Release Now',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) return;

      try {
        this.setLoading = true;

        const payload = {
          iteration_id: this.selectedFilters.iteration.id,
          group_id: this.selectedFilters.group?.id || null,
          stream_id: this.selectedFilters.stream?.id || null,
        };

        const response = await iccApi.post('/release-reports-by-iteration', payload);

        if (response.data.totalFound === 0) {
          Swal.fire({
            title: 'No Eligible Records',
            text: 'No participants found with a report release date set to today.',
            icon: 'info',
            confirmButtonColor: this.brandData.accent_color1 || '#F47820',
          });
        } else {
          Swal.fire({
            title: 'Reports Released!',
            html: `<strong>${response.data.totalReleased}</strong> report(s) released successfully out of <strong>${response.data.totalFound}</strong> eligible.`,
            icon: 'success',
            confirmButtonColor: this.brandData.accent_color1 || '#F47820',
          });

          // Log user history
          try {
            await iccApi.post('/create-user-history-log', {
              ind_id: this.userData.ind_id,
              email: this.userData.email,
              screen: 'ICC',
              action: 'Release Reports Now',
              action_description: `Released ${response.data.totalReleased} reports for ${scopeText}`,
            });
          } catch (err) {
            console.error('History log error:', err);
          }

          // Refresh the table if data is loaded
          if (this.secondFetchUserData.length > 0 || this.firstFetchUserData.length > 0) {
            this.searchResult();
          }
        }

      } catch (error) {
        console.error('Error releasing reports:', error);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'An error occurred while releasing reports.',
          icon: 'error',
          confirmButtonColor: this.brandData.accent_color1 || '#F47820',
        });
      } finally {
        this.setLoading = false;
      }
    },

    async generateReleaseUrl() {
      if (!this.selectedFilters.iteration) {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || '#F47820',
          this.brandData.flash_text_color || '#ffffff',
          'Please select an iteration first.'
        );
        return;
      }

      this.isGeneratingToken = true;
      this.generatedReleaseUrl = '';
      this.urlCopied = false;

      try {
        const response = await iccApi.post('/generate-release-token', {
          iteration_id: this.selectedFilters.iteration.id,
          stream_id: this.selectedFilters.stream?.id || null,
          group_id: this.selectedFilters.group?.id || null,
          created_by: this.userData.ind_id,
        });

        this.generatedReleaseUrl = response.data.releaseUrl;
        this.generatedReleaseUrlExpiry = new Date(response.data.expiresAt).toLocaleDateString('en-GB', {
          day: 'numeric', month: 'short', year: 'numeric',
        });

      } catch (error) {
        console.error('Error generating release URL:', error);
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || '#F47820',
          this.brandData.flash_text_color || '#ffffff',
          'Failed to generate release URL. Please try again.'
        );
      } finally {
        this.isGeneratingToken = false;
      }
    },

    async copyReleaseUrl() {
      try {
        await navigator.clipboard.writeText(this.generatedReleaseUrl);
        this.urlCopied = true;
        setTimeout(() => { this.urlCopied = false; }, 3000);
      } catch (err) {
        console.error('Clipboard copy failed:', err);
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || '#F47820',
          this.brandData.flash_text_color || '#ffffff',
          'Could not copy. Please copy the URL manually.'
        );
      }
    },
  },
  computed: {

  hasSelections() {
      return Object.values(this.selectedFilters).some(v => v !== null);
    },
    selectionCount() {
      return Object.values(this.selectedFilters).filter(v => v !== null).length;
    },
    // White or dark text, chosen from the brand color so a light brand color stays readable
    onPrimaryColor() {
      const hex = (this.brandData && this.brandData.main_color1) || '#0e5071';
      let h = String(hex).replace('#', '').trim();
      if (h.length === 3) h = h.split('').map((c) => c + c).join('');
      if (!/^[0-9a-fA-F]{6}$/.test(h)) return '#ffffff';
      const [r, g, b] = [0, 2, 4]
        .map((i) => parseInt(h.substr(i, 2), 16) / 255)
        .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return lum > 0.179 ? '#141d20' : '#ffffff';
    },
    resultCount() {
      const rows = this.showIndividualRecords ? this.filteredUserData : this.filteredSurveyAssignmentData;
      return rows ? rows.length : 0;
    },
    // Quick date-change entries for the "Batch Action Edit" menu; only shown if the user is allowed that action
    quickBatchItems() {
      const wanted = [
        { id: 8, label: 'Change launch date' },
        { id: 13, label: 'Change Initial deadline date' },
        { id: 7, label: 'Change final deadline date' },
      ];
      if (this.showIndividualRecords) return [];
      const allowed = (this.filteredBatchActions || []).map((a) => a.batch_action_id);
      return wanted.filter((w) => allowed.includes(w.id));
    },
    canSearch() {
      return this.hasSelections && this.filterType;
    },

    dropdownIconColor() {
            return this.filtersApplied ? 'red' : '';
    },

    filteredData2() {
      return this.filteredSurveyAssignmentData.map((item) => {
        const filteredItem = {};
        this.displayedHeaders.forEach((header) => {
          filteredItem[header.key] = item[header.key];
        });
        return filteredItem;
      });
    },

    showHeaders () {
      return this.columnHideShowHeader.filter(s => this.selectedHeaders.includes(s));
    },

    filteredRemoveTags: function () {
      return this.mapTags.filter(function (el) {
        return el !== undefined;
      }, this);
    },

    mapTags: function () {
      return this.userTags
        .sort((a, b) => a.tag_type.localeCompare(b.tag_type))
        .map(function (el) {
          if (this.userData.org_id === el.org_id) {
            return { value: el.tag_id, label: el.tag_type };
          }
        }, this);
    },

    filteredTags: function () {
      return this.userTags
        .sort((a, b) => a.tag_type.localeCompare(b.tag_type))
        .filter(function (el) {
          if (this.userData.org_id != "") {
            return el.org_id == this.userData.org_id;
          }
        }, this);
    },

    filteredSubOrg: function () {
      // Convert roles string to array of integers
      const rolesArray = this.userData.roles.split(',').map(number => number.trim()).map(Number);
      
      // Check if role 16 is present
      const hasRole16 = rolesArray.includes(16);
      
      // Convert suborgs string to array of integers (only used if role 16 is not present)
      const suborgsArray = this.userData.suborgs.split(',').map(number => number.trim()).map(Number);

      // First filter by org_id
      let orgSubOrgs = this.userSubOrg.filter(el => el.org_id === this.userData.org_id);

      // Ensure 'Select' item is handled correctly - only if it belongs to the org
      const selectItem = orgSubOrgs.find(el => el.suborg_id === 0);
      let restOfSubOrgs = orgSubOrgs.filter(el => el.suborg_id !== 0);

      // Sort the suborgs excluding the 'Select' item
      restOfSubOrgs.sort((a, b) => a.suborg_name.localeCompare(b.suborg_name));

      // If role 16 is not present, filter based on the suborgsArray
      if (!hasRole16 && this.userData.suborgs !== "") {
          restOfSubOrgs = restOfSubOrgs.filter(el => suborgsArray.includes(el.suborg_id));
      }
      // If role 16 is present, show all suborgs (but still only for the current org)

      // Re-introduce the 'Select' item at the start if it exists
      if (selectItem) {
          restOfSubOrgs.unshift(selectItem);
      }

      return restOfSubOrgs;
    },

    mapSubOrg: function () {
      // Filter by org_id first, then map to the required format
      return this.userSubOrg
        .filter(el => el.org_id === this.userData.org_id)
        .map(el => ({
            value: el.suborg_id,
            label: el.suborg_name
        }));
    },

    filteredSubOrgActions: function () {
      return this.mapSubOrg.filter(
        function (el) {
          return el !== undefined;
        },
        this,
        console.log(this.statisticsData)
      );
    },

    mapUserProgram: function () {
      return this.userPrograms.map(function (el) {
        if (
          this.userData.org_id === el.org_id &&
          this.survey.suborg.suborg_id == el.suborg_id
        ) {
          return { value: el.program_id, label: el.program_name };
        }
      }, this);
    },

    filteredProgramActions: function () {
      return this.mapUserProgram.filter(function (el) {
        return el !== undefined;
      }, this);
    },

    filteredProgram: function () {
      // Create a 'Select Program' item with program_id of 0 and suborg_id of 0
      const selectProgramItem = { program_id: 0, program_name: 'Select Program', org_id: this.userData.org_id, suborg_id: this.survey.suborg.suborg_id };

      // Clone the userPrograms array to avoid modifying the original
      const clonedUserPrograms = [...this.userPrograms];

      // Sort the array
      const sortedPrograms = clonedUserPrograms.sort((a, b) => a.program_name.localeCompare(b.program_name));

      // Push 'Select Program' item into the sorted array as the topmost option
      const finalPrograms = [selectProgramItem, ...sortedPrograms];

      // Filter based on userData.org_id, survey.suborg.suborg_id, and el.org_id
      return this.userData.org_id !== ""
        ? finalPrograms.filter(el => el.org_id == this.userData.org_id && el.suborg_id == this.survey.suborg.suborg_id)
        : finalPrograms;
    },

    mapIteration: function () {
      return this.userIterations.map(function (el) {
        if (
          this.userData.org_id === el.org_id &&
          this.survey.suborg.suborg_id == el.suborg_id &&
          this.survey.program.program_id == el.program_id
        ) {
          return { value: el.iteration_id, label: el.iteration_name };
        }
      }, this);
    },

    mapIterationMultiSelect: function () {
      return this.userIterations.map(function (el) {
        if (
          this.userData.org_id === el.org_id &&
          this.survey.suborg.suborg_id == el.suborg_id &&
          this.survey.action_program_id == el.program_id
        ) {
          return { value: el.iteration_id, label: el.iteration_name };
        }
      }, this);
    },

    filteredIterationActions: function () {
      return this.mapIteration.filter(function (el) {
        return el !== undefined;
      }, this);
    },

    filteredIterationMultiSelectActions: function () {
      return this.mapIterationMultiSelect.filter(function (el) {
        return el !== undefined;
      }, this);
    },

    filteredIteration: function () {
      // Create a 'Select Iteration' item with iteration_id of 0
      const selectIterationItem = {
        iteration_id: 0,
        iteration_name: 'Select Iteration',
        org_id: this.userData.org_id,
        suborg_id: this.survey.suborg.suborg_id,
        program_id: this.survey.program.program_id,
        abbreviation: '' // Placeholder, since it's a special item
      };

      // Clone the userIterations array to avoid modifying the original
      const clonedUserIterations = [...this.userIterations];

      // Add the abbreviation to each user iteration
      clonedUserIterations.forEach(iteration => {
        const timezoneName = iteration.time_zone; // Assuming you have timezone_name in each iteration
        iteration.abbreviation = getTimezoneAbbreviation(timezoneName); // Use getTimezoneAbbreviation function
      });

      // Sort the array
      const sortedIterations = clonedUserIterations.sort((a, b) => a.iteration_name.localeCompare(b.iteration_name));

      // Push 'Select Iteration' item into the sorted array as the topmost option
      const finalIterations = [selectIterationItem, ...sortedIterations];

      // Filter based on userData.org_id, survey.suborg.suborg_id, and survey.program.program_id
      return this.userData.org_id !== ""
        ? finalIterations.filter(el => el.org_id == this.userData.org_id && el.suborg_id == this.survey.suborg.suborg_id && el.program_id == this.survey.program.program_id)
        : finalIterations;
    },

    mapStream: function () {
      return this.userStreams.map(function (el) {
        if (
          this.userData.org_id === el.org_id &&
          this.survey.suborg.suborg_id == el.suborg_id &&
          this.survey.program.program_id == el.program_id &&
          this.survey.iteration.iteration_id == el.iteration_id
        ) {
          return { value: el.stream_id, label: el.stream_name };
        }
      }, this);
    },

    filteredStreamActions: function () {
      return this.mapStream.filter(function (el) {
        return el !== undefined;
      }, this);
    },

    filteredStream: function () {
      // Create a 'Select Stream' item with stream_id of 0
      const selectStreamItem = { stream_id: 0, stream_name: 'Select Stream', org_id: this.userData.org_id, suborg_id: this.survey.suborg.suborg_id, program_id: this.survey.program.program_id, iteration_id: this.survey.iteration.iteration_id };

      // Clone the userStreams array to avoid modifying the original
      const clonedUserStreams = [...this.userStreams];

      // Sort the array
      const sortedStreams = clonedUserStreams.sort((a, b) => a.stream_name.localeCompare(b.stream_name));

      // Push 'Select Stream' item into the sorted array as the topmost option
      const finalStreams = [selectStreamItem, ...sortedStreams];

      // Filter based on userData.org_id, survey.suborg.suborg_id, survey.program.program_id, and survey.iteration.iteration_id
      return this.userData.org_id !== ""
        ? finalStreams.filter(el => el.org_id == this.userData.org_id && el.suborg_id == this.survey.suborg.suborg_id && el.program_id == this.survey.program.program_id && el.iteration_id == this.survey.iteration.iteration_id)
        : finalStreams;
    },

    mapUserGroup: function () {
      return this.userGroups.map(function (el) {
        if (
          this.userData.org_id === el.org_id &&
          this.survey.suborg.suborg_id == el.suborg_id &&
          this.survey.program.program_id == el.program_id &&
          this.survey.iteration.iteration_id == el.iteration_id &&
          this.survey.stream.stream_id == el.stream_id
        ) {
          return { value: el.group_id, label: el.group_name };
        }
      }, this);
    },

    mapUserGroupMultiSelect: function () {
      return this.userGroups.map(function (el) {
        if (
          this.userData.org_id === el.org_id &&
          this.survey.suborg.suborg_id == el.suborg_id &&
          this.survey.program.program_id == el.program_id &&
          this.survey.iteration.iteration_id == el.iteration_id &&
          this.survey.action_stream_id == el.stream_id
        ) {
          return { value: el.group_id, label: el.group_name };
        }
      }, this);
    },

    filteredGroupActions: function () {
      return this.mapUserGroup.filter(function (el) {
        return el !== undefined;
      }, this);
    },

    filteredGroupMultiSelectActions: function () {
      return this.mapUserGroupMultiSelect.filter(function (el) {
        return el !== undefined;
      }, this);
    },

    mapCoachEmail: function () {
      return this.coachUsers.map(function (el) {
        if (
          el.suborgs != 0 &&
          el.suborgs != null &&
          el.suborgs != undefined &&
          el.suborgs != ""
        ) {
          var newValueSuborg = el.suborgs.split(", ");
          this.newNameSuborg = newValueSuborg.map(
            (r) => this.userSubOrg.find((f) => f.suborg_id == r).suborg_id
          );
        }

        var joinSuborg = this.newNameSuborg.join(", ");

        if (joinSuborg.includes(this.survey.suborg.suborg_id)) {
          return { value: el.ind_id, label: el.email };
        }
      }, this);
    },

    filteredCoachEmailActions: function () {
      return this.mapCoachEmail.filter(function (el) {
        return el !== undefined;
      }, this);
    },

    filteredSurveyTemplate: function () {
      // Create a 'Select Survey Template' item with survey_template_id of 0
      const selectSurveyTemplateItem = { 
        survey_template_id: 0, 
        survey_template_file_name: 'Select Survey Template', 
        survey_template_name: 'Select Survey Template',
        org_id: this.userData.org_id, 
        suborg_id: this.selectedFilters.subOrganization?.id || 0 
      };

      // Clone the userSurveyTemplate array to avoid modifying the original
      const clonedUserSurveyTemplate = [...this.userSurveyTemplate];

      // Sort the array
      const sortedSurveyTemplates = clonedUserSurveyTemplate.sort((a, b) => 
        a.survey_template_name.localeCompare(b.survey_template_name)
      );

      // Push 'Select Survey Template' item into the sorted array as the topmost option
      const finalSurveyTemplates = [selectSurveyTemplateItem, ...sortedSurveyTemplates];

      // Get the current suborg_id from selectedFilters
      const currentSuborgId = this.selectedFilters.subOrganization?.id || 0;

      // Filter based on userData.org_id and current suborg selection
      const filteredTemplates = this.userData.org_id !== ""
        ? finalSurveyTemplates.filter(el => 
            el.org_id == this.userData.org_id && 
            el.suborg_id == currentSuborgId
          )
        : finalSurveyTemplates;

      // Additional filtering based on selected_is_nomination value
      if (this.selected_is_nomination === 0) {
        // Show templates with nominee_survey_template_id not null
        return filteredTemplates.filter(el => 
          el.survey_template_id === 0 || el.nominee_survey_template_id !== null
        );
      } else if (this.selected_is_nomination === 1) {
        // Show templates with nominee_survey_template_id null
        return filteredTemplates.filter(el => 
          el.survey_template_id === 0 || el.nominee_survey_template_id === null
        );
      } else {
        // For value 'All', return all templates
        return filteredTemplates;
      }
    },

    filteredBatchActions: function () {
      return this.batchActions
        .sort((a, b) => a.batch_action_type.localeCompare(b.batch_action_type))
        .filter(function (el) {
          if ((this.template_id != null || this.template_id > 0) && this.survey.iteration.iteration_id != null) {
            return (
              el.batch_action_id != 19 &&
              el.batch_action_id != 21 &&
              el.batch_action_id != 22
            );
          } else {
            return (
              el.batch_action_id != 19 &&
              el.batch_action_id != 21 &&
              el.batch_action_id != 22 &&
              el.batch_action_id != 25
            );
          }
        }, this);
    },

    filteredAssignGroup: function () {
      return this.batchActions2
        .sort((a, b) => a.batch_action_type.localeCompare(b.batch_action_type))
        .filter(function (el) {
          if (
            this.survey.program.program_id != null &&
            this.survey.iteration.iteration_id != null
          ) {
            return el.batch_action_id == 22;
          }
        }, this);
    },

    filteredUserSurveyTemplate: function () {
      return this.userSurveyTemplate
        .sort((a, b) =>
          a.survey_template_name.localeCompare(b.survey_template_name)
        )
        .filter(function (el) {
          if (
            el.nominee_survey_template_id != null &&
            el.org_id == this.userData.org_id &&
            el.suborg_id == this.survey.suborg.suborg_id
          ) {
            return el;
          }
        }, this);
    },

    disableGoButton() {
      if (this.tags != null && this.tags != "") {
        return true;
      } else if (
        this.survey.action_program_id != null &&
        this.survey.action_program_id != ""
      ) {
        return true;
      } else if (
        this.survey.action_iteration_id != null &&
        this.survey.action_iteration_id != ""
      ) {
        return true;
      } else if (
        this.survey.action_stream_id != null &&
        this.survey.action_stream_id != ""
      ) {
        return true;
      } else if (this.survey.group_id != null && this.survey.group_id != "") {
        return true;
      } else if (
        this.survey.survey_template_id != null &&
        this.survey.survey_template_id != ""
      ) {
        return true;
      } else if (this.suborg_inp != null && this.suborg_inp != "") {
        return true;
      } else if (this.program_inp != null && this.program_inp != "") {
        return true;
      } else if (this.iteration_inp != null && this.iteration_inp != "") {
        return true;
      } else if (this.stream_inp != null && this.stream_inp != "") {
        return true;
      } else if (this.group_inp != null && this.group_inp != "") {
        return true;
      } else if (this.coach_email_inp != null && this.coach_email_inp != "") {
        return true;
      }else if (this.secondary_coach_email_inp != null && this.secondary_coach_email_inp != "") {
        return true;
      } else if (
        this.dates.deadline_final != null &&
        this.dates.deadline_final != ""
      ) {
        return true;
      } else if (this.dates.launch != null && this.dates.launch != "") {
        return true;
      } else if (this.dates.reminder != null && this.dates.reminder != "") {
        return true;
      } else if (
        this.dates.coach_report_start != null &&
        this.dates.coach_report_start != ""
      ) {
        return true;
      } else if (
        this.dates.coach_report_end != null &&
        this.dates.coach_report_end != ""
      ) {
        return true;
      } else if (
        this.dates.participant_report_start != null &&
        this.dates.participant_report_start != ""
      ) {
        return true;
      } else if (
        this.dates.initial_deadline != null &&
        this.dates.initial_deadline != ""
      ) {
        return true;
      } else if (
        this.survey.action_tag != null &&
        this.survey.action_tag != ""
      ) {
        return true;
      } else {
        return false;
      }
    },

    sortedProperties() {
      const direction = this.sortDirection;
      const head = this.sortBy;
      return this.firstFetchUserData.sort(this.dynamicSort(head, direction));
    },

    table2SortedProperties() {
      const direction = this.sortDirection;
      const head = this.sortBy;
      return this.secondFetchUserData.sort(this.dynamicSort(head, direction));
    },

    filteredSurveyAssignmentData() {
      
      if (!this.secondFetchUserData) {
        console.error('secondFetchUserData is undefined');
        return [];
      }
      
      if (!this.filters) {
        console.error('filters is undefined');
        return [];
      }
      
      return this.secondFetchUserData.filter(d => {
        return Object.keys(this.filters).every((f) => {
          console.log('Checking filter:', f, 'with value:', this.filters[f]);
          return this.filters[f].length < 1 || this.filters[f].includes(d[f]);
        });
      }).map(d => {
        
        // Check if tag_names is not null before splitting
        if (d.tag_names !== null && typeof d.tag_names === 'string') {
          // Sort the comma-delimited items in tag_names alphabetically
          d.tag_names = d.tag_names.split(',').map(item => item.trim()).sort().join(', ');
        }

        // Add timezone abbreviation to specified fields if not null
        const timezoneAbbreviation = getTimezoneAbbreviation(d.time_zone);
        // commented for the dates sorting bug
        // const addAbbreviation = (field) => {
        //   if (d[field] !== null && !d[field].includes('UTC') && !d[field].includes(timezoneAbbreviation)) {
        //     // If it's the last_login_date field, use "UTC" as the abbreviation
        //     const abbreviation = field === 'last_login_date' ? 'UTC' : timezoneAbbreviation;
        //     d[field] = `${d[field]} ${abbreviation}`;
        //   }
        // };

        const dateFields = [
          'coach_report_end_date',
          'coach_report_start_date',
          'expiry_date',
          'final_deadline_date',
          'initial_deadline_date',
          'it_final_deadline_date',
          'launch_date',
          'participant_report_start_date',
          'survey_reminder_date',
          'last_login_date',
        ];

        dateFields.forEach(field => {
          if (d[field] !== null && d[field] !== undefined) {
            const abbreviation = field === 'last_login_date' ? 'UTC' : timezoneAbbreviation;
            if (!d[field].includes('UTC') && !d[field].includes(timezoneAbbreviation)) {
              d[`_sort_${field}`] = d[field]; // store raw value for sorting
              d[field] = `${d[field]} ${abbreviation}`;
            } else {
              d[`_sort_${field}`] = d[field].replace(/\s\S+$/, ''); // strip tz for sort
            }
          }
        });

        return d;
      });
    },

    surveyHeaders() {
      // Ensure selectedHeaders is an array and has items
      if (Array.isArray(this.selectedHeaders) && this.selectedHeaders.length > 0) {
        // Filter headers based on the key property
        return this.survey_headers_data.filter(header =>
          this.selectedHeaders.some(selectedHeader => selectedHeader.key === header.key)
        );
      } else {
        // If no selected headers, return the default data
        return [...this.survey_headers_data];
      }
    },

    filteredUserData() {
      return this.firstFetchUserData.filter(d => {
        return Object.keys(this.filters).every((f) => {
          return this.filters[f].length < 1 || this.filters[f].includes(d[f]);
        });
      })
    },

    userHeaders() {
      // ... your existing headers configuration ...

      // Modify headers to include 'key' property for filtering
      return this.user_headers_data.map(header => ({ ...header, key: header.key || header.value }));
    },

  },
  watch: {
    // respondentSearch: {
    //   handler() {
    //     // Debounce the search to avoid too many API calls
    //     clearTimeout(this.searchTimeout);
    //     this.searchTimeout = setTimeout(() => {
    //       if (this.firstFetchUserData.length > 0 || this.secondFetchUserData.length > 0) {
    //         this.searchResult(); // Trigger search when user stops typing
    //       }
    //     }, 500); // 500ms delay
    //   }
    // },
    selectedHeaders: {
      async handler(newSelectedHeaders) {

        // Check if newSelectedHeaders is empty or not
        if (Array.isArray(newSelectedHeaders) && newSelectedHeaders.length > 0) {
          // If there are selected headers, log the selected headers
          await this.logSelectedHeaders();
          
          // Update displayedHeaders based on newSelectedHeaders
          this.displayedHeaders = this.survey_headers_data.filter(header =>
            newSelectedHeaders.some(selectedHeader => selectedHeader.key === header.key)
          );
        } else {
          // If no selected headers, reset displayedHeaders to the default data
          this.displayedHeaders = [...this.survey_headers_data];
        }
      },
      immediate: true,
    },
    // Other watchers
  },

  beforeUnmount() {
    // Clean up polling when component is destroyed
    this.clearJobPolling();
  },


};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style>
tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, .05);
}

.v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th {
  background: none !important;
  color: white;
}


</style>
<style scoped>

/* Base styles */
.filter-panel {
  background: white;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.filter-panel.collapsed {
  /* width: 40px;
  height: 40px; */
  width: 350px;
  min-height: 200px;
}

.filter-panel.expanded {
  border: 1px solid #ddd;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 350px;
  min-height: 200px;
}

.filter-header {
  padding: 12px;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 8px;
  min-height: 16px;
  border-bottom: 1px solid #eee;
}

.filter-panel.collapsed .filter-header {
  /* background-color: white; */
  border-bottom: none;
  border-radius: 8px;
}

.filter-panel.expanded .filter-header {
  justify-content: space-between;
  border-radius: 8px 8px 0 0;
}

.header-text {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.3s ease;
  pointer-events: none;
}

.filter-panel.expanded .header-text {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

.filter-panel.collapsed .header-text {
  opacity: 1;
  /* display: none; */
  transform: translateX(0);
  pointer-events: auto;
}

.toggle-button {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: rgba(0,0,0,0.05);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toggle-button:hover {
  background: rgba(0,0,0,0.1);
}

.search-input {
  font-size: 12px;
  padding: 5px;
  width: 100%;
}

.expand-icon {
  font-size: 12px;
  color: #666;
  transition: transform 0.3s ease;
}

.filter-panel.collapsed .expand-icon {
  transform: rotate(-90deg);
}

.filter-panel.expanded .expand-icon {
  transform: rotate(90deg);
}

.filter-content {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.3s ease 0.2s;
  pointer-events: none;
  max-height: 400px;
  overflow-y: auto;
}

.filter-panel.expanded .filter-content {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
  transition-delay: 0.2s;
}

.filter-panel.collapsed .filter-content {
  transition-delay: 0s;
}

.filter-options {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.3s ease 0.2s;
  pointer-events: none;
  padding: 15px 20px;
  border-top: 1px solid #eee;
  background-color: #fafbfc;
}

.filter-panel.expanded .filter-options {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
  transition-delay: 0.2s;
}

.filter-panel.collapsed .filter-options {
  transition-delay: 0s;
}

.filter-footer {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.3s ease 0.2s;
  pointer-events: none;
  padding: 15px 20px;
  border-top: 1px solid #eee;
  background-color: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.filter-panel.expanded .filter-footer {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
  transition-delay: 0.2s;
}

.filter-panel.collapsed .filter-footer {
  transition-delay: 0s;
}

/* Tree and node styles */
.tree-item {
  border-bottom: 1px solid #f0f0f0;
}

.tree-node {
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s;
}

.tree-node:hover {
  background-color: #f8f9fa;
}

.node-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.node-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-right: 8px;
}

.node-count {
  background-color: #e9ecef;
  color: #6c757d;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
}

.node-actions {
  display: flex;
  align-items: center;
}

.node-actions .expand-icon {
  font-size: 12px;
  color: #999;
  transition: transform 0.2s;
}

.node-actions .expand-icon.expanded {
  transform: rotate(180deg);
}

.tree-children {
  background-color: #fafbfc;
  border-left: 2px solid #e9ecef;
}

.child-node {
  padding: 8px 16px 8px 40px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s;
}

.child-node:hover {
  background-color: #f1f3f4;
}

.child-node.selected {
  background-color: #e8f5e8;
  color: #2e7d32;
  font-weight: 500;
}

.checkbox {
  width: 14px;
  height: 14px;
  margin-right: 8px;
  cursor: pointer;
}

/* Options and form styles */
.options-section {
  margin-bottom: 20px;
}

.options-section:last-child {
  margin-bottom: 0;
}

.options-header {
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.options-label {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
  margin: 0;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 0;
}

.radio-option input[type="radio"] {
  display: none;
}

.radio-custom {
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-radius: 50%;
  margin-right: 8px;
  position: relative;
  transition: all 0.2s;
}

.radio-option input[type="radio"]:checked + .radio-custom {
  border-color: #007bff;
  background-color: #007bff;
}

.radio-option input[type="radio"]:checked + .radio-custom::after {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.radio-label {
  color: #555;
  user-select: none;
}

.select-wrapper {
  position: relative;
  width: 100%;
}

.form-select {
  width: 100%;
  height: 30px;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.form-select:focus {
  outline: none;
  border-width: 2px;
  padding: 0 15px;
}

.form-select:hover {
  border-width: 1.5px;
  padding: 0 15.5px;
}

.form-select:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Filter summary and actions */
.filter-summary {
  margin-bottom: 12px;
}

.selected-count {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
}

.breadcrumb {
  font-size: 11px;
  color: #666;
  line-height: 1.4;
}

.breadcrumb strong {
  color: #333;
}

.action-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.action-buttons--row2 {
  margin-top: 10px;
}

.release-url-preview {
  margin-top: 14px;
  background: #f0f8ff;
  border: 1px solid #b0c4de;
  border-radius: 6px;
  padding: 12px 14px;
}

.release-url-preview__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.release-url-preview__label {
  font-size: 12px;
  font-weight: 600;
  color: #0e5071;
}

.release-url-preview__expiry {
  font-size: 11px;
  color: #888;
}

.release-url-preview__box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px 10px;
}

.release-url-preview__text {
  font-size: 12px;
  color: #333;
  word-break: break-all;
  flex: 1;
}

.release-url-preview__copy-btn {
  flex-shrink: 0;
  background: #0e5071;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.release-url-preview__copy-btn:hover {
  background: #0a3d56;
}

.clear-btn {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.clear-btn:hover {
  color: #c82333;
}

.search-btn {
  background: #007bff;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  width: 20%;
  min-width: 100px;
}

.search-btn:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
}

.search-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.search-btn:active:not(:disabled) {
  transform: translateY(0);
}

/* Button and label styles */
.label-btn {
  color: #0e5071;
  background-color: #fff;
}

/* Label div full width styling */
.label-div {
  border: 1px solid #0e5071;
  background-color: #0e5071;
  color: #fff;
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

/* Form inputs */
.input {
  width: 100%;
  outline: 0;
  border-width: 0 0 1px;
  border-color: grey;
  padding: 8px 5px;
  margin: 10px 0px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
}

.input1 {
  width: 85%;
  outline: 0;
  border: 1px solid grey;
  padding: 8px 5px;
  margin: 10px 0px;
  font-size: 14px;
}

.inputTemplate {
  width: 60%;
  outline: 0;
  border: 1px solid grey;
  padding: 8px 5px;
  margin: 10px 0px;
  font-size: 14px;
}

#selectTag {
  width: 90%;
}

.batch-action-input {
  width: 30%;
  outline: 0;
  border: 1px solid grey;
  padding: 8px 5px;
  margin: 10px 0px;
  font-size: 14px;
}

#selectBatchAction,
.batch-action-input2 {
  width: auto;
  min-width: 150px;
  max-width: 100%;
  outline: 0;
  border: 1px solid grey;
  padding: 8px 5px;
  margin: 10px 0px;
  font-size: 14px;
}

#batchAction {
  height: auto;
  white-space: nowrap;
  text-overflow: ellipsis;
  word-wrap: break-word;
  border: 1px solid grey;
  margin: 30px 0px;
  padding: 8px;
}

/* Box layouts */
.box {
  border: 1px solid #ccc;
  border-top: 4px solid rgb(185, 22, 10);
  text-align: center;
  font: -webkit-control;
  margin: 0 10px;
  min-height: 400px;
  padding: 10px;
  max-width: 250px;
}

.boxReports {
  text-align: left;
  font: -webkit-control;
  min-height: 10px;
  max-width: 100%;
  min-width: 75vw;
}

.boxStatistics {
  border: 1px solid #ccc;
  text-align: left;
  font: -webkit-control;
  margin: 0 10px;
  min-height: 10vh;
  max-width: 100%;
  min-width: 37.5vw;
  padding: 15px;
}

/* Table styles */
#iteration-log-table {
  border-collapse: collapse;
  width: 100%;
}

#iteration-log-table th,
#iteration-log-table td {
  border: 1px solid black;
  text-align: left;
  padding: 8px;
  font-size: 14px;
}

#iteration-log-table th {
  background-color: #90d1f1;
  position: sticky;
  top: 0;
}

#iteration-log-table-container {
  height: 200px;
  overflow-y: scroll;
  margin: 0 0 20px 0;
}

#table {
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
}

#table td,
#table th {
  border: 1px solid #ddd;
  padding: 8px;
  font-size: 14px;
}

#table tr:nth-child(even) {
  background-color: #f2f2f2;
}

#table tr:hover {
  background-color: #ddd;
}

#table th {
  padding-top: 12px;
  padding-bottom: 12px;
  background-color: #0e5071;
  color: white;
}

#table td {
  vertical-align: middle;
}

#table2 {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

#table2 td,
#table2 th {
  border: 1px solid #ddd;
  padding: 6px;
  height: auto;
  font-size: 14px;
}

#table2 tr:nth-child(even) {
  background-color: #f2f2f2;
}

#table2 tr:hover {
  background-color: #ddd;
}

#table2 th {
  padding-top: 12px;
  padding-bottom: 12px;
  background-color: #0e5071;
  color: white;
  word-break: break-word;
}

#table2 td {
  vertical-align: middle;
  word-break: break-word;
}

/* Layout and container styles */
.td-checkbox {
  text-align: center !important;
}

.active {
  color: #f47820;
}

.statistics-label {
  margin: 0;
}

.spacer-div {
  width: 1%;
}

.main-div {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-wrap: wrap;
  position: relative;
}

.actions-container {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-content: center;
  margin: 30px;
}

.batchAction-btn {
  margin-left: 20px;
  display: inline-block;
}

.multiselect-input {
  --ms-tag-font-size: 1vw;
  --ms-px: 0.5rem;
}

.survey-data-table-container {
  width: 100%;
  margin: 10px;
}

.survey-custom-data-table {
  border: 1px solid rgb(200, 200, 200);
}

.survey-custom-data-table tr {
  width: 3vw;
}

.survey-custom-data-table th {
  color: white;
  white-space: nowrap;
}

.survey-custom-data-table :deep(table) > thead {
  background-color: #0e5071;
}

.user-data-table-container {
  display: flex;
  justify-content: center;
  margin: 10px;
}

.user-custom-data-table {
  margin-top: 50px;
  border: 1px solid rgb(200, 200, 200);
  width: 90%;
  font-size: 0.9vw;
  border-collapse: collapse;
  word-break: break-word;
}

.user-custom-data-table tr {
  display: flex;
  flex-direction: row;
  width: 12vw;
}

.user-custom-data-table th {
  color: white;
}

.user-custom-data-table tbody tr:nth-child(odd) {
  background-color: #000;
}

.user-custom-data-table :deep(table) > thead {
  background-color: #0e5071;
}

.title-container {
  height: 10%;
  position: relative;
  margin: 10px 0 0 0;
}

.row-start {
  margin-top: 20px;
}

.gospi-form {
  border: 0.5px solid grey;
  padding: 15px;
  border-radius: 20px;
}

.box-statistics {
  border: 0.5px solid grey;
  padding: 30px;
  border-radius: 20px;
  width: 90%;
}

/* Mobile optimization for box-statistics */
@media only screen and (max-width: 900px) {
  .box-statistics {
    width: 100%;
    margin: 10px 0;
    padding: 20px;
    box-sizing: border-box;
  }
}

.v-row {
  margin-top: -20px;
}

.v-radio-group {
  margin-left: 6px;
}

.v-checkbox {
  margin-top: -30px;
  margin-bottom: 20px;
}

.column-header-filter {
  width: 10%;
  padding: 0;
}

.body-form-container {
  box-shadow: none;
  margin-top: 25px;
}

.v-data-table-header {
  white-space: nowrap;
}

.v-data-table-header__icon {
  margin: 10px;
  font-size: medium;
}

.header-th {
  background-color: #0e5071 !important;
}

.column-header-filter .v-input__append-inner {
  display: none !important;
}

.menu-buttons {
  font-weight: bold;
}

.participant-details-link {
  text-decoration: none;
}

.all-data-checkbox {
  margin-left: 10px;
}

.excel-download-btn {
  padding: 0;
}

.label1 {
  top: 0px;
  left: 0px;
  font-size: 18px;
  color: rgba(0, 0, 0, 0.54);
  font-family: Arial, Helvetica, sans-serif;
}

/* Input styling */
input, select, option, textarea {
  border: 1px solid grey;
}

/* Move Survey Across Org Modal */
.move-modal-backdrop {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.move-modal {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 860px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  display: flex;
  flex-direction: column;
}

.move-modal-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 10px 10px 0 0;
  flex-shrink: 0;
}

.move-modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1;
}

.move-modal-close:hover {
  background: rgba(255,255,255,0.2);
}

.move-modal-body {
  padding: 24px;
  flex: 1;
}

.move-modal-columns {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.move-col {
  flex: 1;
  border-radius: 8px;
  padding: 16px;
}

.move-col-from {
  background: #f0f4f8;
  border: 2px solid #b0c4de;
}

.move-col-to {
  background: #f0fff4;
  border: 2px solid #68d391;
}

.move-col-header {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
}

.move-col-icon {
  font-size: 18px;
}

.move-arrow {
  font-size: 32px;
  color: #888;
  padding-top: 60px;
  flex-shrink: 0;
}

.move-field {
  margin-bottom: 12px;
}

.move-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.move-readonly {
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 7px 10px;
  font-size: 13px;
  color: #333;
  min-height: 32px;
}

.move-readonly.move-count {
  font-weight: 700;
  color: #0e5071;
  font-size: 16px;
}

.move-select {
  width: 100%;
  padding: 7px 10px;
  font-size: 13px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  min-height: 32px;
}

.move-select:disabled {
  background: #f5f5f5;
  color: #aaa;
  cursor: not-allowed;
}

.move-required {
  color: #e53e3e;
  margin-left: 2px;
}

.move-optional {
  color: #888;
  font-weight: 400;
  font-size: 10px;
}

.move-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 13px;
  color: #856404;
  margin-bottom: 20px;
}

.move-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.move-btn-cancel {
  padding: 10px 20px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  color: #555;
}

.move-btn-cancel:hover {
  background: #f5f5f5;
}

.move-btn-confirm {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.move-btn-confirm:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.move-btn-confirm:not(:disabled):hover {
  opacity: 0.85;
}

/* Result screen */
.move-result-header {
  text-align: center;
  padding: 14px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 20px;
}

.move-result-success {
  background: #c6f6d5;
  color: #276749;
}

.move-result-error {
  background: #fed7d7;
  color: #9b2c2c;
}

.move-result-stats {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 24px;
}

.move-stat-card {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 28px;
  text-align: center;
}

.move-stat-number {
  font-size: 32px;
  font-weight: 800;
  color: #0e5071;
  line-height: 1;
}

.move-stat-label {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.move-result-tables-title {
  font-size: 13px;
  font-weight: 700;
  color: #444;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.move-result-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 20px;
}

.move-result-table th {
  background: #0e5071;
  color: white;
  padding: 8px 12px;
  text-align: left;
}

.move-result-table td {
  padding: 7px 12px;
  border-bottom: 1px solid #eee;
}

.move-row-active td {
  background: #f0fff4;
  font-weight: 500;
}

.move-row-zero td {
  color: #aaa;
}

.move-result-table code {
  background: #edf2f7;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

/* Mobile Responsive Styles - Enhanced */
@media only screen and (max-width: 900px) {
  /* Main layout adjustments */
  .main-div {
    font-size: 16px; /* Use fixed font size instead of vw */
    background: white;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    position: relative;
    align-items: center;
    padding: 10px;
  }

  /* Filter panel mobile optimization */
  .filter-panel.expanded {
    width: calc(100vw - 40px); /* Full width minus margins */
    max-width: 350px;
    margin: 0 10px;
  }

  .filter-header {
    padding: 16px 12px; /* Increase touch target */
  }

  .toggle-button {
    width: 32px;
    height: 32px; /* Larger touch target */
  }

  /* Button improvements */
  .search-btn {
    width: auto;
    min-width: 120px;
    padding: 12px 16px; /* Larger touch target */
    font-size: 14px;
  }

  /* Form elements */
  .input {
    width: 100%;
    padding: 12px 8px; /* Larger touch targets */
    font-size: 16px; /* Prevent zoom on iOS */
    border: 1px solid grey;
  }

  .input1 {
    width: 100%;
    font-size: 16px;
    padding: 12px 8px;
  }

  .inputTemplate {
    width: 100%;
    font-size: 16px;
    padding: 12px 8px;
  }

  #batchAction {
    font-size: 16px;
    padding: 12px;
    margin: 20px 0;
  }

  .batch-action-input {
    width: 100%;
    font-size: 16px;
    padding: 12px 8px;
    margin: 8px 0;
  }

  #selectBatchAction,
  .batch-action-input2 {
    width: 100%;
    font-size: 16px;
    padding: 12px 8px;
    margin: 8px 0;
  }

  .form-select {
    height: 44px; /* Better touch target */
    font-size: 16px;
    padding: 0 16px;
  }

  .multiselect {
    font-size: 16px;
  }

  /* Table optimizations */
  #table2 {
    font-size: 14px; /* Readable on mobile */
    border-collapse: collapse;
    word-break: break-word;
  }

  #table2 th,
  #table2 td {
    padding: 8px 4px; /* Reduce padding for mobile */
    font-size: 14px;
  }

  #table {
    font-size: 14px;
  }

  #table th,
  #table td {
    padding: 8px 4px;
    font-size: 14px;
  }

  /* Container adjustments */
  .survey-data-table-container {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    position: relative;
    align-items: center;
    width: 100%;
    padding: 0 10px;
    box-sizing: border-box;
  }

  .actions-container {
    display: flex;
    flex-direction: column; /* Stack on mobile */
    gap: 15px;
    align-items: stretch;
    margin: 20px 10px;
    width: calc(100% - 20px);
  }

  .batchAction-btn {
    margin-left: 0;
    width: 100%;
  }

  /* Box layouts for mobile */
  .box {
    margin: 10px 0;
    padding: 15px;
    max-width: none;
    width: 100%;
    box-sizing: border-box;
  }

  .boxReports {
    min-width: auto;
    width: 100%;
    padding: 15px;
    margin: 10px 0;
  }

  .boxStatistics {
    min-width: auto;
    width: 100%;
    margin: 10px 0;
    padding: 20px;
    box-sizing: border-box;
  }

  .box-statistics {
    width: 100%;
    margin: 10px 0;
    padding: 15px;
    box-sizing: border-box;
  }

  /* Label div mobile optimization */
  .label-div {
    width: 100%;
    margin: 5px 0;
    padding: 8px 0;
    justify-content: flex-start;
  }

  /* User table mobile optimization */
  .user-custom-data-table {
    font-size: 14px;
    width: 100%;
    margin-top: 20px;
  }

  .user-custom-data-table tr {
    width: auto;
    display: table-row; /* Reset flex display for mobile */
  }

  .user-data-table-container {
    padding: 0 10px;
    width: 100%;
    box-sizing: border-box;
  }

  /* Multiselect improvements */
  .multiselect-input {
    --ms-tag-font-size: 14px;
    --ms-px: 0.5rem;
  }

  /* Typography improvements */
  .batch-action-desc {
    font-size: 16px;
  }

  .options-label {
    font-size: 16px; /* Larger for mobile readability */
  }

  .node-label {
    font-size: 16px;
  }

  /* Touch-friendly checkboxes and radio buttons */
  .checkbox {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }

  .radio-custom {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }

  .radio-option {
    padding: 8px 0; /* Larger touch target */
    font-size: 16px;
  }

  /* Tree node mobile optimization */
  .tree-node {
    padding: 16px 15px; /* Larger touch target */
  }

  .child-node {
    padding: 12px 16px 12px 35px;
    font-size: 15px;
  }

  /* Horizontal scrolling for wide tables */
  .survey-data-table-container,
  .user-data-table-container {
    overflow-x: auto;
  }

  #table,
  #table2,
  .survey-custom-data-table,
  .user-custom-data-table {
    min-width: 600px; /* Ensure minimum width for usability */
  }
}

/* Extra small screens (phones in portrait) */
@media only screen and (max-width: 480px) {
  .main-div {
    padding: 5px;
  }

  .filter-panel.expanded {
    width: calc(100vw - 20px);
    margin: 0 10px;
  }

  .actions-container {
    margin: 15px 5px;
    width: calc(100% - 10px);
  }

  .survey-data-table-container,
  .user-data-table-container {
    padding: 0 5px;
  }

  .boxReports,
  .boxStatistics,
  .box {
    margin: 8px 0;
    padding: 10px;
  }

  .box-statistics {
    width: 100%;
    margin: 8px 0;
    padding: 10px;
  }

  /* Even more compact table styling */
  #table2 th,
  #table2 td,
  #table th,
  #table td {
    padding: 6px 2px;
    font-size: 13px;
  }

  /* Smaller form elements on very small screens */
  .input,
  .input1,
  .inputTemplate,
  .batch-action-input,
  #selectBatchAction,
  .batch-action-input2 {
    padding: 10px 6px;
  }
}

/* Large screens - maintain original design */
@media only screen and (min-width: 901px) {
  .main-div {
    padding: 0;
  }

  .actions-container {
    flex-direction: row;
  }

  #table2 th {
    font-size: 0.7vw;
  }

  #table2 td {
    font-size: 0.7vw;
  }

  .multiselect-input {
    --ms-tag-font-size: 1vw;
  }
}
.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: -40px;
  margin-bottom: -40px;
}

.options-label {
  white-space: nowrap;
}
.search-section {
  margin-bottom: 8px; /* consistent spacing below search bar */
  padding: 0; /* remove extra padding that pushes content away */
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 6px; /* spacing between label, input, and button */
}

</style>


<style scoped>
/* =====================================================================
   Admin Control Center - new look and feel (from the design document)
   Offset the shell height if the page has a header above it:
   set --icc-offset on a parent (default 140px).
   ===================================================================== */
.icc-root { width: 100%; color: #141d20; }
.icc-form { width: 100%; }

.icc-shell {
  display: flex;
  width: 100%;
  height: calc(100vh - var(--icc-offset, 140px));
  min-height: 560px;
  background: #fff;
  overflow: hidden;
  border: 1px solid #c1c7ce;
}

/* ---------- Sidebar ---------- */
.icc-sidebar {
  display: flex;
  flex-direction: column;
  width: 288px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #c1c7ce;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .12);
  z-index: 5;
  transition: width .3s ease-in-out;
}
.icc-sidebar.collapsed { width: 65px; }
.icc-sidebar-head {
  background: var(--icc-primary);
  color: #fff;
  flex-shrink: 0;
  min-height: 48px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.icc-sidebar.collapsed .icc-sidebar-head { flex-direction: column; padding: 14px 6px; height: auto; }
.icc-sidebar.collapsed .icc-sidebar-title { display: none; }
.icc-sidebar-title { font-size: 12px; font-weight: 700; letter-spacing: .3px; text-transform: uppercase; }
.icc-pill {
  padding: 4px 12px; font-size: 10px; font-weight: 700; text-transform: uppercase;
  color: #fff; background: transparent; border: 1px solid rgba(255, 255, 255, .4);
  border-radius: 9999px; cursor: pointer; white-space: nowrap;
}
.icc-pill:hover { background: rgba(255, 255, 255, .1); }
.icc-vertical { writing-mode: vertical-rl; transform: rotate(180deg); padding: 8px 0; display: block; }

.icc-sidebar-scroll { flex: 1; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 20px; }
.icc-filter-title { display: flex; align-items: center; gap: 8px; }
.icc-filter-title h3 { font-size: 12px; font-weight: 700; margin: 0; }
.icc-info {
  position: relative; display: inline-flex; align-items: center; justify-content: center;
  width: 14px; height: 14px; border-radius: 50%; border: 1.5px solid #0e5071; color: #0e5071;
  font-size: 9px; font-weight: 700; cursor: help;
}
.icc-tip {
  position: absolute; top: 100%; left: 50%; transform: translateX(-50%); margin-top: 8px; width: 224px;
  padding: 8px; background: #293235; color: #fff; font-size: 10px; font-weight: 400; line-height: 1.5;
  border-radius: 4px; box-shadow: 0 4px 12px rgba(0, 0, 0, .25); opacity: 0; visibility: hidden;
  transition: opacity .2s; z-index: 100;
}
.icc-info:hover .icc-tip { opacity: 1; visibility: visible; }

.icc-stats {
  background: #ecf5f9; border: 1px solid #c1c7ce; border-radius: 4px; padding: 16px;
  font-size: 11px; line-height: 1.6;
}
.icc-stats p { margin: 0; }
.icc-pct { color: #323700; font-weight: 700; }

.icc-fields { display: flex; flex-direction: column; gap: 16px; }
.icc-label {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;
  font-size: 10px; font-weight: 700; color: #41484d; text-transform: uppercase; letter-spacing: .05em;
}
.icc-chev { color: #71787e; }
.icc-select {
  width: 100%; font-size: 12px; font-family: inherit; padding: 7px 8px; background: #fff;
  border: 1px solid #c1c7ce; border-radius: 4px; color: #141d20;
}
.icc-select:focus { outline: 2px solid var(--icc-primary); outline-offset: -1px; border-color: var(--icc-primary); }
.icc-select:disabled { background: #f2f5f7; cursor: not-allowed; }
.icc-select.icc-placeholder { font-style: italic; color: #71787e; }

.icc-sidebar-foot {
  flex-shrink: 0; padding: 16px 20px; border-top: 1px solid #c1c7ce; background: #fff;
  display: flex; flex-direction: column; gap: 10px;
}
.icc-select-btn {
  width: 100%; padding: 10px; background: #fe8028; color: #612900; font-size: 12px; font-weight: 700;
  border: none; border-radius: 4px; cursor: pointer; box-shadow: 0 1px 2px rgba(0, 0, 0, .2);
}
.icc-select-btn:hover { opacity: .9; }
.icc-select-btn:disabled { opacity: .5; cursor: not-allowed; }
.icc-link-btn {
  background: none; border: none; padding: 2px 0; font-size: 11px; font-weight: 600;
  color: var(--icc-primary); cursor: pointer; text-align: left; text-decoration: underline;
}
.icc-link-btn:disabled { opacity: .5; cursor: not-allowed; }
.icc-foot-row { display: flex; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.icc-release { font-size: 10px; background: #ecf5f9; border: 1px solid #c1c7ce; border-radius: 4px; padding: 8px; }
.icc-release-head { color: #41484d; margin-bottom: 4px; }
.icc-release-box { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.icc-release-text { word-break: break-all; }

/* ---------- Main area ---------- */
.icc-main { flex: 1; min-width: 0; background: #dce5e9; overflow-y: auto; }
.icc-main-inner { display: flex; flex-direction: column; min-height: 100%; padding: 24px; gap: 16px; }

.icc-actionbar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 16px; }
.icc-actionbar h2 { font-size: 14px; font-weight: 700; margin: 0; }
.icc-btn-row { display: flex; flex-wrap: wrap; gap: 12px; }
.icc-btn {
  display: inline-flex; align-items: center; gap: 8px; padding: 8px 20px; font-size: 12px; font-weight: 700;
  font-family: inherit; color: #000; background: #fff; border: 1px solid #c1c7ce; border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .1); cursor: pointer; white-space: nowrap;
}
.icc-btn:hover:not(:disabled) { background: #ecf5f9; }
.icc-btn:disabled { opacity: .5; cursor: not-allowed; }
.icc-btn-sm { padding: 6px 16px; font-size: 10px; }

.icc-dropdown { position: relative; }
.icc-menu {
  position: absolute; right: 0; top: 100%; margin-top: 4px; width: 224px; background: #fff;
  border: 1px solid #c1c7ce; border-radius: 4px; box-shadow: 0 4px 12px rgba(0, 0, 0, .18); z-index: 50;
  padding: 4px 0; display: none;
}
.icc-dropdown:hover .icc-menu, .icc-dropdown:focus-within .icc-menu { display: block; }
.icc-menu button {
  display: block; width: 100%; text-align: left; padding: 8px 16px; font-size: 12px; font-family: inherit;
  color: #141d20; background: none; border: none; cursor: pointer;
}
.icc-menu button:hover { background: #ecf5f9; }
.icc-menu-more { border-top: 1px solid #e1eaee !important; font-weight: 600; }

.icc-breadcrumb {
  display: flex; align-items: center; gap: 6px; padding: 8px; background: #fff;
  border: 1px solid #c1c7ce; border-radius: 4px; font-size: 11px; color: #666;
}
.icc-path { font-weight: 700; text-transform: uppercase; letter-spacing: .05em; font-size: 9px; color: #71787e; flex-shrink: 0; }
.icc-crumbs { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.icc-crumbs strong { color: #333; }
.icc-crumb-empty { font-style: italic; color: #71787e; }

.icc-panel {
  background: #fff; border: 1px solid #c1c7ce; border-radius: 4px; padding: 12px 16px;
  max-height: 45vh; overflow: auto;
}
.icc-panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-size: 12px; }
.icc-hint { font-size: 11px; color: #71787e; margin: 0 0 8px; }

/* ---------- Table section ---------- */
.icc-table-section {
  flex: 1; min-height: 420px; display: flex; flex-direction: column; background: #fff;
  border: 1px solid #c1c7ce; border-radius: 4px; box-shadow: 0 1px 2px rgba(0, 0, 0, .1); overflow: hidden;
}
.icc-table-title {
  flex-shrink: 0; background: var(--icc-primary); color: #fff; padding: 12px 20px;
  font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em;
}
.icc-select-row {
  flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; padding: 10px 20px;
  font-size: 10px; font-weight: 700; color: #41484d; text-transform: uppercase; letter-spacing: .03em;
  border-bottom: 1px solid #c1c7ce; background: rgba(236, 245, 249, .5);
}
.icc-selectall { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.icc-table-body { flex: 1; min-height: 0; overflow: auto; position: relative; }
.icc-empty { padding: 40px 20px; text-align: center; font-size: 12px; color: #71787e; }
.icc-table-foot {
  flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; padding: 12px 20px;
  border-top: 1px solid #c1c7ce; background: #ecf5f9; font-size: 10px; font-weight: 500; color: #41484d;
}
.icc-history { margin-top: 4px; }

/* Restyle the existing Vuetify tables to match the design (logic untouched) */
.icc-table-body :deep(.v-card) { box-shadow: none; border-radius: 0; }
.icc-table-body :deep(.v-table) { font-size: 11px; }
.icc-table-body :deep(.v-table .v-table__wrapper > table > thead > tr > th),
.icc-table-body :deep(.v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th) {
  background: #ecf5f9 !important;
  color: #41484d !important;
  font-size: 11px;
  font-weight: 700;
  border-bottom: 1px solid #c1c7ce;
}
.icc-table-body :deep(.v-table .v-table__wrapper > table > tbody > tr > td) {
  font-size: 11px; color: #41484d; padding-top: 8px; padding-bottom: 8px;
}
.icc-table-body :deep(tbody tr:nth-of-type(odd)) { background-color: transparent; }
.icc-table-body :deep(tbody tr:hover) { background-color: rgba(236, 245, 249, .6); }
.icc-table-body :deep(.participant-details-link) { color: #003853; font-weight: 700; }
.icc-table-body :deep(thead th),
.icc-table-body :deep(.v-data-table-header th) { color: #41484d !important; }
.icc-table-body :deep(.v-data-table-header__icon) { color: #41484d; }

/* ---------- Small screens ---------- */
@media only screen and (max-width: 900px) {
  .icc-shell { flex-direction: column; height: auto; min-height: 0; }
  .icc-sidebar, .icc-sidebar.collapsed { width: 100%; box-shadow: none; }
  .icc-sidebar-scroll { max-height: 70vh; }
  .icc-sidebar.collapsed .icc-sidebar-head { flex-direction: row; }
  .icc-vertical { writing-mode: horizontal-tb; transform: none; padding: 0; }
  .icc-main { overflow: visible; }
  .icc-main-inner { padding: 12px; }
  .icc-table-section { min-height: 360px; }
}
 
/* ===== Step 1: type scale (later rules override the older sizes above) ===== */
.icc-root {
  --icc-fs-title: 24px;
  --icc-fs-heading: 20px;
  --icc-fs-body: 16px;
  --icc-fs-label: 14px;
  --icc-fs-table: var(--icc-fs-body);
  --icc-fs-button: var(--icc-fs-body);
  font-size: var(--icc-fs-body);
}
 
/* supporting labels (14px) */
.icc-sidebar-title, .icc-pill, .icc-label, .icc-stats, .icc-tip, .icc-link-btn,
.icc-release, .icc-hint, .icc-path, .icc-select-row, .icc-table-foot { font-size: var(--icc-fs-label); }
 
/* headings (20px) */
.icc-actionbar h2, .icc-table-title { font-size: var(--icc-fs-heading); }
 
/* body text and form fields (16px) */
.icc-filter-title h3, .icc-breadcrumb, .icc-panel-head, .icc-empty { font-size: var(--icc-fs-body); }
.icc-select { font-size: var(--icc-fs-body); min-height: 40px; padding: 8px 10px; }
 
/* buttons (16px) */
.icc-btn, .icc-btn-sm, .icc-select-btn, .icc-menu button { font-size: var(--icc-fs-button); }
.icc-btn, .icc-select-btn { min-height: 40px; }
.icc-menu { width: max-content; min-width: 224px; max-width: 90vw; }
 
/* info icon */
.icc-info { width: 20px; height: 20px; font-size: 12px; }
 
/* tables (16px) */
.icc-table-body :deep(.v-table) { font-size: var(--icc-fs-table); }
.icc-table-body :deep(.v-table .v-table__wrapper > table > thead > tr > th),
.icc-table-body :deep(.v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th),
.icc-table-body :deep(.v-table .v-table__wrapper > table > tbody > tr > td) { font-size: var(--icc-fs-table); }
.user-custom-data-table { font-size: var(--icc-fs-table); }
#iteration-log-table th, #iteration-log-table td { font-size: var(--icc-fs-table); }
 
/* batch panel fields and tag selector (old code used 14px and 1vw) */
.icc-batch-panel .input1, .icc-batch-panel .batch-action-input, .icc-batch-panel #selectBatchAction,
.icc-batch-panel .batch-action-input2, .icc-batch-panel input, .icc-batch-panel select { font-size: var(--icc-fs-body); }
.multiselect-input { --ms-tag-font-size: var(--icc-fs-body); }
 
/* ===== Step 2: responsive ===== */
.icc-sidebar { width: 320px; }
.icc-sidebar-head { flex-wrap: wrap; }
.icc-pill { min-height: 32px; padding: 6px 12px; }
 
.icc-breadcrumb { align-items: flex-start; flex-wrap: wrap; }
.icc-crumbs { white-space: normal; overflow: visible; text-overflow: clip; }
 
.icc-history .boxReports { min-width: 0; width: 100%; }
.user-custom-data-table { width: 100%; margin-top: 0; }
#iteration-log-table-container { overflow: auto; }
 
@media only screen and (max-width: 1000px) {
  .icc-shell { flex-direction: column; height: auto; min-height: 0; }
  .icc-sidebar, .icc-sidebar.collapsed { width: 100%; box-shadow: none; }
  .icc-sidebar-scroll { max-height: 70vh; }
  .icc-sidebar.collapsed .icc-sidebar-head { flex-direction: row; }
  .icc-vertical { writing-mode: horizontal-tb; transform: none; padding: 0; }
  .icc-main { overflow: visible; }
  .icc-main-inner { padding: 12px; }
  .icc-table-section { min-height: 360px; }
}
 
@media only screen and (max-width: 600px) {
  .icc-btn-row { width: 100%; }
  .icc-btn-row .icc-btn { flex: 1 1 100%; justify-content: center; white-space: normal; text-align: center; }
  .icc-dropdown { flex: 1 1 100%; }
  .icc-dropdown .icc-btn-drop { width: 100%; justify-content: center; }
  .icc-menu { left: 0; right: auto; }
}
 
/* ===== Step 3: table scroll area ===== */
.icc-table-body .survey-data-table-container,
.icc-table-body .user-data-table-container { margin: 0; width: 100%; max-width: 100%; }
 
.icc-table-body :deep(.v-table__wrapper) {
  overflow: auto;
  max-height: max(360px, calc(100vh - 380px));
}
.icc-table-body :deep(.v-table .v-table__wrapper > table > thead > tr > th) { white-space: nowrap; }
 
/* ===== Step 4: header contrast and density ===== */
.icc-sidebar-head, .icc-table-title { color: var(--icc-on-primary, #fff); }
.icc-pill { color: var(--icc-on-primary, #fff); border-color: var(--icc-on-primary, #fff); }
 
/* table header = brand color, text auto white/dark */
.icc-table-body :deep(.v-table .v-table__wrapper > table > thead > tr > th),
.icc-table-body :deep(.v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th) {
  background: var(--icc-primary) !important;
  color: var(--icc-on-primary, #fff) !important;
  border-bottom: 1px solid rgba(0, 0, 0, .2);
  padding-top: 8px;
  padding-bottom: 8px;
}
.icc-table-body :deep(thead th),
.icc-table-body :deep(.v-data-table-header th) { color: var(--icc-on-primary, #fff) !important; }
 
/* the custom header slots put a thead/tr/th inside each header cell: no second background or padding */
.icc-table-body :deep(th thead),
.icc-table-body :deep(th tr),
.icc-table-body :deep(th th) { background: transparent !important; padding: 0 !important; }
 
/* filter icon, checkbox and sort icon follow the header text color */
.icc-table-body :deep(thead .v-icon),
.icc-table-body :deep(thead .v-field),
.icc-table-body :deep(thead .v-field__input),
.icc-table-body :deep(thead .v-selection-control) { color: var(--icc-on-primary, #fff) !important; opacity: 1; }
.icc-table-body :deep(.v-data-table-header__icon) { color: inherit; margin: 0 4px; }
.icc-table-body :deep(.title-container) { margin: 0; }
 
/* tighter rows, still comfortable to click */
.icc-table-body :deep(.v-table) { --v-table-row-height: 40px; }
.icc-table-body :deep(.v-table .v-table__wrapper > table > tbody > tr > td) { padding-top: 4px; padding-bottom: 4px; }
 
/* ===== Step 5: column resize handle ===== */
.icc-root :deep(.icc-col-resizer) {
  position: absolute; top: 0; right: 0; width: 10px; height: 100%;
  cursor: col-resize; touch-action: none; user-select: none; z-index: 3;
}
.icc-root :deep(.icc-col-resizer)::after {
  content: ""; position: absolute; top: 20%; bottom: 20%; right: 4px; width: 2px;
  background: currentColor; opacity: .35;
}
.icc-root :deep(.icc-col-resizer:hover)::after,
.icc-root :deep(.icc-col-resizer.active)::after { opacity: 1; }
/* user table is not fixed-header, so its header cells need a positioning context */
.icc-table-body :deep(.v-table:not(.v-table--fixed-header) > .v-table__wrapper > table > thead > tr > th) { position: relative; }
 
/* ICC-END */
</style>